import { REGION_FRANCFORT } from "./regionsFonctions";
import { Jauge } from "./Jauge";
import React, { useState, useEffect, useLayoutEffect, useCallback, useMemo, useRef, Suspense } from "react";
import { lazyPrechargeable } from "./lazyRetry";
import VersionAramis from "./VersionAramis";
import { perimetresAccessibles } from "./accesInstances";
import { INSTANCES_NAV, routeInstance } from "./instancesNav";
import { TourProvider } from "./TourEngine";
import { SessionReelleContexte, droitsReels } from "./sessionReelleContexte";
import EnTetePage from "./EnTetePage";
import { toast, SkeletonCard, useConfirm, differerSiFenetreOuverte } from "./uxComponents";
// extractMentions vit dans le module minimal src/mentions.js : un import
// statique depuis un module paresseux annulerait son React.lazy et
// l'embarquerait dans main.js (perf P2, ~160 Ko à l'époque d'Enio 2030).
// StatusBadge, EmptyStateCTA, GlossaryTerm disponibles dans uxComponents.js pour
// usage futur (remplacer les badges ad-hoc, états vides, tooltips de jargon)
import { creerCodesApi } from "./agoraCodes";
import { useSeancePublique, versEtatSalle, urlAccesSeance, urlAccesLisible } from "./seanceServeur";
import { dernierPointClos } from "./pointClos";
import { useFirebaseState, useConfig, useMemberLists, useAdmins, useDelegates, useGouvernance, useAuditLog, useIdeaBox, useProjects, useMeetings, useMembers, useReunions, useExternesReunion, noterConnexion, useDernieresConnexions, setAuditActor } from "./useFirebaseState";
import FloatingChat from "./FloatingChat";
import { MUTUELLE_ALIAS, normalizeMutuelleName, sameMutuelle as helperSameMutuelle } from "./mutuellesHelper"; // eslint-disable-line no-unused-vars
import { sanitizeHtml as sanitizeAmdtHtml, isHtmlContent as isAmdtHtml, useAmendementsScreen } from "./amendementsCommun";
import { compterCandidats } from "./modaliteVote";
import { C, POLES_PAR_DOMAINE, VoteBadge, ResultsBadge, InstanceBadge, ORG_DOMAINES, hasValidLegacyAdmin, hasValidLegacySuperAdmin, getFontSize, applyFontSize, getTheme, setThemeLS, applyTheme, themeEnVigueur, useThemePagePublique, nowFull, isActif, getInstConfig, creerNotification, getMutuelles, NootaImportPanelLazy, consumeNavParams, loadJsPDF, sanitize, Prog, ScrutinResults, DocumentLibrary } from "./appShared";
import { AuthBackdrop, UnlockAnimation, BootGenese } from "./AuthBackdrop";
import PictoEnioAnime, { PictoLoader } from "./PictoEnioAnime";
import Header from "./Header";
import AramisLogin from "./AramisLogin";
import { useDemandesEngagementEnAttente } from "./demandesEngagement";
import { accessScopeForView } from "./accesVues";
import { buildNavAdmin, pageServie, ARGOS_VIEWS, buildNavModules, buildNavEspaceSalarie, estProprietaireBudget, navTexte, VUES_BUDGET, NAV_COLORS, routePortail, MODULE_VIEWS } from "./navConstants";
import { useMediaQuery, installerClasseCoquille, useCoquille } from "./responsive";
// Feuille globale à ordre explicite (lot L2) : raffinements, effets, assemblage.
import { installerFeuilleGlobale, CSS_RAFFINEMENTS, CSS_EFFETS, CSS_COQUILLE, CSS_PRIMITIVES } from "./feuilleGlobale";
import BarreOnglets, { AttributOnglets } from "./BarreOnglets";
import RailNavigation from "./RailNavigation";
import { appliquerPreferences, suivreThemeSysteme } from "./preferencesCoquille";
import { prechargerSons, signalerEcranSalle } from "./sons";
import { ModuleSidebar, moduleForView, AttributModuleHtml } from "./ModuleSidebar";
import { Icone, IconeTexte } from "./icones";
import { adresseVue, suivreLienVue } from "./lienInterne";
// Coquille (lot L6) : mécanique de navigation — débit, source, transitions.
import { mouvementReduit, saisieClavierRecente } from "./mouvement";
import { creerLimiteur, LIMITE_CONTENU, LIMITE_COQUILLE, transitionVue, terminerTransitionEnCours, doitAnimerTransition, apiTransitionsDisponible, pointeurFin, noterRecent, optsCoquille } from "./navigationCoquille";
import { enregistrerChunksVues, vuePrete, prechargerVue } from "./prechargementVues";
import { raccourciModule } from "./navigationCoquille";
import { noterVueModule } from "./usagesModules";
import { useTourInterruption } from "./fileInterruptions";
import ProgressionNavigation from "./ProgressionNavigation";
import { SquelettePageJeton, SqueletteDocumentsSeance } from "./squelettes";
import { callDepenses } from "./appelDepenses";
import { STATUTS_MATURITE } from "./projetShared";
// Aides des projets partagées avec la page paresseuse (lot L11d.0).
import { getStatutMaturite, RisqueBadges, renderBold } from "./projetsCommuns";
// Le rôle de SESSION décide seul de l'accès administrateur (audit du 28/09/2026).
import { estSessionAdmin } from "./consoleAdmin";
import { lectureResultats, affichageResultats } from "./ciblesLiens";
import { annoncerParametresVue, useParametresVue } from "./parametresVue";
import { gardeHistorique } from "./gardeHistorique";
import { oublierMemoiresPersonne } from "./memoiresPersonne";
import { signalSeance } from "./seanceStatut";
import { alpha } from "./alpha";
import { motifDoublon, normAdresse } from "./doublonAdresse";
import { taillePx } from "./echelleTexte";
import { useDrapeauHestia } from "./hestia/drapeauHestia";
import { useDrapeauAudit } from "./audit/drapeauAudit";
import { useDroitRH } from "./droitRH";
import { peutAccederRH } from "./droitsRH";

// Aperçu mobile — développement uniquement (cf. src/ApercuMobile.js). Lot L7 :
// le chargeur lui-même n'existe qu'en développement ; en production la branche
// est éliminée et le morceau (qui porte le jeu FICTIF de « Ma semaine »,
// maSemaineDemo.js) n'est même pas émis dans le build.
const ApercuMobileLazy = process.env.NODE_ENV !== "production" ? lazyPrechargeable(() => import("./ApercuMobile")) : null;
// Fiche membre : chargée à la demande (30/09/2026), le bundle principal devant
// rester sous 400 000 o gzip. GouvernancePanel et PolesHubModule la reçoivent
// en prop ; Admin.js l'importe dans son propre morceau, déjà paresseux.
const DelegateContactPopupRaw = lazyPrechargeable(() => import("./DelegateContactPopup"));
const DelegateContactPopup = (props) => (<Suspense fallback={null}><DelegateContactPopupRaw {...props}/></Suspense>);
// Refonte 2026-05 — Sprint C : nouvelle home Activité éditorialisée (persona + carrousels)
// L'ancienne home (« athos-home-classique ») redirige vers l'accueil Activité (lot L11p).
const AthosHomeV2Raw = lazyPrechargeable(() => import("./AthosHomeV2"));
const ModuleVitrineRaw = lazyPrechargeable(() => import("./ModuleVitrine"));
const ModuleVitrineLazy = (props) => (<Suspense fallback={<PictoLoader label="Chargement…"/>}><ModuleVitrineRaw {...props}/></Suspense>);
const AthosHomeV2Lazy = (props) => (<Suspense fallback={<PictoLoader label="Chargement..."/>}><AthosHomeV2Raw {...props}/></Suspense>);
const GouvernanceAccueilRaw = lazyPrechargeable(() => import("./GouvernanceAccueil"));
const GouvernanceAccueilLazy = (props) => (<Suspense fallback={<PictoLoader label="Chargement..."/>}><GouvernanceAccueilRaw {...props}/></Suspense>);
const ExplorationHomeRaw = lazyPrechargeable(() => import("./ExplorationHome"));
const ExplorationHomeLazy = (props) => (<Suspense fallback={<PictoLoader label="Chargement..."/>}><ExplorationHomeRaw {...props}/></Suspense>);
// Hygie / Calypso — Module DPO / RGPD (Sprint 1+, 2026-05-26)
const HygieHomeRaw = lazyPrechargeable(() => import("./HygieHome"));
const HygieHomeLazy = (props) => (<Suspense fallback={<PictoLoader label="Chargement Calypso..."/>}><HygieHomeRaw {...props}/></Suspense>);
const DPOGateRaw = lazyPrechargeable(() => import("./DPOGate"));
const DPOGateLazy = (props) => (<Suspense fallback={<PictoLoader label="Chargement..."/>}><DPOGateRaw {...props}/></Suspense>);
const HygieRegistreRaw = lazyPrechargeable(() => import("./HygieRegistre"));
const HygieRegistreLazy = (props) => (<Suspense fallback={<PictoLoader label="Chargement Registre…"/>}><HygieRegistreRaw {...props}/></Suspense>);
const HygieLibraryRaw = lazyPrechargeable(() => import("./HygieLibrary"));
const HygieLibraryLazy = (props) => (<Suspense fallback={<PictoLoader label="Chargement Bibliothèque…"/>}><HygieLibraryRaw {...props}/></Suspense>);
const HygieAIPDRaw = lazyPrechargeable(() => import("./HygieAIPD"));
const HygieAIPDLazy = (props) => (<Suspense fallback={<PictoLoader label="Chargement AIPD…"/>}><HygieAIPDRaw {...props}/></Suspense>);
const HygieDSARRaw = lazyPrechargeable(() => import("./HygieDSAR"));
const HygieDSARLazy = (props) => (<Suspense fallback={<PictoLoader label="Chargement DSAR…"/>}><HygieDSARRaw {...props}/></Suspense>);
const HygieViolationsRaw = lazyPrechargeable(() => import("./HygieViolations"));
const HygieViolationsLazy = (props) => (<Suspense fallback={<PictoLoader label="Chargement Violations…"/>}><HygieViolationsRaw {...props}/></Suspense>);
const HygieSubprocessorsRaw = lazyPrechargeable(() => import("./HygieSubprocessors"));
const HygieSubprocessorsLazy = (props) => (<Suspense fallback={<PictoLoader label="Chargement Sous-traitants…"/>}><HygieSubprocessorsRaw {...props}/></Suspense>);
const HygieFormationRaw = lazyPrechargeable(() => import("./HygieFormation"));
const HygieFormationLazy = (props) => (<Suspense fallback={<PictoLoader label="Chargement Formation…"/>}><HygieFormationRaw {...props}/></Suspense>);
const HygieDashboardRaw = lazyPrechargeable(() => import("./HygieDashboard"));
const HygieDashboardLazy = (props) => (<Suspense fallback={<PictoLoader label="Chargement Dashboard…"/>}><HygieDashboardRaw {...props}/></Suspense>);
const HygieLibraryAdminRaw = lazyPrechargeable(() => import("./HygieLibraryAdmin"));
const HygieLibraryAdminLazy = (props) => (<Suspense fallback={<PictoLoader label="Chargement Curation…"/>}><HygieLibraryAdminRaw {...props}/></Suspense>);
const HygieForumRaw = lazyPrechargeable(() => import("./HygieForum"));
const HygieForumLazy = (props) => (<Suspense fallback={<PictoLoader label="Chargement Forum…"/>}><HygieForumRaw {...props}/></Suspense>);
const HygieAnnuaireRaw = lazyPrechargeable(() => import("./HygieAnnuaire"));
const HygieAnnuaireLazy = (props) => (<Suspense fallback={<PictoLoader label="Chargement Annuaire…"/>}><HygieAnnuaireRaw {...props}/></Suspense>);
// Reprise des registres de Calypso en clair (chantier « Calypso sans phrase
// secrète », 21/10/2026) : sous-page ouverte depuis l'accueil de Calypso.
const HygieRepriseRaw = lazyPrechargeable(() => import("./HygieReprise"));
const HygieRepriseLazy = (props) => (<Suspense fallback={<PictoLoader label="Chargement…"/>}><HygieRepriseRaw {...props}/></Suspense>);
const DatavizModuleRaw = lazyPrechargeable(() => import("./DatavizModule"));
const ConsultationAsyncRaw = lazyPrechargeable(() => import("./ConsultationAsync"));
const TravailGroupesRaw = lazyPrechargeable(() => import("./TravailGroupes"));
// Hestia (lot H0) : un seul morceau paresseux pour ses quatre vues.
const HestiaModuleRaw = lazyPrechargeable(() => import("./hestia/HestiaModule"));
const PageInviteEventRaw = lazyPrechargeable(() => import("./PageInviteEvent"));
const PageDatavizSaisieRaw = lazyPrechargeable(() => import("./PageDatavizSaisie"));
const PageDatavizSaisieLazy = (props) => (<Suspense fallback={<PictoLoader label="Chargement de la saisie…" pad={40}/>}><PageDatavizSaisieRaw {...props}/></Suspense>);
const NdfValiderTokenRaw = lazyPrechargeable(() => import("./NdfValiderToken"));
const NdfValiderTokenLazy = (props) => (<Suspense fallback={<PictoLoader label="Chargement…" pad={40}/>}><NdfValiderTokenRaw {...props}/></Suspense>);
const PageExportComptableRaw = lazyPrechargeable(() => import("./PageExportComptable"));
const PageExportComptableLazy = (props) => (<Suspense fallback={<PictoLoader label="Chargement…" pad={40}/>}><PageExportComptableRaw {...props}/></Suspense>);
const PageMediathequeInviteRaw = lazyPrechargeable(() => import("./mediatheque/PageMediathequeInvite"));
const PageMediathequeInviteLazy = (props) => (<Suspense fallback={<PictoLoader label="Chargement…" pad={40}/>}><PageMediathequeInviteRaw {...props}/></Suspense>);
// Mes formations (16/10/2026) : pages à jeton (émargement, formateur, personne
// sans compte et « Recevoir mon lien »), d'ordinaire montées avant <App/>
// (routesPubliques.js) ; ces routes sont le secours.
const PageMesFormationsInviteRaw = lazyPrechargeable(() => import("./mesFormations/PageMesFormationsInvite"));
const PageMesFormationsInviteLazy = (props) => (<Suspense fallback={<PictoLoader label="Chargement…" pad={40}/>}><PageMesFormationsInviteRaw {...props}/></Suspense>);
// Mes formations (20/10/2026) : un document de session lu dans sa propre
// fenêtre, hors coquille (vues formation-document et formation-document-jeton).
const LectureFenetreRaw = lazyPrechargeable(() => import("./mesFormations/LectureFenetre"));
const LectureFenetreLazy = (props) => (<Suspense fallback={<PictoLoader label="Ouverture du document…" pad={40}/>}><LectureFenetreRaw {...props}/></Suspense>);
const PageMobiliteAccordRaw = lazyPrechargeable(() => import("./rhPole/PageMobiliteAccord"));
const PageMobiliteAccordLazy = (props) => (<Suspense fallback={<PictoLoader label="Chargement…" pad={40}/>}><PageMobiliteAccordRaw {...props}/></Suspense>);
const PvSignaturePageRaw = lazyPrechargeable(() => import("./PvSignaturePage"));
const PvSignaturePageLazy = (props) => (<Suspense fallback={<PictoLoader label="Chargement…" pad={40}/>}><PvSignaturePageRaw {...props}/></Suspense>);
const PageEmargementFormationRaw = lazyPrechargeable(() => import("./mesFormations/PageEmargementFormation"));
const PageEmargementFormationLazy = (props) => (<Suspense fallback={<PictoLoader label="Chargement…" pad={40}/>}><PageEmargementFormationRaw {...props}/></Suspense>);
const PageFormateurRaw = lazyPrechargeable(() => import("./mesFormations/PageFormateur"));
const PageFormateurLazy = (props) => (<Suspense fallback={<PictoLoader label="Chargement…" pad={40}/>}><PageFormateurRaw {...props}/></Suspense>);
const PageEventPublicRaw = lazyPrechargeable(() => import("./PageEventPublic"));
const PageEventPublicLazy = (props) => (<Suspense fallback={<PictoLoader label="Chargement de l'événement…" pad={40}/>}><PageEventPublicRaw {...props}/></Suspense>);
const PageCareerPublicRaw = lazyPrechargeable(() => import("./PageCareerPublic"));
const PageCareerPublicLazy = (props) => (<Suspense fallback={<PictoLoader label="Chargement de la page carrières…" pad={40}/>}><PageCareerPublicRaw {...props}/></Suspense>);
const RHEspaceSalarieRaw = lazyPrechargeable(() => import("./RHEspaceSalarie"));
const RHEspaceSalarieLazy = (props) => (<Suspense fallback={<PictoLoader label="Chargement de votre espace…" pad={40}/>}><RHEspaceSalarieRaw {...props}/></Suspense>);
const PageEnqueteFormationRaw = lazyPrechargeable(() => import("./PageEnqueteFormation"));
const PageEnqueteFormationLazy = (props) => (<Suspense fallback={<PictoLoader label="Chargement de l'enquête…" pad={40}/>}><PageEnqueteFormationRaw {...props}/></Suspense>);
// Lot L12h : pages publiques (et carte « Session expirée ») sorties du bundle
// principal, chargées à la demande. Ordre des tests d'URL inchangé.
const PageReponseReunionRaw = lazyPrechargeable(() => import("./PageReponseReunion"));
// Pages publiques montées par App (D-L12-3 étendue, lot L12h-bis) : un premier
// visiteur suit le thème de son téléphone, dès le chargement ; un thème
// mémorisé prime ; le document ne le fait que SANS session (useThemePagePublique).
const PageReponseReunionLazy = (props) => { useThemePagePublique(); return (<Suspense fallback={<PictoLoader label="Chargement…" pad={40}/>}><PageReponseReunionRaw {...props}/></Suspense>); };
const PageResetPasswordRaw = lazyPrechargeable(() => import("./PageResetPassword"));
const PageResetPasswordLazy = (props) => { useThemePagePublique(); return (<Suspense fallback={<PictoLoader label="Chargement…" pad={40}/>}><PageResetPasswordRaw {...props}/></Suspense>); };
const DocumentViewerRaw = lazyPrechargeable(() => import("./DocumentViewer"));
const DocumentViewerLazy = (props) => { useThemePagePublique(!props.aramisSession?.id); return (<Suspense fallback={<PictoLoader label="Chargement…" pad={40}/>}><DocumentViewerRaw {...props}/></Suspense>); };
const CarteSessionExpireeRaw = lazyPrechargeable(() => import("./CarteSessionExpiree"));
const CarteSessionExpireeLazy = (props) => { useThemePagePublique(); return (<Suspense fallback={<PictoLoader label="Chargement…" pad={40}/>}><CarteSessionExpireeRaw {...props}/></Suspense>); };
// Les 3 anciennes files de validation autonomes (pending updates, RSVP inbox,
// demandes d'invitation) ne sont plus routées directement : leurs vues
// redirigent vers le centre de validation regroupé (LegacyEventRedirect).
const EventDayPanelRaw = lazyPrechargeable(() => import("./EventDayPanel"));
const EventDayPanelLazy = (props) => (<Suspense fallback={<PictoLoader label="Chargement de la vue jour J…"/>}><EventDayPanelRaw {...props}/></Suspense>);
const HandwrittenLibraryRaw = lazyPrechargeable(() => import("./HandwrittenLibrary"));
const HandwrittenLibraryLazy = (props) => (<Suspense fallback={<PictoLoader label="Chargement bibliothèque manuscrite…"/>}><HandwrittenLibraryRaw {...props}/></Suspense>);
// EventTablePlan et EventGuestListPanel ne sont plus routés directement :
// ils sont embarqués comme étapes de la fiche événement (EventFiche), et
// leurs anciennes vues redirigent via LegacyEventRedirect.
// Fiche événement à étapes (refonte façon Instances) — porte d'entrée d'un événement
const EventFicheRaw = lazyPrechargeable(() => import("./EventFiche"));
const EventFicheLazy = (props) => (<Suspense fallback={<PictoLoader label="Chargement de la fiche événement…"/>}><EventFicheRaw {...props}/></Suspense>);
// Suivi & alertes — le digest des évolutions événements, à l'écran
const EventSuiviRaw = lazyPrechargeable(() => import("./EventSuiviView"));
const EventSuiviLazy = (props) => (<Suspense fallback={<PictoLoader label="Chargement du suivi…"/>}><EventSuiviRaw {...props}/></Suspense>);
// Redirections des anciennes vues événement (deep-links des digests e-mail)
const LegacyEventRedirectRaw = lazyPrechargeable(() => import("./LegacyEventRedirect"));
const LegacyEventRedirectLazy = (props) => (<Suspense fallback={<PictoLoader label="Redirection…"/>}><LegacyEventRedirectRaw {...props}/></Suspense>);
// Redirections des anciennes vues hors événements (themis, themis-instances…) :
// les strings restent valides à jamais, cf. LEGACY_REDIRECTS (navConstants).
// Les écrans du module Budget : la liste vit dans navConstants (source unique).
// Recopiée ici, elle avait déjà divergé — « budget-exercice » manquait, et
// l'entrée de menu menait à une page blanche.

const LegacyViewRedirectRaw = lazyPrechargeable(() => import("./LegacyViewRedirect"));
const LegacyViewRedirectLazy = (props) => (<Suspense fallback={<PictoLoader label="Redirection…"/>}><LegacyViewRedirectRaw {...props}/></Suspense>);
const TravailGroupesLazy = (props) => (<Suspense fallback={<PictoLoader label="Chargement..."/>}><TravailGroupesRaw {...props}/></Suspense>);
const HestiaModuleLazy = (props) => (<Suspense fallback={<PictoLoader label="Chargement d'Hestia…"/>}><HestiaModuleRaw {...props}/></Suspense>);
// Médiathèque (lot L17.2a) : un seul morceau paresseux pour ses deux vues.
const MediathequeModuleRaw = lazyPrechargeable(() => import("./mediatheque/MediathequeModule"));
const MediathequeModuleLazy = (props) => (<Suspense fallback={<PictoLoader label="Chargement de la Médiathèque…"/>}><MediathequeModuleRaw {...props}/></Suspense>);
// Audits (D1, 11/10/2026) : un seul morceau paresseux pour ses cinq vues.
const AuditModuleRaw = lazyPrechargeable(() => import("./audit/AuditModule"));
const AuditModuleLazy = (props) => (<Suspense fallback={<PictoLoader label="Chargement des audits…"/>}><AuditModuleRaw {...props}/></Suspense>);
// Contrôle interne (D2, 11/10/2026) : risques et procédures, un seul morceau paresseux.
const ControleInterneModuleRaw = lazyPrechargeable(() => import("./controleInterne/ControleInterneModule"));
const ControleInterneModuleLazy = (props) => (<Suspense fallback={<PictoLoader label="Chargement du contrôle interne…"/>}><ControleInterneModuleRaw {...props}/></Suspense>);
const ConsultationAsyncLazy = (props) => (<Suspense fallback={<PictoLoader label="Chargement..."/>}><ConsultationAsyncRaw {...props}/></Suspense>);
const PageInviteEventLazy = (props) => (<Suspense fallback={<PictoLoader label="Chargement de votre invitation…" pad={40}/>}><PageInviteEventRaw {...props}/></Suspense>);
const DatavizModuleLazy = (props) => (<Suspense fallback={<PictoLoader label="Chargement Dataviz..."/>}><DatavizModuleRaw {...props}/></Suspense>);
const ThemisEvenementsRaw = lazyPrechargeable(() => import("./ThemisEvenements"));
// Outil "Préparer une présentation" — wizard 4 étapes + génération PPTX
const PreparationPresentationRaw = lazyPrechargeable(() => import("./PreparationPresentation"));
const PreparationPresentationLazy = (props) => (<Suspense fallback={<PictoLoader label="Chargement de l'outil…"/>}><PreparationPresentationRaw {...props}/></Suspense>);
// Module RH/Recrutement — banque CV, offres mutualisées, IA matching, chatbot
const RHModuleRaw = lazyPrechargeable(() => import("./RHModule"));
const RHModuleLazy = (props) => (<Suspense fallback={<PictoLoader label="Chargement RH…"/>}><RHModuleRaw {...props}/></Suspense>);
const ThemisInstancesHubRaw = lazyPrechargeable(() => import("./ThemisInstances"));
const ThemisInstancesHubLazy = (props) => (<Suspense fallback={<PictoLoader label="Chargement des instances…"/>}><ThemisInstancesHubRaw {...props}/></Suspense>);
// Documents préparatoires d'une séance, servis aux délégués convoqués (le lien
// de la convocation pointe ici, pas vers SharePoint — cf. ThemisDocumentsSeance.js)
const ThemisDocumentsSeanceRaw = lazyPrechargeable(() => import("./ThemisDocumentsSeance"));
const ThemisDocumentsSeanceLazy = (props) => (<Suspense fallback={<SqueletteDocumentsSeance />}><ThemisDocumentsSeanceRaw {...props}/></Suspense>);
const ThemisPresencePageRaw = lazyPrechargeable(() => import("./ThemisPresencePage"));
const ThemisPresencePageLazy = (props) => (<Suspense fallback={<SquelettePageJeton />}><ThemisPresencePageRaw {...props}/></Suspense>);
const ThemisPouvoirPageRaw = lazyPrechargeable(() => import("./ThemisPouvoirPage"));
const ThemisEmargementPageRaw = lazyPrechargeable(() => import("./ThemisEmargementPage"));
const ThemisEmargementPageLazy = (props) => (<Suspense fallback={<SquelettePageJeton />}><ThemisEmargementPageRaw {...props}/></Suspense>);
// Repas d'une séance (28/09/2026) : montée d'ordinaire par src/AppPublique.js ;
// ici en secours seulement (jamais de page blanche).
const ThemisRepasPageRaw = lazyPrechargeable(() => import("./ThemisRepasPage"));
const ThemisPouvoirPageLazy = (props) => (<Suspense fallback={<SquelettePageJeton />}><ThemisPouvoirPageRaw {...props}/></Suspense>);
const VeilleModuleRaw = lazyPrechargeable(() => import("./VeilleModule"));
const VeilleModuleLazy = (props) => (<Suspense fallback={<PictoLoader label="Chargement de la veille…"/>}><VeilleModuleRaw {...props}/></Suspense>);
// Carte interactive des implantations territoriales
const ImplantationsMapRaw = lazyPrechargeable(() => import("./ImplantationsMap"));
const ImplantationsMapLazy = (props) => (<Suspense fallback={<PictoLoader label="Chargement de la carte…"/>}><ImplantationsMapRaw {...props}/></Suspense>);
// Historiques par membre / par mutuelle (présence, votes, contributions, connexions)
const HistoriquesViewRaw = lazyPrechargeable(() => import("./HistoriquesView"));
const HistoriquesViewLazy = (props) => (<Suspense fallback={<PictoLoader label="Chargement des historiques…"/>}><HistoriquesViewRaw {...props}/></Suspense>);
// Dashboard "Ma semaine" + vue dirigeant "Engagement de ma mutuelle"
const MaSemaineViewRaw = lazyPrechargeable(() => import("./MaSemaineView"));
const MaSemaineViewLazy = (props) => (
  <Suspense fallback={
    <div className="page-largeur">
      {/* Hero skeleton */}
      <div className="aramis-shimmer" style={{height:120, borderRadius:"var(--radius-xl)", marginBottom:20}}/>
      {/* Stats skeleton */}
      <div style={{display:"grid", gridTemplateColumns:"repeat(auto-fit,minmax(min(160px,100%),1fr))", gap:10, marginBottom:18}}>
        {[1,2,3,4].map(i => (
          <div key={i} className="aramis-shimmer" style={{height:80, borderRadius:"var(--radius-md)"}}/>
        ))}
      </div>
      {/* Liste skeleton */}
      <SkeletonCard count={4}/>
    </div>
  }>
    <MaSemaineViewRaw {...props}/>
  </Suspense>
);
// Vitrine « Demander l'accès » (lot L7) : remplace la page d'excuses
// quand un module est visible mais pas ouvert à la personne.
const VitrineAccesRaw = lazyPrechargeable(() => import("./VitrineAcces"));
const VitrineAccesLazy = (props) => (<Suspense fallback={<PictoLoader label="Chargement…"/>}><VitrineAccesRaw {...props}/></Suspense>);
const EngagementMutuelleViewRaw = lazyPrechargeable(() => import("./EngagementMutuelleView"));
const EngagementMutuelleViewLazy = (props) => (<Suspense fallback={<PictoLoader label="Chargement…"/>}><EngagementMutuelleViewRaw {...props}/></Suspense>);
// Dashboard d'adoption (super-admin)
const AdoptionPanelRaw = lazyPrechargeable(() => import("./AdoptionPanel"));
const AdoptionPanelLazy = (props) => (<Suspense fallback={<PictoLoader label="Chargement stats…"/>}><AdoptionPanelRaw {...props}/></Suspense>);
// Paramétrage emails hebdo automatiques (admin)
const AdminEmailsAutoPanelRaw = lazyPrechargeable(() => import("./AdminEmailsAutoPanel"));
const AdminEmailsAutoPanelLazy = (props) => (<Suspense fallback={<PictoLoader label="Chargement…"/>}><AdminEmailsAutoPanelRaw {...props}/></Suspense>);
// Budget / appels de cotisation (admin)
const AdminBudgetPanelRaw = lazyPrechargeable(() => import("./AdminBudgetPanel"));
const AdminBudgetPanelLazy = (props) => (<Suspense fallback={<PictoLoader label="Chargement du budget…"/>}><AdminBudgetPanelRaw {...props}/></Suspense>);
// Boîte à outils budgétaire — paie ENIO, dépenses, projections (admin ; données de paie superadmin)
const BudgetToolboxPanelRaw = lazyPrechargeable(() => import("./BudgetToolboxPanel"));
const BudgetToolboxPanelLazy = (props) => (<Suspense fallback={<PictoLoader label="Chargement de la boîte à outils…"/>}><BudgetToolboxPanelRaw {...props}/></Suspense>);
// Dépenses & paiements — P2P : factures, validation, virements SEPA (admin)
const DepensesPanelRaw = lazyPrechargeable(() => import("./DepensesPanel"));
const DepensesPanelLazy = (props) => (<Suspense fallback={<PictoLoader label="Chargement des dépenses…"/>}><DepensesPanelRaw {...props}/></Suspense>);
const ComptaExpertPanelRaw = lazyPrechargeable(() => import("./ComptaExpertPanel"));
const ComptaExpertPanelLazy = (props) => (<Suspense fallback={<PictoLoader label="Chargement de la comptabilité…"/>}><ComptaExpertPanelRaw {...props}/></Suspense>);
// Comptabilité générale (src/ComptaModule.js) : DÉLIBÉRÉMENT NON MONTÉE.
// Le socle est livré et conservé au dépôt, mais le module n'est branché à
// AUCUNE route — décision Pierre 01/08/2026 : nous ne nous en servons pas à ce
// stade. Ne rien y imputer depuis du code nouveau (cf. commentaire de tête de
// functions/compta.js).
// Mes notes de frais — espace personnel unifié (accessible à tout compte Aramis)
const NotesFraisSelfRaw = lazyPrechargeable(() => import("./NotesFraisSelf"));
const NotesFraisSelfLazy = (props) => (<Suspense fallback={<PictoLoader label="Chargement…"/>}><NotesFraisSelfRaw {...props}/></Suspense>);
const AccessGovernancePanelRaw = lazyPrechargeable(() => import("./AccessGovernancePanel"));
const AccessGovernancePanelLazy = (props) => (<Suspense fallback={<PictoLoader label="Chargement…"/>}><AccessGovernancePanelRaw {...props}/></Suspense>);
const AdminAuthMesurePanelRaw = lazyPrechargeable(() => import("./AdminAuthMesurePanel"));
const AdminAuthMesurePanelLazy = (props) => (<Suspense fallback={<PictoLoader label="Chargement…"/>}><AdminAuthMesurePanelRaw {...props}/></Suspense>);
const AdminSanteCollectesPanelRaw = lazyPrechargeable(() => import("./AdminSanteCollectesPanel"));
const AdminSanteCollectesPanelLazy = (props) => (<Suspense fallback={<PictoLoader label="Chargement…"/>}><AdminSanteCollectesPanelRaw {...props}/></Suspense>);
const AdminUsagesPanelRaw = lazyPrechargeable(() => import("./AdminUsagesPanel"));
const AdminUsagesPanelLazy = (props) => (<Suspense fallback={<PictoLoader label="Chargement…"/>}><AdminUsagesPanelRaw {...props}/></Suspense>);
// Super administration › Synchronisation (13/10/2026) : tous les échanges avec
// Cowork sur une page ; super-administrateur strict (session RÉELLE).
const SynchronisationPageRaw = lazyPrechargeable(() => import("./synchronisation/SynchronisationPage"));
const NouveautesPageRaw = lazyPrechargeable(() => import("./nouveautes/NouveautesPage"));
const NouveautesPageLazy = (props) => (<Suspense fallback={<PictoLoader label="Chargement des nouveautés d'Aramis…"/>}><NouveautesPageRaw {...props}/></Suspense>);
const SynchronisationPageLazy = (props) => (<Suspense fallback={<PictoLoader label="Chargement des échanges avec Cowork…"/>}><SynchronisationPageRaw {...props}/></Suspense>);
const AdminErreursPanelRaw = lazyPrechargeable(() => import("./AdminErreursPanel"));
const AdminErreursPanelLazy = (props) => (<Suspense fallback={<PictoLoader label="Chargement…"/>}><AdminErreursPanelRaw {...props}/></Suspense>);
// Vote en direct de l'AG (24/09/2026) — console Pilotage. Les pages publiques
// /vote/<code> (téléphone) et /vote/<code>/ecran sont montées hors App (index.js).
const VoteAgConsoleRaw = lazyPrechargeable(() => import("./VoteAgConsole"));
const VoteAgConsoleLazy = (props) => (<Suspense fallback={<PictoLoader label="Chargement du vote en direct…"/>}><VoteAgConsoleRaw {...props}/></Suspense>);
const EmailTrackingPanelRaw = lazyPrechargeable(() => import("./EmailTrackingPanel"));
const EmailTrackingPanelLazy = (props) => (<Suspense fallback={<PictoLoader label="Chargement du suivi…"/>}><EmailTrackingPanelRaw {...props}/></Suspense>);
const MutuelleNewsFeedRaw = lazyPrechargeable(() => import("./MutuelleNewsFeed"));
const MutuelleNewsFeedLazy = (props) => (<Suspense fallback={<PictoLoader label="Chargement du flux…"/>}><MutuelleNewsFeedRaw {...props}/></Suspense>);
const AdminCampagnesPanelRaw = lazyPrechargeable(() => import("./AdminCampagnesPanel"));
const AdminCampagnesPanelLazy = (props) => (<Suspense fallback={<PictoLoader label="Chargement des campagnes…"/>}><AdminCampagnesPanelRaw {...props}/></Suspense>);
const VeilleJuridiqueModuleRaw = lazyPrechargeable(() => import("./VeilleJuridiqueModule"));
const VeilleJuridiqueModuleLazy = (props) => (<Suspense fallback={<PictoLoader label="Chargement de la veille juridique…"/>}><VeilleJuridiqueModuleRaw {...props}/></Suspense>);
const ReferentielOrganismesRaw = lazyPrechargeable(() => import("./ReferentielOrganismes"));
const ReferentielOrganismesLazy = (props) => (<Suspense fallback={<PictoLoader label="Chargement du référentiel…"/>}><ReferentielOrganismesRaw {...props}/></Suspense>);
// Les mutuelles en clair (11/10/2026) : SFCR publics des mutuelles lus par
// l'IA et vérifiés par le serveur (sfcrLire, sfcrAdmin). Fiches publiées pour
// tous ; brouillons et administration aux administrateurs de la session réelle.
const MutuellesEnClairRaw = lazyPrechargeable(() => import("./sfcr/MutuellesEnClair"));
const MutuellesEnClairLazy = (props) => (<Suspense fallback={<PictoLoader label="Chargement des mutuelles en clair…" pad={40}/>}><MutuellesEnClairRaw {...props}/></Suspense>);
// Fondations et fonds de dotation des mutuelles (Nos Mutuelles, 18/10/2026).
const FondationsMutuellesRaw = lazyPrechargeable(() => import("./fondations/FondationsMutuelles"));
const FondationsMutuellesLazy = (props) => (<Suspense fallback={<PictoLoader label="Chargement des fondations…" pad={40}/>}><FondationsMutuellesRaw {...props}/></Suspense>);
// Argos — intelligence économique. Seul import du module dans tout le repo :
// aucun fichier Argos ne doit être importé statiquement ailleurs (App.jsx,
// appShared.js, Header.js, navConstants.js, GlobalSearch.js), sous peine
// d'annuler son code-splitting (cf. l'avertissement en tête de ce fichier).
const ArgosModuleRaw = lazyPrechargeable(() => import("./ArgosModule"));
const ArgosModuleLazy = (props) => (<Suspense fallback={<PictoLoader label="Chargement d'Argos…"/>}><ArgosModuleRaw {...props}/></Suspense>);
const GelPpeModuleRaw = lazyPrechargeable(() => import("./GelPpeModule"));
const GelPpeModuleLazy = (props) => (<Suspense fallback={<PictoLoader label="Chargement du module conformité…"/>}><GelPpeModuleRaw {...props}/></Suspense>);
const TourHubPageRaw = lazyPrechargeable(() => import("./TourHubPage"));
const TourHubPageLazy = (props) => (<Suspense fallback={<PictoLoader pad={40}/>}><TourHubPageRaw {...props}/></Suspense>);

// ── Perf P2 : dernières grosses vues sorties de main.js (~1 Mo brut cumulé).
// Même pattern lazy+Suspense que le reste du fichier. Admin entraîne toute sa
// chaîne (SuperAdminPanel → MutuellesAdminPanel) dans son chunk.
const AdminRaw = lazyPrechargeable(() => import("./Admin"));
const AdminLazy = (props) => (<Suspense fallback={<PictoLoader label="Chargement de l'administration…"/>}><AdminRaw {...props}/></Suspense>);
const ThemisViewRaw = lazyPrechargeable(() => import("./ThemisView"));
const ThemisViewLazy = (props) => (<Suspense fallback={<PictoLoader label="Chargement de la convocation…"/>}><ThemisViewRaw {...props}/></Suspense>);
const AthosReunionsViewRaw = lazyPrechargeable(() => import("./AthosReunionsView"));
const AthosReunionsViewLazy = (props) => (<Suspense fallback={<PictoLoader label="Chargement des réunions…"/>}><AthosReunionsViewRaw {...props}/></Suspense>);
const GouvernancePanelRaw = lazyPrechargeable(() => import("./GouvernancePanel"));
const GouvernancePanelLazy = (props) => (<Suspense fallback={<PictoLoader label="Chargement de la gouvernance…"/>}><GouvernancePanelRaw {...props}/></Suspense>);
const AthosProjetsRaw = lazyPrechargeable(() => import("./AthosProjectsView"));
// Questions & entraide (Gouvernance participative) : extraites d'App.jsx (lot
// L11c.0), en UN morceau commun : la fenêtre « Poser une question » (en-tête,
// conversion d'une idée), le fil d'une question (ouvert aussi par Domaines &
// Pôles et par une notification) et l'historique. La Boîte à idées suit.
const chargerQuestions = () => import("./QuestionsEntraide");
const QuestionsHistoriqueViewRaw = lazyPrechargeable(() => chargerQuestions().then((m) => ({ default: m.QuestionsHistoriqueView })));
const QuestionModalRaw = lazyPrechargeable(() => chargerQuestions().then((m) => ({ default: m.QuestionModal })));
const QuestionThreadRaw = lazyPrechargeable(() => chargerQuestions().then((m) => ({ default: m.QuestionThread })));
const QuestionsHistoriqueViewLazy = (props) => (<Suspense fallback={<PictoLoader label="Chargement des questions…" pad={40}/>}><QuestionsHistoriqueViewRaw {...props}/></Suspense>);
const QuestionModalLazy = (props) => (<Suspense fallback={null}><QuestionModalRaw {...props}/></Suspense>);
const QuestionThreadLazy = (props) => (<Suspense fallback={null}><QuestionThreadRaw {...props}/></Suspense>);
const IdeaBoxPublicRaw = lazyPrechargeable(() => import("./IdeaBoxPublic"));
// Résultats des votes (bandeau de commande, 27/10/2026) : la barre de la
// liste (En direct / Archivées en segments) vient de pageTravail.js, jamais
// au bundle principal : chargée à la demande (src/BarreResultats.js).
const BarreResultatsRaw = lazyPrechargeable(() => import("./BarreResultats"));
const IdeaBoxPublicLazy = (props) => (<Suspense fallback={<PictoLoader label="Chargement de la boîte à idées…" pad={40}/>}><IdeaBoxPublicRaw {...props}/></Suspense>);
// Annuaire des membres (Nos Mutuelles) : extrait d'App.jsx (lot L11b.0).
const AthosAnnuaireViewRaw = lazyPrechargeable(() => import("./AthosAnnuaireView"));
const AthosAnnuaireViewLazy = (props) => (<Suspense fallback={<PictoLoader label="Chargement de l'annuaire…" pad={40}/>}><AthosAnnuaireViewRaw {...props}/></Suspense>);
const AthosProjetsLazy = (props) => (<Suspense fallback={<PictoLoader label="Chargement des projets…" pad={40}/>}><AthosProjetsRaw {...props}/></Suspense>);
// Demandes d'engagement (Activité, lot L11d-bis) : la page ; et l'écran de la
// page « Demandes de compte » d'Administration (lot L11i), qui garde le renvoi.
const DemandesEngagementViewRaw = lazyPrechargeable(() => import("./DemandesEngagementView"));
const DemandesEngagementViewLazy = (props) => (<Suspense fallback={<PictoLoader label="Chargement des demandes…" pad={40}/>}><DemandesEngagementViewRaw {...props}/></Suspense>);
// Affaires publiques (02/10/2026, module depuis le 12/10/2026) : l'index de la rubrique et le
// Radar Présidentielle 2027 (src/radar/), deux morceaux paresseux. Lecture
// ouverte à tout utilisateur connecté (vues transverses : aucune entrée
// VIEW_ACCESS) ; le serveur vérifie la session (radarLire) et réserve les
// gestes d'administration aux administrateurs.
const AffairesPubliquesViewRaw = lazyPrechargeable(() => import("./AffairesPubliquesView"));
const AffairesPubliquesViewLazy = (props) => (<Suspense fallback={<PictoLoader label="Chargement…" pad={40}/>}><AffairesPubliquesViewRaw {...props}/></Suspense>);
const RadarPresidentielleRaw = lazyPrechargeable(() => import("./radar/RadarPresidentielle"));
const RadarPresidentielleLazy = (props) => (<Suspense fallback={<PictoLoader label="Chargement du radar…" pad={40}/>}><RadarPresidentielleRaw {...props}/></Suspense>);
const PlfPlfssRaw = lazyPrechargeable(() => import("./lf/PlfPlfss"));
const PlfPlfssLazy = (props) => (<Suspense fallback={<PictoLoader label="Chargement du suivi PLF & PLFSS…" pad={40}/>}><PlfPlfssRaw {...props}/></Suspense>);
// Rapports AMO-AMC (05/10/2026) : analyse des deux rapports publics de
// septembre 2026 et du communiqué de la Mutualité Française, hors du suivi
// PLF & PLFSS. Aucune donnée serveur : tout est dans le contenu du morceau.
const RapportsAmoAmcRaw = lazyPrechargeable(() => import("./lf/rapports/RapportsAmoAmc"));
const RapportsAmoAmcLazy = (props) => (<Suspense fallback={<PictoLoader label="Chargement des rapports AMO-AMC…" pad={40}/>}><RapportsAmoAmcRaw {...props}/></Suspense>);
// Textes de loi (lot B1.2, 11/10/2026) : tous les dossiers de l'inventaire de
// l'Assemblée et les dossiers suivis (lfLire, lfReglages). Lecture ouverte à
// tout connecté ; « Suivre » aux administrateurs de la session réelle.
const TextesLoiRaw = lazyPrechargeable(() => import("./textesLoi/TextesLoi"));
const TextesLoiLazy = (props) => (<Suspense fallback={<PictoLoader label="Chargement des textes de loi…" pad={40}/>}><TextesLoiRaw {...props}/></Suspense>);
// Refonte des Affaires publiques (12/10/2026, maquettes v1 et v2) : le texte
// en clair, Parlementaires, Votes et scrutins. Lecture ouverte à tout
// connecté ; un morceau paresseux par page.
const TexteEnClairRaw = lazyPrechargeable(() => import("./affairesPubliques/TexteEnClairView"));
const TexteEnClairLazy = (props) => (<Suspense fallback={<PictoLoader label="Chargement du texte en clair…" pad={40}/>}><TexteEnClairRaw {...props}/></Suspense>);
const ParlementairesRaw = lazyPrechargeable(() => import("./affairesPubliques/ParlementairesView"));
const ParlementairesLazy = (props) => (<Suspense fallback={<PictoLoader label="Chargement des parlementaires…" pad={40}/>}><ParlementairesRaw {...props}/></Suspense>);
// Fiche d'un parlementaire en pleine page (lot B, 12/10/2026) : vue
// « parlementaire » (?elu=PA… ou SEN-…), sous-page de Parlementaires.
const FicheParlementaireRaw = lazyPrechargeable(() => import("./affairesPubliques/FicheParlementaire"));
const FicheParlementaireLazy = (props) => (<Suspense fallback={<PictoLoader label="Chargement de la fiche…" pad={40}/>}><FicheParlementaireRaw {...props}/></Suspense>);
const VotesScrutinsRaw = lazyPrechargeable(() => import("./affairesPubliques/VotesScrutinsView"));
const VotesScrutinsLazy = (props) => (<Suspense fallback={<PictoLoader label="Chargement des votes et scrutins…" pad={40}/>}><VotesScrutinsRaw {...props}/></Suspense>);
// Fiche d'un scrutin en pleine page (lot C, 12/10/2026) : vue « scrutin »
// (?uid=VTANR5L… ou SEN-…), sous-page de Votes et scrutins.
const FicheScrutinRaw = lazyPrechargeable(() => import("./affairesPubliques/FicheScrutin"));
const FicheScrutinLazy = (props) => (<Suspense fallback={<PictoLoader label="Chargement du scrutin…" pad={40}/>}><FicheScrutinRaw {...props}/></Suspense>);
// Fiche d'un amendement en pleine page (lot D, 12/10/2026) : vue « amendement »
// (?uid=AMANR5L…), sous-page de Textes de loi ; notre position pour l'équipe
// d'Enio de la session RÉELLE seule, jamais en « Voir comme ».
const PageAmendementRaw = lazyPrechargeable(() => import("./affairesPubliques/PageAmendement"));
const PageAmendementLazy = (props) => (<Suspense fallback={<PictoLoader label="Chargement de l'amendement…" pad={40}/>}><PageAmendementRaw {...props}/></Suspense>);
// Fiche d'un article de loi en pleine page (lot E, 12/10/2026) : vue
// « article-de-loi » (?texte=…&article=… ou ?dossier=DLR…&article=…), sous-page
// du texte en clair ; notre position pour l'équipe d'Enio de la session RÉELLE
// seule, jamais en « Voir comme ».
const FicheArticleRaw = lazyPrechargeable(() => import("./affairesPubliques/FicheArticle"));
const FicheArticleLazy = (props) => (<Suspense fallback={<PictoLoader label="Chargement de l'article…" pad={40}/>}><FicheArticleRaw {...props}/></Suspense>);
// Notes institutionnelles (B6, 11/10/2026) : un seul morceau paresseux pour
// ses trois vues (liste, fiche d'une note, modèles) ; administrateurs seuls.
const NotesModuleRaw = lazyPrechargeable(() => import("./notesInst/NotesModule"));
const NotesModuleLazy = (props) => (<Suspense fallback={<PictoLoader label="Chargement des notes institutionnelles…" pad={40}/>}><NotesModuleRaw {...props}/></Suspense>);
// Doctrine d'Enio (socle, 11/10/2026) : un seul morceau paresseux pour ses
// trois vues (index des thèmes, fiche d'un thème, import d'un premier jet) ;
// équipe d'Enio (administrateurs) seule.
const DoctrineModuleRaw = lazyPrechargeable(() => import("./doctrine/DoctrineModule"));
const DoctrineModuleLazy = (props) => (<Suspense fallback={<PictoLoader label="Chargement de la doctrine…" pad={40}/>}><DoctrineModuleRaw {...props}/></Suspense>);
// Positions d'Enio (doctrine ouverte, lot L2, 12/10/2026) : la page des
// membres, un morceau paresseux À PART de l'atelier ; tout membre connecté,
// comptes invités exclus.
const DoctrinePositionsRaw = lazyPrechargeable(() => import("./doctrine/DoctrinePositions"));
const DoctrinePositionsLazy = (props) => (<Suspense fallback={<PictoLoader label="Chargement des positions d'Enio…" pad={40}/>}><DoctrinePositionsRaw {...props}/></Suspense>);
const PageDemandesCompteRaw = lazyPrechargeable(() => import("./DemandesCompteVue"));
const PageDemandesCompteLazy = (props) => (<Suspense fallback={<PictoLoader label="Chargement des demandes…" pad={40}/>}><PageDemandesCompteRaw {...props}/></Suspense>);
const MonProfilViewRaw = lazyPrechargeable(() => import("./MonProfilView"));
const MonProfilViewLazy = (props) => (<Suspense fallback={<PictoLoader label="Chargement du profil…"/>}><MonProfilViewRaw {...props}/></Suspense>);
// Mes formations, écran personnel ouvert depuis « Moi » (16/10/2026).
const MesFormationsMembreRaw = lazyPrechargeable(() => import("./mesFormations/MesFormationsMembre"));
const MesFormationsMembreLazy = (props) => (<Suspense fallback={<PictoLoader label="Chargement de vos formations…"/>}><MesFormationsMembreRaw {...props}/></Suspense>);
const EmailingViewRaw = lazyPrechargeable(() => import("./EmailingView"));
const EmailingViewLazy = (props) => (<Suspense fallback={<PictoLoader label="Chargement de l'emailing…"/>}><EmailingViewRaw {...props}/></Suspense>);
const ParticipantRaw = lazyPrechargeable(() => import("./Participant"));
// Habillage de l'écran de salle d'une séance (grammaire de l'écran du vote de l'AG).
const SalleSeanceLazy = lazyPrechargeable(() => import("./SalleSeance"));
const ParticipantLazy = (props) => (<Suspense fallback={<PictoLoader label="Chargement du vote…"/>}><ParticipantRaw {...props}/></Suspense>);

// Espace collaboratif par pôle (discussions / réunions / notifs)
// Hub Domaines & Pôles (landing → domaine → pôle) — UX redesignée
const PolesHubModuleRaw = lazyPrechargeable(() => import("./PolesHubModule"));
const PolesHubModuleLazy = (props) => (<Suspense fallback={<PictoLoader label="Chargement de l'espace Domaines & Pôles…" pad={40}/>}><PolesHubModuleRaw {...props}/></Suspense>);
// Recherche globale ⌘K
const GlobalSearchRaw = lazyPrechargeable(() => import("./GlobalSearch"));
// Socrate, interlocuteur unique de l'équipe (sprint 0, 09/09/2026) : client
// neuf, chargé paresseusement, réservé aux rôles admin/superadmin — le
// composant demande son statut au serveur (interrupteur securiteConfig/socrate)
// avant d'afficher son bouton. L'ancien widget a été retiré le 27/06/2026.
const SocrateRaw = lazyPrechargeable(() => import("./Socrate"));
const SocrateLazy = (props) => (<Suspense fallback={null}><SocrateRaw {...props}/></Suspense>);
const GlobalSearchLazy = (props) => (<Suspense fallback={null}><GlobalSearchRaw {...props}/></Suspense>);
const FeuilleModulesRaw = lazyPrechargeable(() => import("./FeuilleModules"));
const ThemisEvenementsLazy = (props) => (
  <Suspense fallback={<PictoLoader label="Chargement..."/>}>
    <ThemisEvenementsRaw {...props}/>
  </Suspense>
);
// Refonte module Événements (hub façon Calypso + centre de validation + portail partenaire)
const ThemisEvenementsHomeRaw = lazyPrechargeable(() => import("./ThemisEvenementsHome"));
const ThemisEvenementsHomeLazy = (props) => (<Suspense fallback={<PictoLoader label="Chargement…" pad={40}/>}><ThemisEvenementsHomeRaw {...props}/></Suspense>);
const ThemisEventValidationRaw = lazyPrechargeable(() => import("./ThemisEventValidation"));
const ThemisEventValidationLazy = (props) => (<Suspense fallback={<PictoLoader label="Chargement…" pad={40}/>}><ThemisEventValidationRaw {...props}/></Suspense>);
const PagePartnerEventRaw = lazyPrechargeable(() => import("./PagePartnerEvent"));
const PagePartnerEventLazy = (props) => (<Suspense fallback={<PictoLoader label="Chargement de votre espace…" pad={40}/>}><PagePartnerEventRaw {...props}/></Suspense>);



// ⟦CHUNKS_VUES : généré par src/prechargementVues.test.js, ne pas éditer⟧
// Vue → composants paresseux qu'elle monte (préchargement à l'intention, attente
// du code avant la bascule). [] : rien à charger. Absente : vue inconnue.
const CHUNKS_VUES = {
  "accueil": [LegacyViewRedirectRaw],
  "accueil-athos": [ModuleVitrineRaw],
  "accueil-conformite": [ModuleVitrineRaw],
  "accueil-gouv": [GouvernanceAccueilRaw],
  "accueil-marches": [ModuleVitrineRaw],
  "accueil-mutuelles": [ModuleVitrineRaw],
  "accueil-pilotage": [ModuleVitrineRaw],
  "accueil-rh": [RHModuleRaw],
  "admin": [AdminRaw],
  "admin-acces": [AccessGovernancePanelRaw],
  "admin-adoption": [AdoptionPanelRaw],
  "admin-auth-mesure": [AdminAuthMesurePanelRaw],
  "admin-budget": [AdminBudgetPanelRaw],
  "admin-budget-outils": [LegacyViewRedirectRaw],
  "admin-campagnes": [AdminCampagnesPanelRaw],
  "admin-demandes": [PageDemandesCompteRaw],
  "admin-depenses": [DepensesPanelRaw],
  "admin-emails-auto": [AdminEmailsAutoPanelRaw],
  "admin-envois": [EmailTrackingPanelRaw],
  "admin-erreurs": [AdminErreursPanelRaw],
  "admin-ndf": [DepensesPanelRaw],
  "admin-nouveautes": [NouveautesPageRaw],
  "admin-sante-collectes": [AdminSanteCollectesPanelRaw],
  "admin-usages": [AdminUsagesPanelRaw],
  "admin-vote-ag": [VoteAgConsoleRaw],
  "affaires-publiques": [AffairesPubliquesViewRaw],
  "agora": [LegacyViewRedirectRaw],
  "agora-admin": [AdminRaw],
  "amendement": [PageAmendementRaw],
  "argos": [ArgosModuleRaw],
  "argos-dossiers": [ArgosModuleRaw],
  "argos-entreprise": [ArgosModuleRaw],
  "argos-graphe": [ArgosModuleRaw, LegacyViewRedirectRaw],
  "argos-journal": [ArgosModuleRaw],
  "argos-personne": [ArgosModuleRaw],
  "argos-sources": [ArgosModuleRaw],
  "argos-surveillance": [ArgosModuleRaw],
  "article-de-loi": [FicheArticleRaw],
  "athos": [AthosHomeV2Raw],
  "athos-annuaire": [AthosAnnuaireViewRaw],
  "athos-dashboard": [LegacyViewRedirectRaw],
  "athos-demandes": [DemandesEngagementViewRaw],
  "athos-domaines-poles": [PolesHubModuleRaw],
  "athos-engagement-mutuelle": [EngagementMutuelleViewRaw],
  "athos-home-classique": [LegacyViewRedirectRaw],
  "athos-implantations": [ImplantationsMapRaw],
  "athos-ma-mutuelle": [EngagementMutuelleViewRaw],
  "athos-migration-review": [LegacyViewRedirectRaw],
  "athos-missions": [LegacyViewRedirectRaw],
  "athos-missions-statutaires": [LegacyViewRedirectRaw],
  "athos-noota": [],
  "athos-projets": [AthosProjetsRaw],
  "athos-projets-classique": [LegacyViewRedirectRaw],
  "athos-reunions": [AthosReunionsViewRaw],
  "athos-solutions": [LegacyViewRedirectRaw],
  "audit": [AuditModuleRaw],
  "audit-acces": [AuditModuleRaw],
  "audit-actions": [AuditModuleRaw],
  "audit-constat": [AuditModuleRaw],
  "audit-mission": [AuditModuleRaw],
  "audit-modeles": [AuditModuleRaw],
  "audit-plan": [AuditModuleRaw],
  "audit-plans": [AuditModuleRaw],
  "audit-propositions": [LegacyViewRedirectRaw],
  "audit-registre": [AuditModuleRaw],
  "budget-accueil": [BudgetToolboxPanelRaw],
  "budget-compte": [BudgetToolboxPanelRaw],
  "budget-depenses": [BudgetToolboxPanelRaw],
  "budget-exercice": [BudgetToolboxPanelRaw],
  "budget-paie": [BudgetToolboxPanelRaw],
  "budget-simulation": [BudgetToolboxPanelRaw],
  "budget-situation": [BudgetToolboxPanelRaw],
  "classement": [LegacyViewRedirectRaw],
  "compta-ec": [ComptaExpertPanelRaw],
  "conformite-cowork": [LegacyViewRedirectRaw],
  "conformite-demonstration": [ControleInterneModuleRaw],
  "conformite-propositions": [ControleInterneModuleRaw],
  "consultations": [ConsultationAsyncRaw],
  "controle-interne": [ControleInterneModuleRaw],
  "controle-interne-acces": [ControleInterneModuleRaw],
  "controle-interne-referentiel": [ControleInterneModuleRaw],
  "dataviz": [DatavizModuleRaw],
  "doctrine": [DoctrineModuleRaw],
  "doctrine-debat": [DoctrineModuleRaw],
  "doctrine-debats": [DoctrineModuleRaw],
  "doctrine-import": [DoctrineModuleRaw],
  "doctrine-mes-propositions": [DoctrinePositionsRaw],
  "doctrine-positions": [DoctrinePositionsRaw],
  "doctrine-propositions": [DoctrineModuleRaw],
  "doctrine-theme": [DoctrineModuleRaw],
  "emailing": [EmailingViewRaw],
  "event-day": [EventDayPanelRaw],
  "event-fiche": [EventFicheRaw],
  "event-guest-list": [LegacyEventRedirectRaw],
  "event-invitation-requests": [LegacyEventRedirectRaw],
  "event-rsvp-inbox": [LegacyEventRedirectRaw],
  "event-suivi": [EventSuiviRaw],
  "event-table-plan": [LegacyEventRedirectRaw],
  "exploration": [ExplorationHomeRaw],
  "fondations-mutuelles": [FondationsMutuellesRaw],
  "gel-avoirs-ppe": [GelPpeModuleRaw],
  "gouvernance": [GouvernancePanelRaw],
  "hestia": [HestiaModuleRaw],
  "hestia-a-traiter": [HestiaModuleRaw],
  "hestia-objectif": [HestiaModuleRaw],
  "historiques": [HistoriquesViewRaw],
  "hygie": [DPOGateRaw, HygieHomeRaw],
  "hygie-aipd": [DPOGateRaw, HygieAIPDRaw],
  "hygie-annuaire": [DPOGateRaw, HygieAnnuaireRaw],
  "hygie-dashboard": [DPOGateRaw, HygieDashboardRaw],
  "hygie-dsar": [DPOGateRaw, HygieDSARRaw],
  "hygie-formation": [DPOGateRaw, HygieFormationRaw],
  "hygie-forum": [DPOGateRaw, HygieForumRaw],
  "hygie-library": [DPOGateRaw, HygieLibraryRaw],
  "hygie-library-admin": [DPOGateRaw, HygieLibraryAdminRaw],
  "hygie-registre": [DPOGateRaw, HygieRegistreRaw],
  "hygie-reprise": [DPOGateRaw, HygieRepriseRaw],
  "hygie-subprocessors": [DPOGateRaw, HygieSubprocessorsRaw],
  "hygie-veille": [LegacyViewRedirectRaw],
  "hygie-violations": [DPOGateRaw, HygieViolationsRaw],
  "ideas": [IdeaBoxPublicRaw],
  "ma-semaine": [MaSemaineViewRaw],
  "mediatheque": [MediathequeModuleRaw],
  "mediatheque-album": [MediathequeModuleRaw],
  "mes-formations": [MesFormationsMembreRaw],
  "mes-notes-frais": [NotesFraisSelfRaw],
  "mon-profil": [MonProfilViewRaw],
  "mutuelles-en-clair": [MutuellesEnClairRaw],
  "note-institutionnelle": [NotesModuleRaw],
  "notes-institutionnelles": [NotesModuleRaw],
  "notes-institutionnelles-modeles": [NotesModuleRaw],
  "notes-recues": [NotesModuleRaw],
  "nouvelles-mutuelles": [MutuelleNewsFeedRaw],
  "parametrage-mutuelle": [ControleInterneModuleRaw],
  "parlementaire": [FicheParlementaireRaw],
  "parlementaires": [ParlementairesRaw],
  "participant": [ParticipantRaw],
  "pilotage-hestia": [HestiaModuleRaw],
  "pilotage-seances": [ThemisInstancesHubRaw],
  "plf-plfss-2027": [PlfPlfssRaw],
  "preparer-presentation": [PreparationPresentationRaw],
  "procedure": [ControleInterneModuleRaw],
  "procedures": [ControleInterneModuleRaw],
  "questions": [LegacyViewRedirectRaw],
  "questions-historique": [QuestionsHistoriqueViewRaw],
  "radar-presidentielle-2027": [RadarPresidentielleRaw],
  "rapports-amo-amc": [RapportsAmoAmcRaw],
  "referentiel-organismes": [ReferentielOrganismesRaw],
  "ressources": [LegacyViewRedirectRaw],
  "results": [],
  "rh": [RHModuleRaw],
  "rh-admin-config": [RHModuleRaw],
  "rh-banque-cv": [RHModuleRaw],
  "rh-bilan-social": [RHModuleRaw],
  "rh-candidatures": [RHModuleRaw],
  "rh-co-recrutement": [RHModuleRaw],
  "rh-competences": [RHModuleRaw],
  "rh-comptes": [RHModuleRaw],
  "rh-conservation": [RHModuleRaw],
  "rh-cv-detail": [RHModuleRaw],
  "rh-effectifs": [RHModuleRaw],
  "rh-email-templates": [RHModuleRaw],
  "rh-entretiens": [RHModuleRaw],
  "rh-fiches-poste": [RHModuleRaw],
  "rh-formations-catalogue": [RHModuleRaw],
  "rh-formations-sessions": [RHModuleRaw],
  "rh-formations-suivi": [RHModuleRaw],
  "rh-formations-suivies": [RHModuleRaw],
  "rh-import-email": [RHModuleRaw],
  "rh-lisez-moi": [RHModuleRaw],
  "rh-login": [RHModuleRaw],
  "rh-matching": [RHModuleRaw],
  "rh-offre-detail": [RHModuleRaw],
  "rh-offres-emploi": [RHModuleRaw],
  "rh-organigramme": [RHModuleRaw],
  "rh-personnalisation": [RHModuleRaw],
  "rh-pipeline": [RHModuleRaw],
  "rh-pipeline-templates": [RHModuleRaw],
  "rh-plan-formation": [RHModuleRaw],
  "rh-recruteurs": [RHModuleRaw],
  "rh-registre-formation": [RHModuleRaw],
  "rh-scorecard-templates": [RHModuleRaw],
  "rh-simulateur-paie": [RHModuleRaw],
  "rh-statistiques": [RHModuleRaw],
  "rh-taches": [RHModuleRaw],
  "rh-talent-pool": [RHModuleRaw],
  "risque": [ControleInterneModuleRaw],
  "risques": [ControleInterneModuleRaw],
  "risques-bibliotheque": [ControleInterneModuleRaw],
  "risques-propositions": [LegacyViewRedirectRaw],
  "risques-tableau": [ControleInterneModuleRaw],
  "scrutin": [FicheScrutinRaw],
  "seance-emargement": [AdminRaw],
  "seance-scrutins": [AdminRaw],
  "strategie-2030": [LegacyViewRedirectRaw],
  "super-synchronisation": [SynchronisationPageRaw],
  "texte-en-clair": [TexteEnClairRaw],
  "textes-de-loi": [TextesLoiRaw],
  "themis": [LegacyViewRedirectRaw],
  "themis-contacts": [ThemisEvenementsRaw],
  "themis-documents-seance": [ThemisDocumentsSeanceRaw],
  "themis-emargement": [ThemisEmargementPageRaw],
  "themis-evenements": [ThemisEvenementsHomeRaw],
  "themis-evenements-validation": [ThemisEventValidationRaw],
  "themis-events-list": [ThemisEvenementsRaw],
  "themis-handwritten": [HandwrittenLibraryRaw],
  "themis-historique": [AdminRaw],
  "themis-instances": [LegacyViewRedirectRaw],
  "themis-live": [AdminRaw],
  "themis-pending-updates": [LegacyEventRedirectRaw],
  "themis-pouvoir": [ThemisPouvoirPageRaw],
  "themis-pouvoir-confirmation": [ThemisPouvoirPageRaw],
  "themis-preparer": [LegacyViewRedirectRaw],
  "themis-presence": [ThemisPresencePageRaw],
  "themis-repas": [ThemisRepasPageRaw],
  "themis-seance": [AdminRaw],
  "tour-hub": [TourHubPageRaw],
  "travail-groupes": [TravailGroupesRaw],
  "veille": [VeilleModuleRaw],
  "veille-juridique": [VeilleJuridiqueModuleRaw],
  "votes-et-scrutins": [VotesScrutinsRaw],
};
const CHUNKS_PREFIXES = [
  ["admin-super-", [AdminRaw]],
  ["gouvernance-", [GouvernancePanelRaw]],
  ["mon-espace-salarie", [RHEspaceSalarieRaw]],
];
const chunksDeVueApp = (v) => CHUNKS_VUES[v] || (CHUNKS_PREFIXES.find(([p]) => typeof v === "string" && v.startsWith(p)) || [null, null])[1];
enregistrerChunksVues(chunksDeVueApp);
// ⟦fin CHUNKS_VUES⟧


/* =============================================================================
   ENIOVOTE v5 - Application complete avec Firebase Firestore
   ============================================================================= */

const injectFont = () => {
  if (document.getElementById("enio-font")) return;
  const l = document.createElement("link");
  l.id = "enio-font"; l.rel = "stylesheet";
  // Montserrat VARIABLE (axe wght 200→700, lot L4) — même URL que
  // public/index.html ; 800 et 900 sont rendus en 700.
  l.href = "https://fonts.googleapis.com/css2?family=Montserrat:wght@200..700&display=swap";
  document.head.appendChild(l);
};





// Confettis supprimés (trop festifs pour un outil de dirigeants).

// -- PDF export (jsPDF via CDN) -------------------------------------------------


// -- Jauge circulaire SVG ------------------------------------------------------
const CircleGauge = ({value,max,size=160,color=C.teal,label}) => {
  const r=60;const circ=2*Math.PI*r;
  const pct=max>0?Math.min(value/max,1):0;
  const dash=pct*circ;
  return(
    <div style={{textAlign:"center",position:"relative",display:"inline-block"}}>
      <svg width={size} height={size} style={{transform:"rotate(-90deg)"}}>
        <circle cx={size/2} cy={size/2} r={r} fill="none" stroke="var(--border-color)" strokeWidth={12}/>
        <circle cx={size/2} cy={size/2} r={r} fill="none" stroke={color} strokeWidth={12}
          strokeDasharray={`${dash} ${circ-dash}`}
          strokeLinecap="round"
          style={{transition:"stroke-dasharray 1.2s cubic-bezier(.22,1,.36,1)"}}/>
      </svg>
      <div style={{position:"absolute",inset:0,display:"flex",flexDirection:"column",alignItems:"center",justifyContent:"center"}}>
        <div style={{fontSize:taillePx(size/4.5),fontWeight:700,color: navTexte(color),lineHeight:1}}>{value}</div>
        {label&&<div style={{fontSize:taillePx(size/11),color:navTexte(C.gray),marginTop:2}}>{label}</div>}
      </div>
    </div>
  );
};


// ADMIN_CODE supprimé pour raison de sécurité (était hardcodé dans le client)



// eslint-disable-next-line no-unused-vars
const _buildPouvoirPdf_legacy = (session, titulaire, mandant) => {
  loadJsPDF((jsPDF)=>{
    const doc = new jsPDF({orientation:"portrait",unit:"mm",format:"a4"});
    const W=210; const margin=22; const usable=W-2*margin;
    let y = margin;
    const ln=(h=7)=>{y+=h;};

    // En-tête
    doc.setFillColor(13,58,79);
    doc.rect(0,0,W,36,"F");
    doc.setTextColor(255,255,255);
    doc.setFontSize(18);doc.setFont("helvetica","bold");
    doc.text("Pouvoir de representation",margin,16);
    doc.setFontSize(9);doc.setFont("helvetica","normal");
    doc.setTextColor(232,119,34);
    doc.text("Assemblee Generale d'Enio — Union de Groupe Mutualiste",margin,24);
    doc.setTextColor(180,200,210);
    doc.text("SIREN 788 486 637 — Siege : 53 Cours Mirabeau, 13100 Aix-en-Provence",margin,30);
    y=48;

    // Encadré principal
    doc.setDrawColor(42,157,143);
    doc.setLineWidth(0.5);
    doc.rect(margin,y,usable,90);
    y+=8;

    doc.setTextColor(13,58,79);
    doc.setFontSize(11);doc.setFont("helvetica","bold");
    doc.text("Je, soussigne(e),",margin+6,y);ln(8);

    doc.setFontSize(12);doc.setFont("helvetica","bold");doc.setTextColor(232,119,34);
    doc.text((titulaire.prenom||"")+" "+(titulaire.nom||"").toUpperCase(),margin+6,y);ln(6);
    doc.setFontSize(10);doc.setFont("helvetica","normal");doc.setTextColor(60,60,60);
    doc.text("Delegue(e) a l'Assemblee Generale d'Enio",margin+6,y);ln(5);
    doc.text("Representant(e) : "+(titulaire.mutuelle||"—"),margin+6,y);ln(10);

    doc.setFontSize(11);doc.setFont("helvetica","bold");doc.setTextColor(13,58,79);
    doc.text("donne pouvoir a,",margin+6,y);ln(8);

    doc.setFontSize(12);doc.setFont("helvetica","bold");doc.setTextColor(232,119,34);
    doc.text((mandant.prenom||"")+" "+(mandant.nom||"").toUpperCase(),margin+6,y);ln(6);
    doc.setFontSize(10);doc.setFont("helvetica","normal");doc.setTextColor(60,60,60);
    doc.text("Delegue(e) a l'Assemblee Generale d'Enio",margin+6,y);ln(5);
    doc.text("Representant(e) : "+(mandant.mutuelle||"—"),margin+6,y);ln(12);

    doc.setFontSize(10);doc.setFont("helvetica","normal");doc.setTextColor(40,40,40);
    const motif = "pour me representer et voter en mon nom a l'Assemblee Generale d'Enio";
    const lines1=doc.splitTextToSize(motif+" se reunissant le "+(session?.date||"…")+" en toutes ses deliberations, conformement aux dispositions de l'article 16 des Statuts d'Enio.",usable-12);
    lines1.forEach(l=>{doc.text(l,margin+6,y);ln(6);});
    y+=4;

    // Bloc signatures
    doc.setFillColor(245,242,237);
    doc.rect(margin,y,usable,52,"F");
    doc.setDrawColor(200,200,200);doc.setLineWidth(0.3);
    doc.rect(margin,y,usable,52);
    y+=8;
    doc.setFontSize(9);doc.setFont("helvetica","bold");doc.setTextColor(13,58,79);
    doc.text("Signature du mandant (delegue donnant pouvoir)",margin+6,y);
    doc.text("Signature du mandataire (delegue recevant pouvoir)",margin+usable/2+4,y);
    y+=4;
    doc.setFontSize(8);doc.setFont("helvetica","normal");doc.setTextColor(120,120,120);
    doc.text("Preceder de la mention manuscrite 'Bon pour pouvoir'",margin+6,y);
    doc.text("Preceder de la mention manuscrite 'Bon pour acceptation'",margin+usable/2+4,y);
    y+=28;
    doc.setDrawColor(150,150,150);doc.setLineWidth(0.3);
    doc.line(margin+6,y,margin+usable/2-6,y);
    doc.line(margin+usable/2+4,y,margin+usable-6,y);
    y+=5;
    doc.setFontSize(8);doc.setTextColor(120,120,120);
    doc.text((titulaire.prenom||"")+" "+(titulaire.nom||""),margin+6,y);
    doc.text((mandant.prenom||"")+" "+(mandant.nom||""),margin+usable/2+4,y);

    y+=18;
    // Note légale
    doc.setFontSize(7.5);doc.setTextColor(100,100,100);doc.setFont("helvetica","italic");
    const note="Conformement a l'article 16 des statuts d'Enio, un delegue empeche peut donner pouvoir pour se faire representer par un autre delegue. Un delegue ne peut detenir plus de deux pouvoirs.";
    const noteLines=doc.splitTextToSize(note,usable);
    noteLines.forEach(l=>{doc.text(l,margin,y);ln(5);});

    // Pied de page
    doc.setFillColor(13,58,79);doc.rect(0,282,W,15,"F");
    doc.setTextColor(138,154,170);doc.setFontSize(7.5);doc.setFont("helvetica","normal");
    doc.text("Enio UGM — Document genere par Agora v1 — Confidentiel",margin,289);
    doc.text("Genere le "+nowFull(),W-margin,289,{align:"right"});

    doc.save("Pouvoir_"+sanitize(titulaire.nom+"_vers_"+mandant.nom)+".pdf");
  });
};

// -- CSS ----------------------------------------------------------------------
// Feuille globale à ORDRE EXPLICITE (lot L2, 27/09/2026) : les composants
// ci-dessous, puis les raffinements et les effets (src/feuilleGlobale.js), puis
// le socle mobile, TOUJOURS dernier. Assemblage : injectCSS(), plus bas.
const CSS_COMPOSANTS = `
    ${/* ===================== RESET ===================== */""}
    *,*::before,*::after{box-sizing:border-box;margin:0;padding:0}

    ${/* ===================== VARIABLES THÈME ===================== */""}
    ${/* Variables de thème : SUPPRIMÉES d'ici (Chantier 3, 6/07/2026) — la
       source de vérité unique est src/theme.css (les deux thèmes complets +
       accents losanges). Ne JAMAIS redéfinir --bg-*/""}--text-*/--border-*/
       --shadow-* ailleurs que dans theme.css. */

    ${/* Logo Enio : deux images, une par thème */""}
    .header-logo-dark{display:inline}
    .header-logo-light{display:none}
    body.theme-light .header-logo-dark{display:none}
    body.theme-light .header-logo-light{display:inline}

    ${/* ===================== BASE ===================== */""}
    html{-webkit-text-size-adjust:100%;text-size-adjust:100%}
    ${/* 100 % : l'iPhone ne regrossit pas le texte en paysage. Mais Safari
       iOS et iPadOS n'appliquent pas le zoom de la taille du texte aux polices
       déclarées : la sonde d'applyFontSize pose data-texte-ajuste
       (src/echelleTexte.js). iPhone, « pourcentage » : le pourcentage devient
       l'échelle (Très grand : 138 %), toujours sans grossissement en paysage.
       iPad, « jetons » : les jetons --fs-* sont multipliés (theme.css), et le
       texte sans taille déclarée part d'un corps multiplié lui aussi. Une zone
       éditable (contenteditable) de l'iPhone ignore le pourcentage : elle prend
       l'échelle par --fs-texte (theme.css), ou, pour une taille héritée, plus
       bas (POLICE GLOBALE). */""}
    html[data-texte-ajuste="pourcentage"] body{-webkit-text-size-adjust:calc(var(--fs-zoom) * 100%);text-size-adjust:calc(var(--fs-zoom) * 100%)}
    ${/* Le même pourcentage, redit sur chaque enfant de <body> (#root, et les
       feuilles et fenêtres portées hors de lui) : sans cette redite, WebKit
       compte l'échelle une fois aux longueurs en em d'un texte qui hérite sa
       taille du seul <body>, et deux fois partout ailleurs, si bien que
       --em-ajuste rapetissait l'icône d'un tel texte (mesuré au simulateur,
       01/10). Spécificité nulle : une règle de composant garde la main. */""}
    :where(html[data-texte-ajuste="pourcentage"] body) > *{-webkit-text-size-adjust:calc(var(--fs-zoom) * 100%);text-size-adjust:calc(var(--fs-zoom) * 100%)}
    html[data-texte-ajuste="jetons"] body{font-size:calc(13px * var(--fs-scale) * var(--fs-zoom))}
    ${/* iPad, « jetons » : le zoom atteint déjà le texte d'un <input> ou d'une
       <textarea> VIDE (ses jetons y valent leur taille de base, theme.css),
       mais pas leur texte indicatif, ramené à la taille déclarée : il la
       reprend multipliée. Le
       SVG est zoomé lui aussi : il repart de la taille de son parent divisée
       par l'échelle (un <text> sans taille propre n'est pas agrandi deux
       fois ; un SVG imbriqué ne divise pas deux fois), son em n'est compté
       qu'une fois, et un <foreignObject> rend au
       texte HTML qu'il contient la taille de son parent. Spécificité minimale :
       une taille posée par un composant l'emporte. */""}
    :where(html[data-texte-ajuste="jetons"]) :is(input,textarea:empty)::placeholder{font-size:calc(var(--fs-zoom) * 100%)}
    :where(html[data-texte-ajuste="jetons"]) svg:not(svg svg){font-size:calc(100% / var(--fs-zoom));--em-ajuste:1}
    :where(html[data-texte-ajuste="jetons"]) foreignObject{font-size:calc(100% * var(--fs-zoom))}
    body{
      font-family:'Montserrat',Arial,sans-serif;
      background:var(--bg-app);
      color:var(--text-main);
      margin:0;
      transition:background .3s,color .3s;
      overflow-x:hidden;
      max-width:var(--vw100);
      -webkit-font-smoothing:antialiased;
    }
    .ev{
      font-family:'Montserrat',Arial,sans-serif;
      background:transparent;
      ${/* 100dvh = hauteur REELLEMENT visible sur iPhone. Avec 100vh (barres
         Safari masquees) chaque page portait ~100px de defilement fantome :
         on « arrivait en bas » sur du vide. Le 100vh reste en repli pour les
         navigateurs sans dvh. */""}
      min-height:var(--vh100);
      color:var(--text-main);
      position:relative;
    }
    ${/* Filigrane gris body::before : RETIRÉ au lot L4 (synthèse §3, principe 1 :
       la mosaïque décorative ne vit plus qu'en débord d'une plaque navy, une
       par écran au plus). Le fond de page est un aplat de --bg-app. */""}

    ${/* ===================== SCROLLBAR ===================== */""}
    ::-webkit-scrollbar{width:5px;height:5px}
    ::-webkit-scrollbar-track{background:var(--scrollbar-track)}
    ::-webkit-scrollbar-thumb{background:var(--scrollbar-thumb);border-radius:4px}

    ${/* ===================== CARTES ===================== */""}
    ${/* Carte (lot L4) : rayon de la charte (--radius-lg, 16 px) qui ne rétrécit
       plus au doigt (§4.3), ombre teintée navy du premier niveau (--elev-1). */""}
    .card{
      background:var(--bg-card);
      border-radius:var(--radius-lg);
      padding:clamp(14px,var(--vw100) * 0.03,22px);
      border:1px solid var(--border-color);
      color:var(--text-main);
      box-shadow:var(--elev-1);
      transition:background .25s,border-color .25s,box-shadow .25s;
    }
    .card-sm{
      background:var(--bg-card);
      border-radius:12px;
      padding:clamp(10px,var(--vw100) * 0.02,14px);
      border:1px solid var(--border-color);
      color:var(--text-main);
      transition:background .25s;
    }
    .card-dark{
      background:var(--bg-card-dark);
      border-radius:12px;
      padding:clamp(10px,var(--vw100) * 0.02,14px);
      border:1px solid var(--border-color);
      color:var(--text-main);
      transition:background .25s;
    }

    ${/* ===================== INPUTS ===================== */""}
    .inp{
      width:100%;
      background:var(--bg-input);
      ${/* --champ-bord : 3:1 au moins sur les fonds (WCAG 1.4.11, revue du
         27/10/2026) ; --border-input, 1,5:1, faisait paraître le champ
         désactivé à côté des boutons en contour. */""}
      border:1.5px solid var(--champ-bord, var(--border-input));
      border-radius:var(--radius-sm);
      padding:10px 14px;
      color:var(--text-main);
      font-family:'Montserrat',Arial,sans-serif;
      font-size:var(--fs-meta);
      font-weight:500;
      outline:none;
      transition:border-color .15s,background .25s,color .25s;
      -webkit-appearance:none;
    }
    ${/* Focus des champs : teal de la charte et anneau (--ring, jetons DS), au
       lieu d'une bordure orange sans anneau. outline:none explicite : la
       bordure et l'anneau suffisent, quel que soit l'ordre des feuilles. */""}
    .inp:focus{border-color:var(--focus);box-shadow:var(--ring);outline:none}
    .inp::placeholder{color:var(--text-sub)}
    select.inp{cursor:pointer;max-width:100%}
    ${/* .inp pose -webkit-appearance:none, qui SUPPRIME le chevron natif : sur
       iOS un menu deroulant etait alors visuellement indiscernable d'un champ
       texte, et on tapait dessus en s'attendant a un clavier. On redessine le
       chevron (SVG en data:, teinte du texte secondaire) et on reserve la
       place a droite pour qu'il ne recouvre pas la valeur choisie.
       AU DOIGT UNIQUEMENT : au clavier-souris le menu se deroule au clic et
       la densite d'origine (pas de chevron dessine, pas de reserve de 34px)
       reste en vigueur — le rendu du bureau est inchange. */""}
    @media (pointer:coarse){
      select.inp{
        padding-right:34px;
        background-image:url("data:image/svg+xml;charset=utf-8,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 12 8'%3E%3Cpath fill='none' stroke='%238a9aaa' stroke-width='1.8' stroke-linecap='round' stroke-linejoin='round' d='M1 1.5 6 6.5 11 1.5'/%3E%3C/svg%3E");
        background-repeat:no-repeat;
        background-position:right 12px center;
        background-size:11px 8px;
      }
    }
    select.inp option{background:var(--bg-card);color:var(--text-main)}
    textarea.inp{resize:vertical;min-height:70px;line-height:1.5}

    ${/* ===================== LABELS ===================== */""}
    ${/* Étiquette de champ (lot L4) : casse phrase, 13 px / 600. Fini les
       capitales espacées à .78em (≈ 10 px) : la casse du texte source fait
       foi (« Nom du projet », « IBAN »). */""}
    .lbl{
      font-size:var(--fs-lbl);
      font-weight:600;
      line-height:1.35;
      color:var(--text-sub);
      margin-bottom:5px;
      display:block;
      transition:color .25s;
    }

    ${/* ===================== BOUTONS ===================== */""}
    ${/* Bouton (lot L4, synthèse §4.2) : casse phrase, 15 px / 600 (16 px au
       doigt, --fs-bouton), rayon --radius-md. Plus de capitales espacées. */""}
    .btn{
      display:inline-flex;
      align-items:center;
      justify-content:center;
      gap:6px;
      font-family:'Montserrat',Arial,sans-serif;
      font-weight:600;
      font-size:var(--fs-bouton);
      letter-spacing:calc(-.005em * var(--em-ajuste, 1));
      line-height:1.2;
      border:none;
      cursor:pointer;
      border-radius:var(--radius-md);
      padding:10px 18px;
      white-space:nowrap;
      -webkit-tap-highlight-color:transparent;
    }
    ${/* Transition, survol, appui et focus de .btn : section « raffinements » de
       src/feuilleGlobale.js SEULEMENT, composée APRÈS cette section dans
       #enio-css5. Les redéclarer ici ferait une seconde source ; avant le lot
       L0, c'est ainsi que cette feuille écrasait ceux de theme.css (survol
       collant au doigt, appui sans enfoncement, mouvement réduit ignoré —
       audit u3, H1 ; verrou : src/cascadeBoutons.test.js). */""}
    .btn:disabled{opacity:.35;cursor:not-allowed;transform:none!important}
    .btn-orange{background:#F49800;color:#fff}
    .btn-teal{background:#1D948A;color:#fff}
    .btn-violet{background:#951B81;color:#fff}
    .btn-red{background:#E20816;color:#fff}
    .btn-green{background:#94C11F;color:#fff}
    .btn-navy{background:#0A4A63;color:#fff}
    .btn-gold{background:#F49800;color:#1a2a35}
    .btn-ghost{
      background:var(--btn-ghost-bg);
      color:var(--btn-ghost-color);
      border:1.5px solid var(--btn-ghost-border);
    }
    ${/* Survol réservé à la souris : au doigt, iOS le laisse COLLÉ après le tap.
       Texte à l'encre orange du THÈME (--tx-orange : l'orange de la charte la
       nuit, inchangé ; --acc-orange-ink le jour, 5:1) : l'orange brut faisait
       2,1:1 sur carte claire. */""}
    @media (hover:hover) and (pointer:fine){
      .btn-ghost:hover:not(:disabled){border-color:#F49800;color:var(--tx-orange);background:rgba(244, 152, 0,.08)}
    }
    .btn-sm{padding:6px 13px;font-size:var(--fs-bouton-sm);border-radius:var(--radius-sm)}
    .btn-lg{padding:14px 24px;font-size:var(--fs-bouton-lg)}
    .btn-full{width:100%;display:flex}

    ${/* ===================== ONGLETS (.ptab) — lot L9 =====================
       UNE rangée, toujours : la piste DÉFILE, elle ne se replie jamais (règle
       « jamais d'onglets empilés » ; un second niveau est un sélecteur).
       Contrôle segmenté de la synthèse : piste en lavis à la largeur de son
       contenu (bornée à la colonne), onglet actif en pastille claire ;
       <Onglets> (src/Onglets.js) y ajoute rôles ARIA, flèches et la pastille
       qui glisse. Survol : la couleur seule, à la souris seulement (fin de
       l'orange au survol). */""}
    .ptab{display:flex;flex-wrap:nowrap;gap:2px;width:fit-content;max-width:100%;box-sizing:border-box;overflow-x:auto;scrollbar-width:none;-webkit-overflow-scrolling:touch;background:var(--ptab-bg);padding:3px;border-radius:var(--radius-md)}
    .ptab::-webkit-scrollbar{display:none}
    ${/* Au pointeur fin, une rangée qui DÉBORDE montre une barre fine : sans
       elle, un onglet coupé à droite (console de séance, super-admin,
       préparation des séances à 1 280 px) ne se devine pas à la souris
       (critique du 29/09). Au doigt, la piste défile au geste, sans barre.
       Chrome et Safari : `scrollbar-width` / `scrollbar-color` autres que
       `auto` y rendent la barre NATIVE, en surimpression sur macOS avec un
       pavé tactile, donc invisible au repos (mesuré au banc : 0 px). Là où
       ::-webkit-scrollbar existe, on repasse en `auto` pour que la barre
       dessinée ci-dessous (6 px, toujours visible) s'applique ; Firefox garde
       `thin`. */""}
    @media (hover:hover) and (pointer:fine){.ptab,.ptab.ptab-admin{scrollbar-width:thin;scrollbar-color:color-mix(in srgb, var(--text-sub) 45%, transparent) transparent}@supports selector(::-webkit-scrollbar){.ptab,.ptab.ptab-admin{scrollbar-width:auto;scrollbar-color:auto}}.ptab::-webkit-scrollbar,.ptab.ptab-admin::-webkit-scrollbar{display:block;height:6px}.ptab::-webkit-scrollbar-track{background:transparent}.ptab::-webkit-scrollbar-thumb{background:color-mix(in srgb, var(--text-sub) 45%, transparent);border-radius:var(--radius-pill)}}
    .ptab-btn{
      flex:none;
      ${/* min-width:max-content fait du bouton un ATOME : sa largeur minimale est
         son libellé entier. Relâché au doigt plus bas (SOCLE MOBILE), où la
         barre .ptab défile de toute façon. */""}
      min-width:max-content;
      padding:7px 12px;
      border-radius:9px;
      font-size:var(--fs-segment);
      font-weight:600;
      line-height:1.3;
      cursor:pointer;
      border:none;
      background:transparent;
      color:var(--ptab-btn-color);
      transition:background-color var(--dur-fast) var(--ease-out),color var(--dur-fast) var(--ease-out),box-shadow var(--dur-fast) var(--ease-out);
      font-family:inherit;
      white-space:nowrap;
      -webkit-tap-highlight-color:transparent;
    }
    .ptab-btn.act{background:var(--ptab-btn-act-bg);color:var(--text-main);box-shadow:var(--ptab-act-ombre)}
    @media (hover:hover) and (pointer:fine){.ptab-btn:not(.act):hover{color:var(--text-main)}}
    .ptab-admin{overflow-x:auto;-webkit-overflow-scrolling:touch;scrollbar-width:none}
    .ptab-admin::-webkit-scrollbar{display:none}

    ${/* ===================== NAV DROPDOWN CARDS ===================== */""}
    ${/* L'inline style fournit "left" directement (bord gauche reel),
       donc plus de translateX(-50%) - animation = simple glissement vers le bas. */""}
    @keyframes dropdownSlide{
      from{opacity:0;transform:translateY(-6px)}
      to{opacity:1;transform:translateY(0)}
    }

    .nav-dropdown-panel{
      scrollbar-width:none;
    }
    .nav-dropdown-panel::-webkit-scrollbar{display:none}
    @keyframes mobileMenuSlide{
      from{opacity:0;transform:translateY(-8px)}
      to{opacity:1;transform:translateY(0)}
    }
    @keyframes mobileMenuOverlay{
      from{opacity:0}
      to{opacity:1}
    }
    ${/* ===================== CALENDRIER RÉUNIONS (F3) ===================== */""}
    ${/* Sur tablette portrait (<768px), compactage modéré */""}
    @media(max-width:767px){
      .reunions-calendar-cell{min-height:70px !important;padding:4px 4px !important}
      .reunions-calendar-daynum{font-size:calc(10px * var(--fs-texte, 1)) !important}
      .reunions-calendar-event{font-size:calc(9px * var(--fs-texte, 1)) !important;padding:1px 4px !important}
    }
    ${/* Sur mobile (<600px) :
       - les événements deviennent des "barres pastilles" colorées
         (titre + heure masqués) → tap → ouvre la fiche complète
       - la barre garde une HAUTEUR TAPPABLE. Elle a longtemps ete reduite a
         5px : le bouton qui ouvre la reunion mesurait alors ~45x5pt, avec 3px
         entre deux reunions du meme jour — on ouvrait la voisine, ou rien.
         14px de barre + 4px de marge = un pas de 18px, et surtout une cible
         que le pouce atteint sans viser. */""}
    @media(max-width:600px){
      .reunions-calendar-cell{min-height:64px !important;padding:3px 3px !important}
      .reunions-calendar-day-label{padding:5px 4px !important;font-size:calc(9px * var(--fs-texte, 1)) !important;letter-spacing:0 !important}
      .reunions-calendar-daynum{font-size:calc(10px * var(--fs-texte, 1)) !important}
      .reunions-calendar-aujbadge{display:none !important}
      .reunions-calendar-event{
        font-size:0 !important;
        padding:0 !important;
        min-height:14px !important;
        height:14px !important;
        margin:2px 0 !important;
        border-radius:4px !important;
        line-height:1 !important;
        overflow:hidden !important;
      }
      .reunions-calendar-event-time,
      .reunions-calendar-event-title{display:none !important}
      .reunions-calendar-more{font-size:calc(9px * var(--fs-texte, 1)) !important;text-align:center;padding-left:0 !important;min-height:20px}
    }
    ${/* Sur très petit écran (<375px), encore plus compact — mais la cible
       tactile de l'evenement, elle, ne bouge plus. */""}
    @media(max-width:375px){
      .reunions-calendar-cell{min-height:58px !important}
      .reunions-calendar-day-label{font-size:calc(8px * var(--fs-texte, 1)) !important;padding:4px 2px !important}
    }
    ${/* ===================== RESPONSIVE FIXES — grilles 2-3 col figées ===================== */""}
    ${/* Sur mobile, on force tous les grids ayant la classe .grid-responsive
       à passer en 1 colonne. Utilisable comme override ciblé. */""}
    @media(max-width:600px){
      .grid-responsive-3,
      .grid-responsive-2-3{grid-template-columns:minmax(0,1fr) !important}
    }
    @media(min-width:601px) and (max-width:900px){
      .grid-responsive-3{grid-template-columns:repeat(2,1fr) !important}
    }
    ${/* Tableau présences : sur mobile (<600px), cache la colonne "Répondu le"
       (5e) pour économiser la place horizontale. Le reste reste scrollable. */""}
    @media(max-width:600px){
      .tableau-presences-hide-mobile{display:none !important}
    }
    ${/* ===================== HOTFIX RESPONSIVE 2026-05-22 =====================
       Classe utilitaire à appliquer aux grilles 2-3 col figées :
       <div className="grid-collapse-mobile" style={{display:"grid",
            gridTemplateColumns:"1fr 1fr"}}>
       → sur mobile (<600px) passe à 1 colonne.
       → sur tablette (601-900px) passe à 2 colonnes si la grille est 3-col. */""}
    @media(max-width:600px){
      .grid-collapse-mobile{grid-template-columns:minmax(0,1fr) !important}
    }
    @media(min-width:601px) and (max-width:900px){
      .grid-collapse-mobile-3to2{grid-template-columns:repeat(2,1fr) !important}
    }
    ${/* Conteneur global : empêche les overflows horizontaux inattendus sur
       mobile (e.g. tableau qui déborde sans wrapper). Les scrolls
       intentionnels restent OK via overflow-x:auto local. */""}
    @media(max-width:600px){
      body{overflow-x:hidden}
    }
    ${/* Sécurité : un média ne doit jamais sortir de son conteneur sur mobile.
       833px et non 600 : l'iPad portrait n'avait aucune protection. svg et
       canvas ajoutés — ils en étaient absents alors que le dépôt en compte 27.
       Pas de height:auto sur eux, à la différence des images : un SVG porte
       souvent une hauteur d'attribut qu'on écraserait. */""}
    @media(max-width:833px){
      img,iframe,video{max-width:100% !important;height:auto}
      svg,canvas{max-width:100% !important}
    }
    ${/* Sur mobile (<600px), les contenus en padding 20px+ deviennent
       cramps. On réduit le padding latéral des containers principaux
       (.card et la maxWidth wrapper) pour récupérer ~12px de chaque
       côté. */""}
    @media(max-width:600px){
      .card{padding:14px 12px}
    }

    ${/* ===================== VROW ===================== */""}
    .vrow{
      display:flex;
      align-items:center;
      padding:8px 12px;
      border-radius:8px;
      font-size:var(--fs-micro);
      margin-bottom:3px;
      background:var(--vrow-bg);
      color:var(--text-main);
      gap:8px;
      flex-wrap:wrap;
      transition:background .25s;
    }

    ${/* ===================== SÉPARATEUR ===================== */""}
    .sep{border:none;border-top:1px solid var(--sep-color);margin:16px 0;transition:border-color .25s}

    ${/* ===================== GRILLES ===================== */""}
    ${/* minmax(0,…) et non 1fr : « 1fr » vaut minmax(AUTO,1fr), donc une piste
       refuse de descendre sous la largeur minimale de son contenu et ÉLARGIT
       la page. Mesuré : une .grid2 de deux boutons rendait « 259,7px 229,7px »
       au lieu de 184,5 × 2 sur un écran de 430. Sans effet au clavier-souris,
       où le contenu tient déjà dans la piste (vérifié à 1024 et 1440 px). */""}
    .grid2{display:grid;grid-template-columns:repeat(2,minmax(0,1fr));gap:11px}
    .grid3{display:grid;grid-template-columns:repeat(3,minmax(0,1fr));gap:11px}
    .grid4{display:grid;grid-template-columns:repeat(4,minmax(0,1fr));gap:11px}
    .grid2>*,.grid3>*,.grid4>*{min-width:0}

    ${/* ===================== PROGRESS ===================== */""}
    .ptrack{background:var(--ptrack-bg);border-radius:99px;height:8px;overflow:hidden;transition:background .25s}

    ${/* ===================== BADGES ===================== */""}
    .badge-secret{background:#951B81;color:#fff;font-size:calc(10px * var(--fs-texte, 1));font-weight:700;padding:4px 12px;border-radius:20px;display:inline-flex;align-items:center;gap:5px}
    .badge-public{background:#1D948A;color:#fff;font-size:calc(10px * var(--fs-texte, 1));font-weight:700;padding:4px 12px;border-radius:20px;display:inline-flex;align-items:center;gap:5px}
    .badge-hidden{background:rgba(244, 152, 0,.13);color:#F49800;border:1px solid rgba(244, 152, 0,.35);font-size:calc(10px * var(--fs-texte, 1));font-weight:700;padding:4px 12px;border-radius:20px;display:inline-flex;align-items:center;gap:5px}
    .badge-published{background:rgba(148, 193, 31,.13);color:#5a9628;border:1px solid rgba(148, 193, 31,.35);font-size:calc(10px * var(--fs-texte, 1));font-weight:700;padding:4px 12px;border-radius:20px;display:inline-flex;align-items:center;gap:5px}
    .badge-CA{background:#1D948A;color:#fff;font-size:calc(10px * var(--fs-texte, 1));font-weight:700;padding:4px 12px;border-radius:20px}
    .badge-AG{background:#951B81;color:#fff;font-size:calc(10px * var(--fs-texte, 1));font-weight:700;padding:4px 12px;border-radius:20px}
    .badge-VL{background:#F49800;color:#fff;font-size:calc(10px * var(--fs-texte, 1));font-weight:700;padding:4px 12px;border-radius:20px}
    .badge-SE{background:#1D948A;color:#fff;font-size:calc(10px * var(--fs-texte, 1));font-weight:700;padding:4px 12px;border-radius:20px}
    .tag-manual{background:rgba(149, 27, 129,.18);border:1px solid #951B81;color:#951B81;font-size:calc(9px * var(--fs-texte, 1));font-weight:700;padding:2px 7px;border-radius:12px;white-space:nowrap}
    .tag-pouvoir{background:rgba(244, 152, 0,.15);border:1px solid #F49800;color:#c07800;font-size:calc(9px * var(--fs-texte, 1));font-weight:700;padding:2px 7px;border-radius:12px;white-space:nowrap}
    body.theme-light .tag-pouvoir{color:#c07800}

    ${/* ===================== CHIPS ===================== */""}
    .chip{font-size:calc(10px * var(--fs-texte, 1));font-weight:700;padding:2px 9px;border-radius:20px;white-space:nowrap}
    .chip-oui{background:rgba(148, 193, 31,.2);color:#5a9628}
    .chip-non{background:rgba(226, 8, 22,.2);color:#E20816}
    .chip-abs{background:rgba(138,154,170,.2);color:#5a7080}
    .chip-nom{background:rgba(244, 152, 0,.2);color:#F49800}

    ${/* ===================== VOTE OPTIONS ===================== */""}
    .vote-opt{
      display:flex;align-items:center;gap:12px;
      padding:14px 17px;border-radius:10px;
      border:2px solid var(--vote-opt-border);
      cursor:pointer;margin-bottom:8px;
      transition:background .15s, border-color .15s, color .15s, box-shadow .15s, opacity .15s, transform .15s;font-weight:600;font-size:var(--fs-nav);
      color:var(--text-main);
      background:var(--bg-card);
      -webkit-tap-highlight-color:transparent;
    }
    ${/* Le survol peint la MEME bordure orange que .vote-opt.sel. Sur iPhone
       le :hover reste colle a la derniere option touchee : apres un
       changement d'avis, DEUX options apparaissaient cochees. On le
       reserve donc a la souris et on donne au doigt un retour d'appui. */""}
    @media (hover:hover) and (pointer:fine){
      .vote-opt:hover{border-color:#F49800;background:rgba(244, 152, 0,.07)}
    }
    ${/* Retour d'appui reserve au doigt (le bureau n'avait pas d'etat :active). */""}
    @media (pointer:coarse){
      .vote-opt:active{background:rgba(244, 152, 0,.13)}
    }
    .vote-opt.sel{border-color:#F49800;background:rgba(244, 152, 0,.13)}
    .radio{width:18px;height:18px;border-radius:50%;border:2.5px solid var(--text-sub);display:grid;place-items:center;flex-shrink:0;transition:background .15s, border-color .15s, color .15s, box-shadow .15s, opacity .15s, transform .15s}
    .vote-opt.sel .radio{border-color:#F49800;background:#F49800}
    .vote-opt.sel .radio::after{content:'';width:6px;height:6px;border-radius:50%;background:#fff}

    ${/* ===================== AGENDA ===================== */""}
    .agenda-item{
      padding:13px 15px;border-radius:10px;
      border:1.5px solid var(--border-input);
      margin-bottom:7px;
      color:var(--text-main);
      background:var(--bg-card);
      transition:background .18s, border-color .18s, color .18s, box-shadow .18s, opacity .18s, transform .18s;
    }
    .agenda-item.open{border-color:#1D948A;background:rgba(29, 148, 138,.05)}
    .agenda-item.closed{border-color:rgba(148, 193, 31,.35);opacity:.8}
    .scrutin-card{
      border-radius:12px;border:2px solid var(--border-input);
      padding:14px 16px;cursor:pointer;transition:background .18s, border-color .18s, color .18s, box-shadow .18s, opacity .18s, transform .18s;margin-bottom:8px;
      color:var(--text-main);background:var(--bg-card);
    }
    @media (hover:hover) and (pointer:fine){
      .scrutin-card:hover{border-color:#1D948A}
    }
    .scrutin-card.active{border-color:#F49800;background:rgba(244, 152, 0,.05)}
    .scrutin-card.voted{border-color:#94C11F;background:rgba(148, 193, 31,.04)}

    ${/* ===================== DIVERS ===================== */""}
    .results-hidden{
      background:var(--bg-card-dark);border-radius:12px;padding:28px;
      text-align:center;border:1.5px dashed rgba(244, 152, 0,.35);
      color:var(--text-main);
    }
    .err{color:#E20816;font-size:calc(11px * var(--fs-texte, 1));margin-top:6px;font-weight:600}
    .quorum-ok{color:#5a9628;font-weight:700}
    .quorum-ko{color:#E20816;font-weight:700}
    body.theme-light .quorum-ok{color:#3a7a10}
    .pouvoir-block{border:2px solid #F49800;border-radius:12px;padding:16px;margin-bottom:12px;background:rgba(244, 152, 0,.04)}
    .pouvoir-block.done{border-color:#94C11F;background:rgba(148, 193, 31,.04)}
    .stat-box{background:var(--bg-card-dark);border-radius:10px;padding:11px 14px;transition:background .25s;color:var(--text-main)}
    .scrollbox{overflow-y:auto;max-height:260px}
    .vl-block{background:var(--bg-card-dark);border:1.5px solid #F49800;border-radius:12px;padding:16px;margin-bottom:14px}
    .checkbox-row{display:flex;align-items:center;gap:10px;cursor:pointer;font-size:var(--fs-micro);font-weight:600;padding:8px 0;color:var(--text-main)}
    .hist-sel{background:rgba(226, 8, 22,.08);border:1.5px solid rgba(226, 8, 22,.3);border-radius:10px}
    .qr-box{background:#fff;padding:16px;border-radius:12px;display:inline-block}
    .font-size-btn{background:transparent;border:1.5px solid var(--border-input);border-radius:6px;color:var(--text-sub);cursor:pointer;font-family:Montserrat,sans-serif;font-weight:700;padding:3px 8px;font-size:var(--fs-micro);transition:background .15s, border-color .15s, color .15s, box-shadow .15s, opacity .15s, transform .15s}
    .font-size-btn.active{background:#0A4A63;color:#fff}
    .font-size-btn:hover{border-color:#F49800;color:#F49800}

    ${/* ===================== ÉCRAN DE SALLE ===================== */""}
    .screen-mode{
      font-family:'Montserrat',Arial,sans-serif;
      background:var(--bg-app);
      min-height:var(--vh100);
      color:var(--text-main);
      display:flex;flex-direction:column;
      align-items:center;justify-content:center;
      padding:clamp(16px,var(--vw100) * 0.04,40px);
      text-align:center;
      transition:background .3s,color .3s;
    }
    .screen-card{background:var(--bg-card-dark);border-radius:16px;padding:20px}
    body.theme-light .screen-card{background:rgba(0,0,0,.04)}

    ${/* ===================== STAT BOX ===================== */""}
    .statbox-label{color:var(--text-sub);font-size:var(--fs-micro);font-weight:600}

    ${/* ===================== ANIMATIONS ===================== */""}
    @keyframes countUp{from{opacity:0;transform:translateY(8px)}to{opacity:1;transform:translateY(0)}}
    @keyframes barFill{from{width:0}to{width:var(--target-w)}}
    @keyframes fadeUp{from{opacity:0;transform:translateY(20px)}to{opacity:1;transform:translateY(0)}}
    @keyframes pulse{0%,100%{opacity:1}50%{opacity:.4}}.pulse{animation:pulse 1.6s infinite}
    ${/* .fi sans « forwards » (03/11/2026, le PV signé) : l'état final de
       l'entrée est celui du repos (opacité 1, aucun transform), et une
       animation de transform RETENUE après sa fin faisait de .fi.adm-cadre
       le bloc contenant de tout calque fixe de l'administration (la fenêtre
       de dépôt du PV, rognée dans sa carte). Aucun .fi ne porte de transform
       ni d'opacité en ligne : rien ne change à l'œil. */""}
    @keyframes fadeIn{from{opacity:0;transform:translateY(8px)}to{opacity:1;transform:none}}.fi{animation:fadeIn .26s ease}
    @keyframes pop{0%{transform:scale(.88)}60%{transform:scale(1.06)}100%{transform:scale(1)}}.pop{animation:pop .3s ease forwards}
    .anim-countup{animation:countUp .4s ease both}
    .anim-fadeup{animation:fadeUp .5s ease both}
    .anim-pulse-ring{animation:pulse-ring 2s ease-in-out infinite}
    @keyframes pulse-ring{0%{transform:scale(1);opacity:.8}50%{transform:scale(1.06);opacity:1}100%{transform:scale(1);opacity:.8}}
    ${/* Mouvement réduit : les boucles et les entrées de cette section
       s'arrêtent ; le contenu reste visible, à sa place. */""}
    @media (prefers-reduced-motion: reduce){
      .pulse,.anim-pulse-ring,.anim-countup,.anim-fadeup,.fi,.pop{animation:none}
    }

    ${/* ===================== MINUTERIE ===================== */""}
    .timer-display{font-size:calc(clamp(36px,6vw,72px) * var(--fs-texte, 1));font-weight:700;font-variant-numeric:tabular-nums;letter-spacing:calc(-.02em * var(--em-ajuste, 1));line-height:1;color:#ffffff}
    .timer-warn{color:#F49800}.timer-danger{color:#E20816}

    ${/* ===================== VARIABLES SUPPLEMENTAIRES ===================== */""}
    ${/* Alias --card-inner/--text-gray/--navy : DÉPLACÉS dans src/theme.css
       (source de vérité unique du Chantier 3). */""}

    ${/* ===================== RESPONSIVE ===================== */""}
    ${/* Tablette */""}
    @media(max-width:900px){
      .grid4{grid-template-columns:repeat(2,minmax(0,1fr))}
    }
    ${/* Mobile large */""}
    @media(max-width:768px){
      .grid4{grid-template-columns:repeat(2,minmax(0,1fr))}
      .card{padding:16px}
      .btn-lg{padding:13px 20px}
      ${/* Organigramme : domaines en 2-3 colonnes au lieu de 5 */""}
      .org-domaines-grid{grid-template-columns:repeat(auto-fill,minmax(min(200px,100%),1fr)) !important}
    }
    ${/* Mobile portrait */""}
    @media(max-width:600px){
      .grid2{grid-template-columns:repeat(2,minmax(0,1fr))}
      .grid3{grid-template-columns:repeat(2,minmax(0,1fr))}
      .btn-lg{padding:12px 16px}
      .screen-mode{padding:14px;justify-content:flex-start;padding-top:20px}
      ${/* padding LATERAL reduit a 12px : c'est l'intention du hotfix du
         2026-05-22 (l.717-723), restee lettre morte pendant un an parce que
         cette declaration-ci, plus basse dans la feuille et de meme
         specificite, remettait 14px partout. */""}
      .card{padding:14px 12px}
      ${/* Le corps des onglets NE se reduit plus en em (il tombait a 7,8px de
         majuscules illisibles) ; sur ecran tactile le SOCLE MOBILE porte en
         plus .ptab-btn a 44pt. */""}
      .ptab-btn{padding:7px 10px}
      ${/* Hublots de defilement imbriques : sur iPhone le doigt s'y perd et on
         croit etre arrive au bas de la page. On rend la liste au flux. */""}
      .scrollbox{max-height:none;overflow-y:visible}
      ${/* Listes de classement du tableau de bord Activite : meme piege de
         defilement imbrique (fenetres de 240px au milieu d'une page qui
         defile deja). */""}
      .classement-scroll{max-height:none !important;overflow:visible !important}
      ${/* Ligne de projet (Projets, offres & solutions). Tous ses enfants sauf
         le titre etaient incompressibles : leur somme depassait les ~340px
         utiles d'un iPhone, le titre etait rabote a quelques caracteres et la
         fin de ligne sortait de l'ecran. La rangee s'enroule desormais, et le
         bouton « En savoir plus » passe seul sur une 2e ligne — une cible
         pleine largeur a 44pt au lieu d'un timbre-poste de 20px. */""}
      .projet-row{flex-wrap:wrap;row-gap:6px}
      .projet-row-cta{flex-basis:100%;min-height:44px;font-size:var(--fs-micro) !important;padding:0 12px !important}
      ${/* Organigramme hiérarchie : scroll horizontal pour rester lisible */""}
      .org-hierarchy{overflow-x:auto;-webkit-overflow-scrolling:touch;padding-bottom:8px}
      .org-hierarchy>div{min-width:500px}
    }
    @media(max-width:480px){
      .header-brand{display:none}
      .header-logo-dark,.header-logo-light{height:26px !important}
      ${/* Header compact sur mobile : le padding de zone sure est pose une seule
         fois, dans le SOCLE MOBILE en fin de feuille (seul endroit d'ou il
         peut battre le padding en ligne de Header.js). */""}
      .app-header{ min-height:calc(48px + var(--sat)) !important; }
      ${/* Organigramme : 1 colonne domaines */""}
      .org-domaines-grid{grid-template-columns:minmax(0,1fr) !important}
      ${/* Menu mobile : le haut suit la hauteur REELLE du header, mesuree
         en continu par le ResizeObserver de Header.js et publiee dans
         --app-header-h. Le 48px force ici decalait le tiroir de 8px des
         qu'un membre etait connecte (header a 56px), laissant une bande
         de page visible entre le header et le tiroir. La hauteur max et
         la zone sure basse sont posees par le SOCLE MOBILE. */""}
      .mobile-menu-panel{top:var(--app-header-h,48px) !important}
    }
    ${/* Collapse nav en "Menu" quand l'espace horizontal est insuffisant
       (mobile + tablette + petit laptop). Au-dessus de 1024px on affiche
       tous les boutons de module. */""}
    @media(max-width:1024px){
      .header-nav-full{display:none !important}
      .header-nav-mobile{display:flex !important}
    }
    @media(min-width:1025px){
      .header-nav-mobile{display:none !important}
      .header-nav-full{display:flex}
    }
    ${/* Petits ecrans : les deux paliers etaient ECRITS A L'ENVERS (375 avant
       420). Sur un iPhone SE les deux media queries s'appliquent, et a
       specificite egale la DERNIERE ecrite gagne : tout le palier iPhone SE
       etait mort depuis sa creation (grid4 restait a 2 colonnes, la carte
       gardait 12px). On va desormais du plus large au plus etroit. */""}
    ${/* Petit mobile — 448 px et non 420 : les iPhone Plus et Pro Max mesurent
       428 (12/13/14 Pro Max, 14 Plus), 430 (15/16 Plus et Pro Max) et 440
       (16 Pro Max). À 420 ils passaient TOUS à côté de ce palier et recevaient
       la mise en page TABLETTE, à deux colonnes — « les éléments s'affichent
       comme si on était sur un ordinateur » (retour Pierre, 15/09/2026), avec
       525 px de contenu pour 428 px d'écran, mesuré au banc. 448 laisse une
       marge pour le prochain modèle tout en restant sous l'iPad mini. */""}
    @media(max-width:448px){
      .grid2{grid-template-columns:minmax(0,1fr)}
      .grid3{grid-template-columns:minmax(0,1fr)}
      .grid4{grid-template-columns:repeat(2,minmax(0,1fr))}
      .card{padding:12px 10px}
    }
    ${/* Petit mobile (iPhone SE / 13 mini) */""}
    @media(max-width:375px){
      .grid2{grid-template-columns:minmax(0,1fr)}
      .grid3{grid-template-columns:minmax(0,1fr)}
      .grid4{grid-template-columns:minmax(0,1fr)}
      .card{padding:10px}
      .org-domaines-grid{grid-template-columns:minmax(0,1fr) !important}
    }
    ${/* Paysage mobile */""}
    @media(max-height:500px) and (orientation:landscape){
      .screen-mode{justify-content:flex-start;padding-top:12px}
      .card{padding:10px}
    }

    ${/* ===================== GRAND ÉCRAN =====================
       Plus de paliers de --content-max ici (décision de Pierre du 27/10/2026) :
       la page occupe toute la colonne de la coquille à tous les écrans, et le
       jeton ne se déclare qu'à :root de theme.css (src/largeurPages.test.js). */""}

    ${/* ===================== BOUTONS MODE PRÉSENCE ===================== */""}
    .mode-presence-btns{
      display:grid;
      ${/* Deux boutons de vote côte à côte : bornés, sinon un libellé long fait
         pousser la rangée hors de l écran en séance. */""}
      grid-template-columns:repeat(2,minmax(0,1fr));
      gap:10px;
      margin-top:6px;
    }
    .mode-presence-btn{
      display:flex;flex-direction:column;align-items:center;justify-content:center;
      gap:5px;padding:14px 8px;border-radius:10px;
      border:2px solid var(--border-input);
      cursor:pointer;transition:background .15s, border-color .15s, color .15s, box-shadow .15s, opacity .15s, transform .15s;
      font-weight:700;font-size:calc(11px * var(--fs-texte, 1));text-transform:uppercase;letter-spacing:calc(.04em * var(--em-ajuste, 1));
      color:var(--text-main);background:var(--bg-card);
      font-family:'Montserrat',Arial,sans-serif;
      min-height:68px;height:auto;
      -webkit-tap-highlight-color:transparent;
    }
    ${/* Meme piege que .vote-opt : le survol posait le teal de l'etat choisi.
       Apres avoir touche les deux modes, les deux paraissaient selectionnes. */""}
    @media (hover:hover) and (pointer:fine){
      .mode-presence-btn:hover{border-color:#1D948A}
    }
    .mode-presence-btn.selected{border-color:#1D948A;background:rgba(29, 148, 138,.12);color:#1D948A}
    ${/* AU DOIGT UNIQUEMENT : l'etat choisi ne repose plus sur la seule couleur
       de bordure — trait plus epais et halo, lisibles au soleil comme en mode
       clair. Au clavier-souris on garde le trait de 2px d'origine. */""}
    @media (pointer:coarse){
      .mode-presence-btn.selected{border-width:3px;box-shadow:0 0 0 1px rgba(29,148,138,.35)}
    }
    .mode-presence-btn .mp-icon{font-size:calc(20px * var(--fs-texte, 1));line-height:1;flex-shrink:0}
    .mode-presence-btn .mp-label{font-size:calc(11px * var(--fs-texte, 1));text-align:center;line-height:1.2;word-break:break-word}

    ${/* ===================== BADGE INSTANCE LISIBLE EN MODE CLAIR ===================== */""}
    body.theme-light .badge-AG{background:#951B81;color:#fff}
    body.theme-light .badge-CA{background:#1D948A;color:#fff}
    body.theme-light .badge-VL{background:#F49800;color:#fff}
    body.theme-light .badge-SE{background:#1D948A;color:#fff}

    ${/* ===================== BLOCS ADAPTATIFS (remplace navy hardcodé) ===================== */""}
    .card-inner{background:var(--card-inner);border-radius:10px;padding:12px 14px;color:var(--text-main);transition:background .25s}
    .admin-header-row{background:var(--bg-app)}
    .font-size-btn.active{background:var(--bg-card-dark);color:var(--text-main)}

    ${/* Force le texte blanc sur les badges colorés */""}
    .badge-secret,.badge-public,.badge-hidden,.badge-published,
    .badge-CA,.badge-AG,.badge-VL,.badge-SE{color:#fff!important}
    .badge-hidden{color:#F49800!important}
    .badge-published{color:#5a9628!important}
    body.theme-light .badge-hidden{color:#F49800!important}
    body.theme-light .badge-published{color:#3a7a10!important}

    ${/* ===================== ADMIN HEADER STICKY ===================== */""}
    .admin-sticky{
      position:sticky;top:0;z-index:10;
      background:var(--bg-app);
      padding-bottom:var(--filet-ecart);
      border-bottom:1px solid var(--border-color);
      margin-bottom:14px;
    }

    ${/* ===================== POLICE GLOBALE ===================== */""}
    :root { --fs-scale: 1; }
    html { font-size: calc(13px * var(--fs-scale)); }
    body { font-size: 100%; }
    div, span, p, label, input, select, textarea, button, a, li, td, th {
      font-size: inherit;
    }
    ${/* iPhone sous la taille du texte (data-texte-ajuste="pourcentage") : une
       zone éditable ignore le pourcentage (x 1,00 mesuré) ; sans taille
       propre, elle reprend celle de son parent multipliée par l'échelle. Même
       spécificité que la règle d'héritage ci-dessus, qu'elle suit : une classe
       ou un style en ligne garde la main (jeton, multiplié par theme.css). */""}
    :where(html[data-texte-ajuste="pourcentage"]) :is(div,span,p,label,a,li,td,th):where([contenteditable]:not([contenteditable="false"])){font-size:calc(100% * var(--fs-zoom))}
    ${/* Les contrôles ne prennent plus la police SYSTÈME du navigateur : ils
       héritent de la famille de leur parent (Montserrat). font-family
       seulement : le raccourci « font: inherit » ferait aussi hériter la
       graisse et l'interligne du parent, et changerait la hauteur des boutons
       au repos. Placé AVANT le socle mobile, qui garde la main sur la taille
       des champs au doigt (16 px). */""}
    button, input, select, textarea{ font-family: inherit; }

    ${/* Les documents d'instance d'un membre (pilotage-seances, membre non
       administrateur ; lot 9 du bandeau de commande, R4) : une ligne
       entière à chevron, un vrai lien, survol et focus visibles. */""}
    .seances-cibles{ list-style:none; display:flex; flex-direction:column; gap:6px; margin:18px 0 0; padding:0; }
    .seances-cible{ display:flex; align-items:center; gap:10px; min-height:var(--cible-min, 44px); padding:10px 12px;
      border-radius:var(--radius-sm); border:1px solid var(--border-color); color:var(--text-main);
      text-decoration:none; font-weight:600; }
    .seances-cible > .seances-cible-t{ flex:1 1 auto; min-width:0; }
    .seances-cible:focus-visible{ outline:2px solid var(--focus); outline-offset:2px; }
    @media (hover:hover) and (pointer:fine){
      .seances-cible:hover{ background:var(--m-soft, color-mix(in srgb, var(--acc-navy) 8%, transparent)); }
    }

`;

// Le socle mobile : DERNIÈRE section de la feuille globale (composerFeuille le
// place après les raffinements et les effets). Ne rien écrire après lui.
const CSS_SOCLE_MOBILE = `
    ${/* ===================== SOCLE MOBILE (iPhone) =====================
       Audit responsive du 10/09/2026 : 402 defauts releves se ramenaient
       a une poignee de causes racines, toutes situees ICI. Ce bloc est
       VOLONTAIREMENT le dernier de la feuille — il corrige en un point
       ce qui etait dissemine sur des centaines d'ecrans. Ne rien ajouter
       apres lui : la cascade en depend.

       Regle d'or : viewport-fit=cover (public/index.html) et les env()
       ci-dessous vont TOUJOURS ensemble. Poser le premier sans les
       seconds CREE le recouvrement par l'encoche au lieu de le corriger.
       ================================================================ */""}

    ${/* 1 — Zone sure : encoche en haut, barre d'accueil en bas, coins
       arrondis en paysage. Vaut 0 partout ailleurs (desktop compris),
       donc ces variables sont sans effet hors iPhone/iPad. */""}
    :root{
      --sat:env(safe-area-inset-top,0px);
      --sar:env(safe-area-inset-right,0px);
      --sab:env(safe-area-inset-bottom,0px);
      --sal:env(safe-area-inset-left,0px);
    }

    ${/* 2 — Saisie au doigt. Sous 16px, Safari iOS ZOOME a chaque prise de
       focus et la mise en page saute : c'etait le cas de TOUS les champs
       de l'app, qui heritent des 13px de html (regle POLICE GLOBALE
       ci-dessus) ou de .inp (817 usages). La condition porte sur le
       POINTEUR et non sur une largeur : elle couvre l'iPhone en paysage
       comme l'iPad, et laisse au clavier-souris sa densite d'origine.
       Ne JAMAIS "corriger" ce zoom par maximum-scale=1 dans le viewport :
       cela desactiverait aussi le zoom d'accessibilite. */""}
    ${/* Passerelle pour les styles EN LIGNE. Un style inline bat toujours une
       classe : les ~90 champs qui portent leur fontSize en dur (constantes
       partagees inp/selectStyle, styles poses au fil du JSX) echappaient donc
       au socle et continuaient de faire zoomer Safari. Ils s'ecrivent
       desormais fontSize:"max(13px, var(--fs-champ-min))" :
         - au clavier-souris  --fs-champ-min vaut 0  -> max(13,0)  = 13px,
           le rendu du bureau est rigoureusement INCHANGE ;
         - au doigt           --fs-champ-min vaut 16 -> max(13,16) = 16px,
           le seuil iOS est franchi et le zoom au focus disparait. */""}
    ${/* Hauteur d'ecran utilisable, pour les styles EN LIGNE. Un objet de style
       JS n'a pas de cascade : y ecrire minHeight:"100vh" PUIS "100dvh" ne
       laisse que la seconde valeur, sans repli pour les navigateurs anciens.
       Cette variable porte le repli une fois pour toutes — les sites ecrivent
       minHeight:"var(--vh100)". Elle vit desormais dans theme.css avec
       --vw100, divisees par --fs-zoom (taille du texte, 01/10/2026) : sous le
       zoom de <body>, 100dvh rendait 1,38 ecran en « Tres grand ». */""}
    ${/* Plancher de cible tactile, pour les styles EN LIGNE. Même principe que
       --fs-champ-min : vaut 0 au clavier-souris (donc AUCUN effet sur le
       rendu du bureau, la hauteur naturelle du bouton l'emporte) et 44 px au
       doigt. Les sites écrivent minHeight:"var(--cible-min)" plutôt qu'un 44
       en dur, qui grossirait aussi les boutons du bureau. */""}

    ${/* Ce qui n'a de sens qu'au clavier-souris disparaît au doigt : la
       recherche globale et son raccourci ⌘K en tête (Header.js). La règle vise
       le POINTEUR et non une largeur, comme le reste du socle. */""}
    @media (pointer:coarse){ .header-desk-only{ display:none !important; } }

    :root{ --cible-min:0px; }
    @media (pointer:coarse){ :root{ --cible-min:44px; } }


    ${/* Hauteur de header retranchee par la COQUILLE des pages, pour les styles
       EN LIGNE (minHeight:"calc(var(--vh100) - var(--h-coquille))").
       AU CLAVIER-SOURIS elle vaut le 56px EN DUR d'avant le chantier. Prendre
       la hauteur MESUREE partout paraissait plus juste, mais le header du
       bureau mesure ~62px (logo de 44 + 2x8 de marge + le filet de 2px) : la
       coquille perdait 6px et le pied de page remontait d'autant sur toute
       page courte — un ecart de bureau, si petit soit-il.
       AU DOIGT au contraire on prend la hauteur reelle (--app-header-h, tenue
       a jour par le ResizeObserver de Header.js ; 48px sur iPhone) : c'est la
       que le 56 en dur, cumule avec 100vh, fabriquait les ~150px de
       defilement fantome que le chantier corrige. */""}
    :root{ --h-coquille:56px; }
    @media (pointer:coarse){ :root{ --h-coquille:var(--app-header-h,48px); } }

    :root{ --fs-champ-min:0px; }
    ${/* Multiplié par --fs-texte comme un jeton --fs-* (theme.css), avec les
       mêmes jumeaux : une <textarea> qui porte son texte suit les jetons ; un
       <input>, un <select>, une zone de texte vide, que le zoom de l'iPad
       atteint déjà, reprennent 16 px ; une zone éditable de l'iPhone,
       16 px x l'échelle. */""}
    @media (pointer:coarse){
      :root,
      html[data-texte-ajuste="jetons"] input, html[data-texte-ajuste="jetons"] select, html[data-texte-ajuste="jetons"] textarea:empty,
      html[data-texte-ajuste="jetons"] svg, html[data-texte-ajuste="jetons"] foreignObject,
      html[data-texte-ajuste="pourcentage"] [contenteditable]:not([contenteditable="false"]){ --fs-champ-min:calc(16px * var(--fs-texte, 1)); }
    }

    @media (pointer:coarse){
      .inp,
      input:not([type=checkbox]):not([type=radio]):not([type=range]):not([type=color]),
      select, textarea{ font-size:calc(16px * var(--fs-texte, 1)); min-height:44px; }

      ${/* Cibles tactiles : plancher Apple de 44 pt. .btn-sm (629 usages)
         mesurait 25 px de haut, .ptab-btn a peine plus. */""}
      .btn{ min-height:44px;
        ${/* Un bouton ne doit pas être un atome incompressible : .btn porte
           white-space:nowrap (l.574), donc sa largeur MINIMALE est son libellé
           entier — « Accéder au tableau de bord » mesure 260 px et poussait la
           page à lui seul. Au doigt le libellé passe à la ligne ; au
           clavier-souris la règle ne s'applique pas et rien ne bouge. */""}
        white-space:normal; max-width:100%; text-align:center; overflow-wrap:anywhere; }
      .btn-sm{ min-height:44px; padding:11px 14px; font-size:var(--fs-bouton-sm); }
      .ptab-btn{ min-height:44px; padding:10px 12px; font-size:var(--fs-segment); }
      ${/* Cases et boutons radio : la cible native fait 13 px de cote. */""}
      input[type=checkbox], input[type=radio]{ width:20px; height:20px; }
      ${/* Barre d'onglets : on defile plutot que d'empiler ou de rogner.
         justify-content:flex-start est INDISPENSABLE ici : avec le centrage
         d'origine, un contenu plus large que la barre deborde des DEUX cotes
         et les premiers onglets deviennent inatteignables (on ne peut pas
         defiler avant le debut du contenu). */""}
      .ptab{ overflow-x:auto; flex-wrap:nowrap; justify-content:flex-start; -webkit-overflow-scrolling:touch; }
      .ptab::-webkit-scrollbar{ display:none }
    }

    ${/* 3 — Header et tiroir de navigation : les deux seuls elements
       ancres en haut de l'ecran. En PWA installee (index.html porte
       apple-mobile-web-app-capable + black-translucent), la barre d'etat
       iOS est dessinee PAR-DESSUS la page : sans ce decalage, le logo,
       le bouton Menu et la deconnexion sont sous l'heure et la batterie,
       donc intouchables — plus aucune navigation possible. */""}
    ${/* !important : Header.js pose padding:"8px 14px" EN LIGNE, et un style en
       ligne bat toujours une classe. Sans le !important, ce decalage de zone
       sure ne s'appliquait QUE sous 480px (ou une autre regle le portait deja),
       laissant l'iPad installe en PWA avec son logo et son bouton Menu sous la
       barre d'etat. */""}
    .app-header{
      padding-top:calc(8px + var(--sat)) !important;
      padding-left:calc(14px + var(--sal)) !important;
      padding-right:calc(14px + var(--sar)) !important;
    }
    @media(max-width:480px){
      .app-header{
        padding-top:calc(6px + var(--sat)) !important;
        padding-bottom:6px !important;
        padding-left:calc(10px + var(--sal)) !important;
        padding-right:calc(10px + var(--sar)) !important;
      }
    }
    .mobile-menu-panel{
      ${/* 100dvh = hauteur REELLEMENT visible ; 100vh vaut sur iOS la
         hauteur barres masquees, ce qui poussait les dernieres entrees
         du menu sous la barre d'outils, sans moyen de les remonter. */""}
      max-height:calc(var(--vh100) - var(--app-header-h,56px));
      padding-bottom:var(--sab);
      overflow-y:auto;
      -webkit-overflow-scrolling:touch;
      overscroll-behavior:contain;
    }

    ${/* 5 — Rangees de puces-filtres (« Maturité », etc.). Les puces sont
       dessinees en ligne a ~25pt : au doigt on filtre sur la voisine. On les
       porte a 44pt SANS toucher au style en ligne (donc desktop inchange). */""}
    @media (pointer:coarse){
      .filtre-chips{ gap:10px !important; }
      .filtre-chips>button{ min-height:44px; padding:0 14px !important; font-size:var(--fs-micro) !important; }
    }

    ${/* 6 — Annuaire : « Écrire » / « Appeler ». Au clavier-souris RIEN ne
       change : les deux liens restent EMPILES, en clair, en 10px et sans
       cadre — exactement comme avant le chantier (a la souris l'adresse se
       lit et se copie). Toute la mise en pastille est enfermee ci-dessous :
       au doigt seulement, chaque lien devient un vrai bouton de 44pt portant
       le verbe, les deux cote a cote. */""}
    .annuaire-contact-court{ display:none; }
    @media (pointer:coarse){
      .annuaire-contacts{ display:flex; flex-wrap:wrap; gap:8px; margin-top:3px; }
      .annuaire-contact-ligne{ margin-top:0 !important; flex:1 1 auto; min-width:0; display:flex; }
      .annuaire-contact{
        display:inline-flex; align-items:center; gap:6px;
        max-width:100%; min-width:0; flex:1 1 auto;
        min-height:44px; padding:0 14px; font-size:var(--fs-micro);
        font-weight:600;
        border:1px solid rgba(29, 148, 138,.28); border-radius:8px;
        background:rgba(29, 148, 138,.07);
      }
      .annuaire-contact-txt{ display:none; }
      .annuaire-contact-court{ display:inline; }
    }

    ${/* 7 — Questions & entraide. Trois cibles minuscules au meme endroit :
       la croix qui RETIRE un destinataire (~8x14pt, alors qu'un envoi part
       aussitot en e-mail chez de vrais destinataires), « Clôturer » (~20pt) et
       la croix de fermeture de la fenetre (~28x24pt), voisines l'une de
       l'autre dans l'en-tete d'une fenetre de travail. */""}
    @media (pointer:coarse){
      .dest-chip{ padding:6px 10px !important; font-size:var(--fs-micro) !important; }
      .dest-chips-row{ gap:8px !important; }
      .dest-chip-x{ min-width:44px; min-height:44px; margin:-8px -6px -8px 0 !important;
        display:grid; place-items:center; font-size:calc(16px * var(--fs-texte, 1)) !important; }
      .dest-suggestion{ min-height:44px; display:flex; align-items:center; }
      .thread-hdr-btn{ min-height:44px; padding:0 14px !important; font-size:var(--fs-micro) !important; }
      .thread-hdr-x{ min-width:44px; min-height:44px; font-size:calc(22px * var(--fs-texte, 1)) !important; padding:0 !important; }
      ${/* Colonne de titre de la discussion : sans min-width:0 elle refuse de
         descendre sous sa largeur de min-content et pousse « Clôturer » et
         « × » hors de l'en-tete sur 375px. Sur les 560px du bureau le cas ne
         se presente pas, d'ou le confinement au doigt. */""}
      .thread-hdr-titre{ min-width:0; }
      .dest-badges{ min-width:0; }
      .dest-badge{ min-width:0; }
      .dest-badge-icon{ flex-shrink:0; }
      ${/* L'ellipse doit etre portee par l'ENFANT qui contient le texte :
         text-overflow reste inoperant sur un conteneur flex, et la liste des
         poles visés etait donc coupee NET, sans points de suspension. */""}
      .dest-badge-txt{ display:block; min-width:0; overflow:hidden;
        text-overflow:ellipsis; white-space:nowrap; }
      ${/* Barre de reponse collee au bas de la carte : dernier enfant d'une
         fenetre a defilement, c'etait la premiere chose que le clavier iOS
         recouvrait. */""}
      .thread-reply-bar{ position:sticky; bottom:0; z-index:2;
        background:var(--bg-card); padding-bottom:calc(12px + var(--sab,0px)) !important; }
    }

    ${/* 8 — « Engagement des mutuelles » (fiche projet, admin). La rangee
       s'enroule et le menu de statut prend toute la largeur sous le nom de la
       mutuelle, avec un corps de 16px (sinon Safari zoome a l'ouverture du
       menu) et une hauteur de 44pt. Le style en ligne, plus dense, reste seul
       en vigueur au clavier-souris. */""}
    @media (pointer:coarse){
      .eng-row{ flex-wrap:wrap; row-gap:2px; }
      .eng-row-nom{ flex:1 1 140px; width:auto !important; min-width:0; }
      .eng-row-lien{ display:none; }
      ${/* !important : le conteneur porte style={{flex:1}} EN LIGNE, ce qui pose
         flex-basis:0% et bat une declaration de classe ordinaire — la rangee ne
         s'enroulait donc pas et le menu de statut restait colle a ~140px a cote
         du nom, exactement le defaut qu'on corrige ici. */""}
      .eng-row-champ{ flex-basis:100% !important; margin-top:6px; }
      .eng-row-champ>select{ font-size:calc(16px * var(--fs-texte, 1)) !important; min-height:44px; padding:8px 10px !important; }
    }

    ${/* 9 — Champs de saisie : plancher de hauteur. Le §2 leur donne
       16px de corps, mais .inp n'a AUCUN min-height et retombait a ~40px des
       que le padding etait surcharge en ligne. */""}
    @media (pointer:coarse){
      .inp{ min-height:44px; padding:11px 14px; }
      textarea.inp{ min-height:88px; }
    }

    ${/* 10 — Fenetres de travail plein ecran (Questions & entraide). AU DOIGT
       seulement : le calque prend son propre defilement et aligne la carte EN
       HAUT, la carte se plafonne a la hauteur REELLEMENT visible. Centree et
       plafonnee a 90vh — unite qui vaut sur iOS la hauteur barres masquees —
       elle debordait symetriquement en haut et en bas, et « Envoyer » restait
       hors d'atteinte sans pincer. Le style en ligne (calque centre, carte a
       90vh) reste seul en vigueur au clavier-souris. */""}
    @media (pointer:coarse){
      .modale-plein-ecran{
        align-items:flex-start !important;
        overflow-y:auto;
        padding-bottom:calc(16px + var(--sab,0px)) !important;
      }
      .modale-plein-ecran>.card{
        margin:auto;
        max-height:min(var(--vh100) * 0.9, calc(var(--vh100) - var(--app-header-h,56px) - 32px)) !important;
      }
    }

    ${/* 11 — Bandeaux d'installation PWA. Sans enroulement, deux boutons
       incompressibles ecrasaient un texte d'instructions de 3-4 lignes sur
       375px. Reserve au doigt : au clavier-souris le bandeau tient sur une
       ligne, comme avant, et n'a jamais eu besoin de s'enrouler. */""}
    @media (pointer:coarse){
      .pwa-banner{ flex-wrap:wrap; }
    }

    ${/* 12 — Ce qui était calculé pour une souris et ne tient pas au doigt.
       Ces trois règles n'existent que sous pointeur grossier : au clavier-souris
       le rendu est celui d'avant, à la déclaration près. */""}
    ${/* 13 — LA COQUILLE MOBILE. data-coquille est posé sur <html> par
       installerClasseCoquille() (src/responsive.js) à partir du POINTEUR et de
       l'ÉCRAN PHYSIQUE, jamais d'une largeur de viewport — qui ment quand
       Safari élargit la page. Ces variables valent ZÉRO partout ailleurs : le
       rendu du clavier-souris ne peut pas bouger, par construction. */""}
    :root{ --h-onglets:0px; --bas-flottant:70px; }
    ${/* data-onglets (lot L6) : posé par App tant que la barre est affichée,
       iPad en portrait compris. 58 px + zone sûre. */""}
    html[data-coquille="tel"], html[data-onglets="1"]{
      --h-onglets:calc(58px + var(--sab,0px));
      --bas-flottant:calc(var(--h-onglets) + 10px);
    }
    ${/* Les bandeaux d'installation montent au-dessus de la barre. Leur padding
       bas est remis à plat : la barre porte déjà la zone sûre, les cumuler
       ferait une bande noire de trop. !important car les deux bandeaux portent
       leur padding EN LIGNE, et un style en ligne bat toujours une classe. */""}
    html[data-coquille="tel"] .pwa-banner, html[data-onglets="1"] .pwa-banner{
      bottom:var(--h-onglets) !important;
      padding-bottom:14px !important;
    }

    @media (pointer:coarse){
      ${/* Un onglet cesse d'être un atome : la barre .ptab défile déjà ici, le
         bouton peut donc céder sur sa largeur au lieu de pousser la page. */""}
      .ptab-btn{ min-width:0; white-space:normal; overflow-wrap:anywhere; }
      ${/* La messagerie flottante s'étale d'un bord à l'autre : ancrée à
         right:90 avec 380px, son bord gauche tombait à -40px sur un iPhone. */""}
      :root{ --chat-bord:10px; --chat-largeur:auto; }
    }

  `;

const injectCSS = () => installerFeuilleGlobale({
  composants: CSS_COMPOSANTS,
  raffinements: CSS_RAFFINEMENTS,
  effets: CSS_EFFETS,
  coquille: CSS_COQUILLE,
  primitives: CSS_PRIMITIVES,
  socle: CSS_SOCLE_MOBILE,
});

// Pages publiques à jeton montées AVANT <App/> (src/AppPublique.js, 28/09/2026) :
// la même feuille et les mêmes polices, sans rien démarrer d'autre.
export function installerFeuilleApp() { injectFont(); injectCSS(); }








// -- Convocation Word (.docx) : produite par src/generateConvocation.js (paquet
// npm docx). L'ancienne version, morte, et son chargeur depuis trois CDN
// sont retirés (briefing de sécurité du 27/10/2026, src/bibliothequesServies.js).







// -- Header -------------------------------------------------------------------
// ============================================================================
//  ATHOS — Module de suivi de projet
// ── PAGE D'ACCUEIL UNIFIÉE (Athos + Agora) ──────────────────────────────────
// ============================================================================
const AthosModule = ({view,onNav,projects,saveProject,deleteProject,setEngagement,meetings,saveMeeting,deleteMeeting,lists,delegates,saveDelegate,isAdmin,externes,saveExterne,saveReunion,updateInviteStatut,reunions=[],aramisSession,sessionReelle=null,questions=[],config,gouvernance,saveGouvernance,doodleAutoOpen,leaderboard=[],apercu=false}) => {

  // ── Projets & partenariats — routes par espace (refonte 2026-04) ──────────
  // athos-projets : Projets, Solutions et Missions ensemble (filtre de catégorie).
  //
  // Refonte 2026-05-20 : athos-projets devient la vue UNIFIÉE par défaut
  // (Projets + Solutions + Missions ensemble). Depuis le 30/09/2026, ses trois
  // alias (athos-solutions, athos-missions, athos-projets-classique), qui
  // rendaient CE MÊME écran sans clé, redirigent vers athos-projets
  // (LEGACY_REDIRECTS) : passer de l'un à l'autre changeait de vue sans
  // remonter l'écran ni lui annoncer ses paramètres, et un lien de projet
  // s'arrêtait sur la liste. La catégorie voyage en paramètre ({ categorie }),
  // filtre de la page.
  if(view==="athos-projets") {
    return(
    <AthosProjetsLazy
      projects={projects} saveProject={saveProject} deleteProject={deleteProject}
      setEngagement={setEngagement} lists={lists} gouvernance={gouvernance} delegates={delegates} isAdmin={isAdmin}
      meetings={meetings} saveMeeting={saveMeeting}
      saveReunion={saveReunion} externes={externes} saveExterne={saveExterne}
      aramisSession={aramisSession} reunions={reunions} config={config}/>
    );
  }

  // ── Réunions ───────────────────────────────────────────────────────────────
  if(view==="athos-reunions") return(
    <AthosReunionsViewLazy
      meetings={meetings} saveMeeting={saveMeeting} deleteMeeting={deleteMeeting}
      lists={lists} delegates={delegates} saveDelegate={saveDelegate} isAdmin={isAdmin}
      externes={externes} saveExterne={saveExterne}
      saveReunion={saveReunion} updateInviteStatut={updateInviteStatut}
      reunions={reunions} aramisSession={aramisSession}
      config={config} gouvernance={gouvernance} doodleAutoOpen={doodleAutoOpen}
      projects={projects} apercu={apercu}/>
  );

  // ── Carte des implantations territoriales ─────────────────────────────────
  if(view==="athos-implantations"){
    // Réglages du panorama (roue dentée) : sous la session RÉELLE (bandeau de
    // commande, lot 14) ; le contenu garde la session affichée.
    return <ImplantationsMapLazy lists={lists} onNav={onNav} aramisSession={aramisSession} sessionReelle={sessionReelle} isAdmin={isAdmin}/>;
  }

  // ── Historiques (présence/votes/contributions/connexions) — admin only ────
  if(view==="historiques"){
    return <HistoriquesViewLazy aramisSession={aramisSession} delegates={delegates}
      lists={lists} reunions={reunions} leaderboard={leaderboard} isAdmin={isAdmin}/>;
  }

  // ── Annuaire (lecture seule pour tous) ────────────────────────────────────
  if(view==="athos-annuaire"){
    // Visibilité des numéros : admin, DG, PR, CODIR (CD), ou membre CA
    // Basé sur le tableau roles structuré assigné par l'admin (pas le texte libre fonction)
    const canSeePhones = (()=>{
      if(isAdmin) return true;
      const me = delegates.find(d=>d.id===aramisSession?.id);
      if(!me) return false;
      const roles = me.roles||[];
      if(roles.includes("DG")) return true;
      if(roles.includes("PR")) return true;
      if(roles.includes("CD")) return true;
      if(roles.includes("CA")) return true;
      return false;
    })();
    return(
      <div style={{padding:0}}>
        <AthosAnnuaireViewLazy delegates={delegates} lists={lists} config={config}
          canSeePhones={canSeePhones}/>
      </div>
    );
  }

  // ── Domaines d'activité & Pôles ───────────────────────────────────────────
  if(view==="athos-domaines-poles") return(
    <PolesHubModuleLazy
      ORG_DOMAINES={ORG_DOMAINES}
      POLES_PAR_DOMAINE={POLES_PAR_DOMAINE}
      delegates={delegates} saveDelegate={saveDelegate}
      gouvernance={gouvernance||{}} saveGouvernance={saveGouvernance||(()=>{})}
      isAdmin={isAdmin}
      aramisSession={aramisSession}
      config={config}
      questions={questions}
      lists={lists}
      projects={projects}
      onNavToView={onNav}
      DocumentLibrary={DocumentLibrary}
      DelegateContactPopup={DelegateContactPopup}
      QuestionThread={QuestionThreadLazy}
      toastError={toast.error}/>
  );

  return null;
};


// ── Vue réunions (placeholder — sera enrichie en étape C) ────────────────────


// Pages publiques logées ici jusqu'au lot L12h (réinitialisation du mot de
// passe, réponse à une réunion, ouverture d'un document) : elles vivent dans
// leurs fichiers, chargées à la demande (voir les déclarations paresseuses).



// ════════════════════════════════════════════════════════════════════════════════
// BLOC D — ESPACE MEMBRE
// ════════════════════════════════════════════════════════════════════════════════

// ── Écran de connexion membre ─────────────────────────────────────────────────


// ════════════════════════════════════════════════════════════════════════
// CONNEXION ARAMIS UNIFIÉE
// ════════════════════════════════════════════════════════════════════════

// AnnuaireVerifPopup supprimé — obsolète, les infos se modifient via Mon Profil

// ── Page de garde Aramis ──────────────────────────────────────────────────────
const PageGarde = ({onConnexion}) => {
  const [theme,setThemeState]=useState(getTheme);

  useEffect(()=>{
    applyFontSize(getFontSize());
    applyTheme(getTheme());
  },[]);

  // Réglage « Système » de « Moi » (lot L6) : l'icône et la bascule partent du
  // thème AFFICHÉ, pas de la valeur mémorisée (sinon icône fausse, et le
  // premier appui passait en nuit même sur un écran déjà sombre).
  const themeAffiche = theme==="system"
    ? ((()=>{try{return !!(window.matchMedia&&window.matchMedia("(prefers-color-scheme: dark)").matches);}catch{return false;}})()?"dark":"light")
    : theme;
  const toggleTheme=()=>{const t=themeAffiche==="dark"?"light":"dark";setThemeState(t);setThemeLS(t);applyTheme(t);};

  return(
    <AuthBackdrop>
      {/* Seul contrôle : thème jour/nuit. Il etait a 34x34 dans le coin le
          moins accessible au pouce — pire, en PWA installee il passait sous la
          barre d'etat / la Dynamic Island, invisible et intapable. Decalage de
          zone sure (nul hors iPhone) + max(34px, --cible-min) : 34x34 comme
          avant au clavier-souris, 44pt au doigt. */}
      <button onClick={toggleTheme} title="Thème clair / sombre"
        style={{position:"fixed",
          top:"calc(16px + var(--sat,0px))",right:"calc(16px + var(--sar,0px))",zIndex:3,
          width:"max(34px, var(--cible-min))",height:"max(34px, var(--cible-min))",
          borderRadius:"var(--radius-md)",
          border:"1px solid rgba(255,255,255,.2)",
          background:"rgba(255,255,255,.08)",cursor:"pointer",
          display:"flex",alignItems:"center",justifyContent:"center"}}
        aria-label={themeAffiche==="dark"?"Passer en thème clair":"Passer en thème sombre"}>
        <Icone name={themeAffiche==="dark"?"sun":"moon"} size={18} style={{color:"var(--plate-ink)"}}/>
      </button>
      {/* Logotype officiel « enio », blanc, en haut à gauche (M13). */}
      <img src="/enio-logo-blanc.png" alt="enio"
        style={{position:"fixed",top:"calc(20px + var(--sat,0px))",left:"calc(20px + var(--sal,0px))",
          height:26,width:"auto",zIndex:3}}/>

      {/* Centre — picto Enio animé en grand : la vague de profondeur traverse
          les losanges du centre vers l'extérieur (PictoEnioAnime.js) */}
      <div className="auth-in" style={{textAlign:"center",maxWidth:460}}>
        <PictoEnioAnime width="min(var(--vw100) * 0.68,380px)" style={{margin:"0 auto"}}/>
        {/* « Aramis » en Light 300, blanc plein : fin du texte en dégradé et
            du Black 900 (synthèse M13). */}
        <h1 style={{fontWeight:300,fontSize:"calc(clamp(44px,8vw,64px) * var(--fs-texte, 1))",margin:"10px 0 0",
          fontFamily:"Montserrat,Arial,sans-serif",letterSpacing:"calc(-.028em * var(--em-ajuste, 1))",lineHeight:1.05,
          color:"var(--plate-ink)"}}>
          Aramis
        </h1>
        <p style={{fontSize:"var(--fs-accroche)",color:"var(--plate-muted)",margin:"12px 0 40px",
          fontFamily:"Montserrat,Arial,sans-serif",lineHeight:1.5}}>
          L'espace dédié aux mutuelles d'Enio
        </p>

        {/* L'action en CARTE CLAIRE sur le navy (14,7:1) : fin du blanc sur
            orange (2,25:1) et du halo orange. */}
        <button className="auth-cta"
          style={{width:"100%",maxWidth:300,minHeight:48,padding:"14px 0",fontSize:"var(--fs-bouton)",fontWeight:600,
            borderRadius:"var(--radius-md)",letterSpacing:"calc(-.005em * var(--em-ajuste, 1))",cursor:"pointer",border:"none",
            color:"var(--plate-cta-ink)",fontFamily:"inherit",background:"var(--plate-cta)"}}
          onClick={onConnexion}>
          Se connecter
        </button>

        <div style={{marginTop:38,fontSize:"var(--fs-micro)",color:"var(--plate-muted)",opacity:.75,
          fontFamily:"Montserrat,Arial,sans-serif"}}>
          53 Cours Mirabeau · Aix-en-Provence
        </div>
      </div>
    </AuthBackdrop>
  );
};


// ── Radar chart (spider) — axes d'engagement ────────────────────────────────
// axes: [{label, value: 0-1, color?}], value borné à [0,1]
// eslint-disable-next-line no-unused-vars
const RadarChart = ({axes, size=220, color="#1D948A", showLabels=true, strokeWidth=2}) => {
  const cx = size/2, cy = size/2;
  const pad = showLabels ? 38 : 10;
  const r  = (size/2) - pad;
  const n  = axes.length;
  const angleFor = i => (i*2*Math.PI/n) - Math.PI/2;

  // Points du polygone rempli
  const pointFor = (i, v) => {
    const a = angleFor(i);
    const dist = Math.max(0, Math.min(1, v)) * r;
    return [cx + dist*Math.cos(a), cy + dist*Math.sin(a)];
  };
  const dataPts = axes.map((ax,i) => pointFor(i, ax.value));
  const dataD = "M" + dataPts.map(p=>p.join(",")).join(" L") + " Z";

  // Grille : cercles à 25, 50, 75, 100%
  const grid = [0.25, 0.5, 0.75, 1.0];

  return (
    <svg width={size} height={size} style={{overflow:"visible"}}>
      {/* Grille circulaire */}
      {grid.map(g => (
        <circle key={g} cx={cx} cy={cy} r={r*g}
          fill="none" stroke="var(--border-color,#e5e7eb)"
          strokeWidth={0.8} strokeDasharray={g===1?"":"2,3"}/>
      ))}
      {/* Axes radiaux */}
      {axes.map((_,i) => {
        const [ex, ey] = pointFor(i, 1);
        return <line key={i} x1={cx} y1={cy} x2={ex} y2={ey}
          stroke="var(--border-color,#e5e7eb)" strokeWidth={0.8}/>;
      })}
      {/* Zone des données */}
      <path d={dataD} fill={color} fillOpacity={0.18}
        stroke={color} strokeWidth={strokeWidth} strokeLinejoin="round"/>
      {/* Points */}
      {dataPts.map((p,i)=>(
        <circle key={i} cx={p[0]} cy={p[1]} r={3} fill={color}/>
      ))}
      {/* Labels */}
      {showLabels && axes.map((ax,i)=>{
        const [lx, ly] = pointFor(i, 1.18);
        const anchor = Math.abs(lx-cx)<6 ? "middle" : (lx>cx ? "start" : "end");
        return (
          <g key={i}>
            <text x={lx} y={ly-2} textAnchor={anchor}
              fontSize={10} fontWeight={700}
              fill="var(--text-main,#111)"
              fontFamily="Montserrat,sans-serif">
              {ax.label}
            </text>
            <text x={lx} y={ly+9} textAnchor={anchor}
              fontSize={9} fontWeight={600}
              fill={color}
              fontFamily="Montserrat,sans-serif">
              {ax.display || ""}
            </text>
          </g>
        );
      })}
    </svg>
  );
};

// ── Barre horizontale de progression (legacy, remplacée par EngagementBars)
// eslint-disable-next-line no-unused-vars
const ProgressBar = ({value, max=100, label, color="#1D948A", sub}) => {
  const pct = Math.max(0, Math.min(100, Math.round((value/(max||1))*100)));
  return (
    <div style={{marginBottom:10}}>
      <div style={{display:"flex",justifyContent:"space-between",alignItems:"baseline",marginBottom:4}}>
        <span style={{fontSize:"var(--fs-micro)",fontWeight:700,color:"var(--text-main)",
          fontFamily:"Montserrat,sans-serif"}}>{label}</span>
        <span style={{fontSize:"var(--fs-micro)",fontWeight:700,color: navTexte(color)}}>
          {pct}% <span style={{fontSize:"var(--fs-micro)",color:"var(--text-sub)",fontWeight:600}}>{sub||""}</span>
        </span>
      </div>
      <Jauge valeur={pct} couleur={color} piste="rgba(0,0,0,.06)" hauteur={8} rayon={6} />
    </div>
  );
};



// buildDeepLink + CopyLinkButton sont définis dans ./deepLink.js et importés
// en haut du fichier (réutilisables aussi par ConsultationAsync, VeilleModule,
// Themis, etc.).






// ── Questions & Demandes d'aide ───────────────────────────────────────────────
const useQuestions = (aramisSession, isAdmin=false) => {
  const [questions, setQuestions] = useState([]);
  useEffect(() => {
    if (!aramisSession) return;
    let unsub;
    (async () => {
      const { collection, onSnapshot, query, orderBy } = await import("firebase/firestore");
      const { db } = await import("./firebase");
      const q = query(collection(db, "questions"), orderBy("ts", "desc"));
      unsub = onSnapshot(q, snap => {
        const all = snap.docs.map(d => ({ id: d.id, ...d.data() }));
        // Admins voient tout ; membres voient uniquement ce qui les concerne
        const filtered = isAdmin ? all : all.filter(q => {
          if (q.createurId === aramisSession.id) return true;
          if ((q.destinatairesIds||[]).includes(aramisSession.id)) return true;
          if ((q.destinatairesPoles||[]).some(p => (aramisSession.polesInteret||[]).includes(p))) return true;
          if ((q.destinatairesExpertises||[]).some(e => (aramisSession.expertises||[]).includes(e))) return true;
          return false;
        });
        setQuestions(filtered);
      });
    })();
    return () => { if (unsub) unsub(); };
  }, [aramisSession, isAdmin]);
  // Fonction de suppression (admin uniquement — la garde côté UI suffit pour la PoC)
  const deleteQuestion = async (qId) => {
    const { doc, deleteDoc } = await import("firebase/firestore");
    const { db } = await import("./firebase");
    await deleteDoc(doc(db, "questions", qId));
  };
  return { questions, deleteQuestion };
};

// ============================================================================
//  ECRAN DE SALLE
// ============================================================================
const ScreenView = ({appState,onNav}) => {
  const {session,participants,agenda,votesByScrutin,liveDisplay}=appState;
  // Écran de salle affiché (vidéoprojecteur) : tous les onglets se taisent (sons.js).
  useEffect(()=>signalerEcranSalle(),[]);
  const actifs=participants.filter(isActif);
  // Hors session (lecture publique, lot 4) : aucun participant, le décompte
  // des présents vient de nbActifs.
  const quorum=typeof appState.nbActifs==="number"?appState.nbActifs:actifs.length;
  const openScrutins=agenda.filter(s=>s.status==="open");
  // Le QR code ouvre l'espace du votant de CETTE séance (18/10/2026).
  const lienAcces=urlAccesSeance(session?.id);
  const instCfg=getInstConfig(session);

  // Determine what to show based on liveDisplay
  const ld=liveDisplay||null;
  const ldScrutin=ld?.scrutinId?agenda.find(s=>s.id===ld.scrutinId):null;
  const ldVs=ldScrutin?(votesByScrutin[ldScrutin.id]||[]):[];

  // Atelier d'amendements à projeter (résultats ciblés ou atelier ouvert) +
  // abonnement live aux propositions et à leurs votes pour l'écran de salle.
  const screenAmdtPoint=(ldScrutin&&ldScrutin.type==="amendement")
    ?ldScrutin
    :(openScrutins.find(s=>s.type==="amendement")||(openScrutins[0]?.type==="amendement"?openScrutins[0]:null));
  const {amendements:screenAmendements,votesByAmd:screenVotesByAmd,counts:screenAmdtCounts}=useAmendementsScreen(screenAmdtPoint?.id||null);

  // Confettis supprimés à la demande du PO — publication de résultats silencieuse.

  // Minuterie depuis liveDisplay.timerEnd
  const [timerSec,setTimerSec]=useState(null);
  useEffect(()=>{
    if(!ld?.timerEnd){setTimerSec(null);return;}
    const tick=()=>{
      const rem=Math.max(0,Math.round((ld.timerEnd-Date.now())/1000));
      setTimerSec(rem);
    };
    tick();
    const id=setInterval(tick,500);
    return()=>clearInterval(id);
  },[ld?.timerEnd]);

  // Render scrutin en cours (participation + resultats si publies)
  const renderScrutinEnCours=(s,vs)=>{
    const oui=vs.filter(v=>v.choice==="oui").length;
    const non=vs.filter(v=>v.choice==="non").length;
    const abst=vs.filter(v=>v.choice==="abstention").length;
    const pct=(n)=>vs.length>0?Math.round(n/vs.length*100):0;
    const partPct=quorum>0?Math.round(vs.length/quorum*100):0;

    // Vote EN SÉANCE enregistré à l'unanimité : un grand bandeau solennel
    // remplace le triptyque OUI/NON/ABSTENTION — plus lisible en salle, et le
    // détail 27/0/0 n'apporte rien quand la saisie même dit « unanimité ».
    if(s.modeVote==="seance"&&s.seanceSaisie?.mode==="unanimite"&&vs.length>0){
      const approuve=s.seanceSaisie.sens!=="rejete";
      const col=approuve?C.green:C.red;
      return(
        <div className="anim-fadeup" style={{textAlign:"center",padding:"40px 20px"}}>
          <div style={{fontSize:"var(--fs-meta)",color:navTexte(C.gray),fontWeight:700,textTransform:"uppercase",letterSpacing:"calc(.12em * var(--em-ajuste, 1))",marginBottom:18}}>
            <IconeTexte nom="hand-stop"/> Vote en séance
          </div>
          <div style={{
            display:"inline-block",padding:"36px 56px",borderRadius:"var(--radius-xl)",
            background:alpha(col, .094),border:`3px solid ${col}`,
          }}>
            <div style={{fontSize:"var(--fs-display)",fontWeight:700,color:navTexte(col),lineHeight:1.15}}>
              {approuve?"ADOPTÉE":"REJETÉE"}
            </div>
            <div style={{fontSize:"var(--fs-chronique)",fontWeight:700,color:navTexte(col),marginTop:6}}>
              à l'unanimité
            </div>
            <div style={{fontSize:"var(--fs-corps)",color:navTexte(C.gray),marginTop:12,fontWeight:600}}>
              {vs.length} voix
            </div>
          </div>
        </div>
      );
    }

    // Podium pour nominatif (top 3)
    const renderNominatif=()=>{
      // Blancs et nuls d'un bulletin secret dépouillé : jamais sur le podium.
      const cc=compterCandidats(vs);
      const entries=cc.candidats;
      if(entries.length===0)return null;
      const maxV=entries[0]?.[1]||1;
      // Calcul du rang réel avec gestion ex-aequo
      const RANK_COLORS=["#F49800","#F49800","#94C11F","#1D948A","#8a9aaa"];
      const RANK_BG=["rgba(244, 152, 0,.15)","rgba(244, 152, 0,.12)","rgba(148, 193, 31,.12)","rgba(29, 148, 138,.1)","transparent"];
      let rank=1;
      const ranked=[];
      entries.forEach((entry,i)=>{
        if(i>0&&entry[1]<entries[i-1][1])rank=i+1;
        ranked.push({name:entry[0],count:entry[1],rank});
      });
      const rankColor=(r)=>RANK_COLORS[Math.min(r-1,RANK_COLORS.length-1)];
      const rankBg=(r)=>RANK_BG[Math.min(r-1,RANK_BG.length-1)];
      return(
        <div style={{background:"var(--bg-card,"+C.navy+")",borderRadius:"var(--radius-xl)",padding:"28px 24px",marginBottom:20}}>
          <div style={{fontSize:"var(--fs-micro)",color:navTexte(C.gray),fontWeight:700,textTransform:"uppercase",letterSpacing:"calc(.1em * var(--em-ajuste, 1))",marginBottom:20,textAlign:"center"}}>Resultats</div>
          {ranked.map(({name,count,rank},i)=>(
            <div key={name} className="anim-fadeup"
              style={{display:"flex",alignItems:"center",gap:14,marginBottom:10,
                background:rankBg(rank),borderRadius:"var(--radius-md)",padding:"12px 16px",
                border:rank===1?"1.5px solid "+rankColor(1):"1px solid transparent",
                animationDelay:(i*.07)+"s"}}>
              <div style={{width:36,height:36,borderRadius:"50%",background:rankColor(rank),
                display:"grid",placeItems:"center",fontSize:"var(--fs-nav)",fontWeight:700,
                flexShrink:0,color:"#fff",boxShadow:"0 2px 8px rgba(0,0,0,.3)"}}>
                {rank}
              </div>
              <div style={{flex:1}}>
                <div style={{fontWeight:700,fontSize:"var(--fs-corps)",marginBottom:5,color:rank<=2?"#fff":navTexte(C.white)}}>{name}</div>
                <Jauge valeur={Math.round(count/maxV*100)} couleur={rankColor(rank)} piste="var(--ptrack-bg)" hauteur={8} />
              </div>
              <div style={{fontSize:"calc(clamp(18px,3vw,34px) * var(--fs-texte, 1))",fontWeight:700,color:rankColor(rank),minWidth:44,textAlign:"right"}}
                className="anim-countup">{count}</div>
            </div>
          ))}
          {(cc.blancs>0||cc.nuls>0)&&(
            <div style={{textAlign:"center",fontSize:"var(--fs-nav)",color:navTexte(C.gray),fontWeight:700,marginTop:12}}>
              {[cc.blancs>0&&`Bulletins blancs : ${cc.blancs}`,cc.nuls>0&&`Bulletins nuls : ${cc.nuls}`].filter(Boolean).join("  ·  ")}
            </div>
          )}
        </div>
      );
    };

    return(
      // Projection en séance : une largeur écrite POUR ce contexte (CLAUDE.md,
      // « Restent bornées ») ; elle ne suit plus --content-max, qui vaut
      // toute la colonne de la coquille depuis le 27/10/2026.
      <div style={{maxWidth:"min(100%, 1600px)",width:"100%"}}>
        <div style={{marginBottom:8,display:"flex",gap:12,justifyContent:"center",flexWrap:"wrap"}}>
          <VoteBadge secret={s.secret}/><ResultsBadge published={s.resultsPublished}/>
        </div>
        <div style={{fontWeight:700,fontSize:"calc(clamp(16px,3vw,28px) * var(--fs-texte, 1))",lineHeight:1.2,marginBottom:"clamp(12px,var(--vw100) * 0.02,24px)",color:"var(--text-main,#fff)",textAlign:"center"}}>{s.question}</div>

        {/* Participation — jauge circulaire */}
        <div style={{background:"var(--bg-card,"+C.navy+")",borderRadius:"var(--radius-xl)",padding:"20px 28px",marginBottom:20,display:"flex",alignItems:"center",justifyContent:"center",gap:40,flexWrap:"wrap"}}>
          <CircleGauge value={vs.length} max={quorum||1} size={160} color={partPct===100?C.green:C.teal} label={"/ "+quorum}/>
          <div style={{textAlign:"center"}}>
            <div style={{fontSize:"calc(clamp(28px,5vw,52px) * var(--fs-texte, 1))",fontWeight:700,color:navTexte(partPct===100?C.green:C.orange),lineHeight:1}} className="anim-countup">{partPct}%</div>
            <div style={{fontSize:"var(--fs-nav)",color:navTexte(C.gray),marginTop:4}}>de participation</div>
            <div style={{fontSize:"var(--fs-micro)",color:navTexte(C.gray),marginTop:8}}>{vs.length} vote{vs.length!==1?"s":""} / {quorum} participant{quorum!==1?"s":""}</div>
          </div>
        </div>

        {/* YON */}
        {s.resultsPublished&&s.type==="yon"&&vs.length>0&&(
          <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fit,minmax(150px,1fr))",gap:16,marginBottom:20}}>
            {[{l:"OUI",v:oui,c:C.green},{l:"NON",v:non,c:C.red},{l:"ABSTENTION",v:abst,c:"#8a9aaa"}].map(({l,v,c},i)=>(
              <div key={l} className="anim-fadeup" style={{background:"var(--bg-card,"+C.navy+")",borderRadius:"var(--radius-xl)",padding:"24px 20px",borderTop:"5px solid "+c,animationDelay:(i*.12)+"s"}}>
                <div style={{fontSize:"var(--fs-micro)",color:navTexte(c),fontWeight:700,textTransform:"uppercase",letterSpacing:"calc(.1em * var(--em-ajuste, 1))",marginBottom:12}}>{l}</div>
                <div style={{fontSize:"calc(clamp(28px,5vw,60px) * var(--fs-texte, 1))",fontWeight:700,color:navTexte(c),lineHeight:1,marginBottom:8}} className="anim-countup">{v}</div>
                <div style={{marginBottom:10}}>
                  <Jauge valeur={pct(v)} couleur={c} piste="var(--ptrack-bg,rgba(255,255,255,.1))" hauteur={10} rayon={99} />
                </div>
                <div style={{fontSize:"var(--fs-chronique)",color:"rgba(255,255,255,.6)",fontWeight:700}}>{pct(v)}%</div>
              </div>
            ))}
          </div>
        )}

        {/* NOMINATIF — podium */}
        {s.resultsPublished&&s.type==="nominatif"&&vs.length>0&&renderNominatif()}

        {/* Autres types */}
        {s.resultsPublished&&(s.type==="nuage"||s.type==="notation"||s.type==="liste"||s.type==="swot")&&(
          <div style={{background:"var(--bg-card,"+C.navy+")",borderRadius:"var(--radius-lg)",padding:"20px"}}>
            <ScrutinResults s={s} vs={vs} quorum={quorum} isAdmin={false}/>
          </div>
        )}

        {!s.resultsPublished&&(
          <div className="results-hidden" style={{fontSize:"var(--fs-corps)",padding:36}}>
            <div style={{fontSize:"var(--fs-display)",marginBottom:10}}><IconeTexte nom="lock" titre="Verrouillé"/></div>
            <div style={{fontWeight:700,color:navTexte(C.orangeL)}}>Resultats non encore publies</div>
          </div>
        )}
      </div>
    );
  };

  // Render message libre
  const renderMessage=()=>(
    <div style={{maxWidth:800,width:"100%",textAlign:"center"}}>
      <div style={{fontSize:"var(--fs-micro)",color:navTexte(C.gray),fontWeight:700,marginBottom:20}}>Message</div>
      <div style={{fontSize:"var(--fs-compteur)",fontWeight:700,lineHeight:1.5,color:"var(--text-main,#fff)",whiteSpace:"pre-wrap"}}>{ld?.message||""}</div>
    </div>
  );

  // Render attente
  const renderAttente=()=>(
    <div>
      <div style={{fontSize:"var(--fs-chronique)",color:navTexte(C.gray),fontWeight:700,marginBottom:16}}>En attente du prochain scrutin</div>
      <div style={{display:"flex",alignItems:"center",gap:10,justifyContent:"center",color:"var(--tx-teal)",fontSize:"var(--fs-corps)",fontWeight:700}}>
        <span className="pulse">●</span> En attente
      </div>
    </div>
  );

  // Render atelier d'amendements — vue de salle riche : propositions en cours
  // (statut + auteur + diff + votes) à gauche, texte de travail/adopté à
  // droite. Tout est mis à jour en direct (Firestore onSnapshot).
  const renderAmendement=(s)=>{
    const VIO="#951B81";
    const texte=s.texteConsolide||s.texteBase||"";
    const adopted=s.phase==="adopte";
    const linked=screenAmdtPoint&&s.id===screenAmdtPoint.id;
    const amdts=linked?screenAmendements:[];
    const counts=linked?screenAmdtCounts:{total:0,adopte:0,enDelib:0,rejete:0,retire:0};
    const hasProps=amdts.length>0;
    const hasTexte=!!(texte&&texte.trim());
    const meta=(st)=> st==="adopte"?{l:"Adopté",c:C.green,icon:"✅"}
      :st==="rejete"?{l:"Rejeté",c:C.red,icon:"✕"}
      :st==="retire"?{l:"Retiré",c:C.gray,icon:"—"}
      :{l:"En délibération",c:C.gold,icon:"⏳"};
    const exc=(t,n)=>{const x=(t||"").replace(/\s+/g," ").trim();return x.length>n?x.slice(0,n)+"…":x;};

    const chips=[
      {l:"Propositions",v:counts.total,c:VIO},
      {l:"Adoptés",v:counts.adopte,c:C.green},
      {l:"En délibération",v:counts.enDelib,c:C.gold},
      {l:"Rejetés",v:counts.rejete,c:C.red},
    ];

    const texteBlockStyle={background:"var(--bg-card,"+C.navy+")",borderRadius:"var(--radius-lg)",padding:"clamp(18px,var(--vw100) * 0.024,32px)",fontSize:"calc(clamp(15px,1.7vw,21px) * var(--fs-texte, 1))",lineHeight:1.65,color:"var(--text-main,#fff)",maxHeight:hasProps?"calc(var(--vh100) * 0.58)":"calc(var(--vh100) * 0.6)",overflowY:"auto",textAlign:"left"};

    const TexteCard=(
      <div>
        <div style={{fontSize:"var(--fs-meta)",fontWeight:700,color:navTexte(adopted?C.green:C.teal),textTransform:"uppercase",letterSpacing:"calc(.08em * var(--em-ajuste, 1))",marginBottom:10,textAlign:"left"}}>
          {adopted?<><IconeTexte nom="circle-check" couleur="var(--tx-green)"/> Texte adopté</>:<><IconeTexte nom="notes"/> Texte de travail (en direct)</>}
        </div>
        {isAmdtHtml(texte)
          ?<div className="rich-text" style={{...texteBlockStyle}} dangerouslySetInnerHTML={{__html:sanitizeAmdtHtml(texte)}}/>
          :<div style={{...texteBlockStyle,whiteSpace:"pre-wrap"}}>{texte||"En attente du texte de référence…"}</div>}
      </div>
    );

    const PropCard=(a)=>{
      const m=meta(a.statut);
      const v=screenVotesByAmd[a.id]||{pour:0,contre:0,abstention:0};
      const total=v.pour+v.contre+v.abstention;
      const pct=(x)=>total>0?Math.round(x/total*100):0;
      const mode=a.mode||"modifier";
      return(
        <div key={a.id} className="anim-fadeup" style={{border:"1px solid rgba(255,255,255,.08)",borderLeft:"5px solid "+m.c,borderRadius:"var(--radius-lg)",padding:"14px 16px",background:"var(--bg-card,"+C.navy+")",textAlign:"left"}}>
          <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",gap:8,marginBottom:9,flexWrap:"wrap"}}>
            <span style={{fontSize:"var(--fs-meta)",color:navTexte(C.gray),fontWeight:600}}>{a.arbitrage?<><IconeTexte nom="scale"/> Arbitrage · </>:""}{a.auteurNom||"Proposition"}{a.mutuelle?" · "+a.mutuelle:""}</span>
            <span style={{fontSize:"var(--fs-meta)",fontWeight:700,color:navTexte(m.c),background:alpha(m.c, .149),borderRadius:"var(--radius-pill)",padding:"3px 13px"}}>{m.icon} {m.l}</span>
          </div>
          {mode==="ajouter"&&<div>
            <div style={{fontSize:"var(--fs-micro)",fontWeight:700,color:navTexte(VIO),marginBottom:4}}><IconeTexte nom="plus"/> Ajout {a.insertPos==="debut"?"au début":a.insertPos==="apres"?"après : « "+exc(a.ancre,46)+" »":"à la fin"}</div>
            <div style={{fontSize:"calc(clamp(15px,1.6vw,19px) * var(--fs-texte, 1))",fontWeight:700,color:"var(--tx-green)",lineHeight:1.4}}>{a.proposition}</div>
          </div>}
          {mode==="supprimer"&&<div>
            <div style={{fontSize:"var(--fs-micro)",fontWeight:700,color:"var(--tx-red)",marginBottom:4}}><IconeTexte nom="trash"/> Suppression</div>
            <div style={{fontSize:"calc(clamp(14px,1.5vw,18px) * var(--fs-texte, 1))",color:"var(--tx-red)",textDecoration:"line-through",opacity:.8,lineHeight:1.4}}>{a.passage}</div>
          </div>}
          {mode==="modifier"&&<div>
            {a.passage&&<div style={{fontSize:"calc(clamp(14px,1.5vw,18px) * var(--fs-texte, 1))",color:"var(--tx-red)",textDecoration:"line-through",opacity:.8,marginBottom:4,lineHeight:1.4}}>{a.passage}</div>}
            <div style={{fontSize:"calc(clamp(15px,1.6vw,19px) * var(--fs-texte, 1))",fontWeight:700,color:"var(--tx-green)",lineHeight:1.4}}>{a.proposition}</div>
          </div>}
          {a.justification&&<div style={{fontSize:"var(--fs-meta)",color:navTexte(C.gray),fontStyle:"italic",margin:"7px 0 0"}}>« {a.justification} »</div>}
          {total>0&&<div style={{display:"grid",gridTemplateColumns:"repeat(3,1fr)",gap:8,marginTop:11}}>
            {[["pour","👍 Pour",C.green,v.pour],["contre","👎 Contre",C.red,v.contre],["abstention","🤷 Abst.",C.gray,v.abstention]].map(([k,lab,col,val])=>(
              <div key={k}>
                <div style={{display:"flex",justifyContent:"space-between",fontSize:"var(--fs-meta)",marginBottom:3}}><span style={{color:navTexte(C.gray)}}>{lab}</span><b style={{color:navTexte(col)}}>{val}</b></div>
                <Jauge valeur={pct(val)} couleur={col} piste="rgba(255,255,255,.1)" hauteur={8} rayon={99} />
              </div>
            ))}
          </div>}
        </div>
      );
    };

    const PropsCard=(
      <div>
        <div style={{fontSize:"var(--fs-meta)",fontWeight:700,color:navTexte(VIO),textTransform:"uppercase",letterSpacing:"calc(.08em * var(--em-ajuste, 1))",marginBottom:10,textAlign:"left"}}><IconeTexte nom="checkbox"/> Propositions d'amendement</div>
        <div style={{display:"flex",flexDirection:"column",gap:11,maxHeight:hasTexte?"calc(var(--vh100) * 0.58)":"calc(var(--vh100) * 0.62)",overflowY:"auto",paddingRight:2}}>
          {amdts.map(PropCard)}
        </div>
      </div>
    );

    return(
      <div style={{maxWidth:"100%",width:"100%"}}>
        <div style={{display:"flex",gap:10,justifyContent:"center",marginBottom:12}}>
          <span style={{fontSize:"var(--fs-meta)",fontWeight:700,color:"#fff",background:VIO,borderRadius:"var(--radius-pill)",padding:"5px 18px",letterSpacing:"calc(.05em * var(--em-ajuste, 1))"}}><IconeTexte nom="scale"/> ATELIER D'AMENDEMENTS</span>
        </div>
        <div style={{fontWeight:700,fontSize:"calc(clamp(18px,3vw,30px) * var(--fs-texte, 1))",lineHeight:1.2,marginBottom:14,color:"var(--text-main,#fff)",textAlign:"center"}}>{s.titre||s.question}</div>
        {counts.total>0&&<div style={{display:"flex",gap:10,justifyContent:"center",flexWrap:"wrap",marginBottom:18}}>
          {chips.map(ch=>(
            <div key={ch.l} style={{background:alpha(ch.c, .122),border:"1px solid "+alpha(ch.c, .333),borderRadius:"var(--radius-lg)",padding:"8px 18px",textAlign:"center",minWidth:96}}>
              <div style={{fontSize:"calc(clamp(20px,2.6vw,32px) * var(--fs-texte, 1))",fontWeight:700,color:navTexte(ch.c),lineHeight:1}}>{ch.v}</div>
              <div style={{fontSize:"var(--fs-micro)",color:navTexte(C.gray),fontWeight:700,marginTop:3}}>{ch.l}</div>
            </div>
          ))}
        </div>}
        {hasProps&&hasTexte
          ?<div style={{display:"grid",gridTemplateColumns:"repeat(auto-fit,minmax(min(100%,320px),1fr))",gap:18,alignItems:"start"}}>{PropsCard}{TexteCard}</div>
          :hasProps?PropsCard:TexteCard}
        <div style={{textAlign:"center",marginTop:16,fontSize:"var(--fs-corps)",fontWeight:700,color:navTexte(adopted?C.green:C.teal)}}>
          {adopted?<><IconeTexte nom="circle-check" couleur="var(--tx-green)"/> Texte adopté</>:<><IconeTexte nom="notes"/> Amendements en cours : texte mis à jour en direct</>}
        </div>
      </div>
    );
  };

  // Determine content
  let content;
  let amendementMode=false; // atelier d'amendements affiché → pleine largeur, sans logo/QR
  // Sans scrutin : la composition d'accueil de l'écran (QR code, étapes).
  let attente=false;
  const attendre=()=>{attente=true;return renderAttente();};
  // Sans point ouvert, les résultats PUBLIÉS du point qui vient d'être clos
  // restent à l'écran jusqu'à l'ouverture du suivant (recette du 18/10/2026,
  // src/pointClos.js) ; non publiés, l'écran attend.
  const closPublie=(()=>{const p=dernierPointClos(agenda);return p&&p.resultsPublished?p:null;})();
  const attendreOuClos=()=>closPublie?renderScrutinEnCours(closPublie,votesByScrutin[closPublie.id]||[]):attendre();
  if(ld&&ld.type==="scrutin_en_cours"){
    const s=openScrutins[0]||null;
    amendementMode=s?.type==="amendement";
    content=s?(s.type==="amendement"?renderAmendement(s):renderScrutinEnCours(s,votesByScrutin[s.id]||[])):attendreOuClos();
  } else if(ld&&ld.type==="resultats"&&ldScrutin){
    amendementMode=ldScrutin.type==="amendement";
    content=ldScrutin.type==="amendement"?renderAmendement(ldScrutin):renderScrutinEnCours(ldScrutin,ldVs);
  } else if(ld&&ld.type==="swot"&&ldScrutin){
    content=(
      <div style={{maxWidth:1000,width:"100%"}}>
        <div style={{fontWeight:700,fontSize:"var(--fs-chronique)",marginBottom:16,color:"var(--text-main,#fff)"}}>{ldScrutin.question}</div>
        <ScrutinResults s={{...ldScrutin,resultsPublished:true}} vs={ldVs} quorum={quorum} isAdmin={false}/>
      </div>
    );
  } else if(ld&&ld.type==="nuage"&&ldScrutin){
    content=(
      <div style={{maxWidth:900,width:"100%"}}>
        <div style={{fontWeight:700,fontSize:"var(--fs-chronique)",marginBottom:16,color:"var(--text-main,#fff)"}}>{ldScrutin.question}</div>
        <ScrutinResults s={{...ldScrutin,resultsPublished:true}} vs={ldVs} quorum={quorum} isAdmin={false}/>
      </div>
    );
  } else if(ld&&ld.type==="message"){
    content=renderMessage();
  } else {
    // Aucun affichage choisi : comportement par défaut. On privilégie un
    // atelier d'amendements ouvert (sinon premier scrutin ouvert).
    const currentScrutin=openScrutins.find(s=>s.type==="amendement")||openScrutins[0]||null;
    amendementMode=currentScrutin?.type==="amendement";
    content=currentScrutin
      ?(currentScrutin.type==="amendement"?renderAmendement(currentScrutin):renderScrutinEnCours(currentScrutin,votesByScrutin[currentScrutin.id]||[]))
      :attendreOuClos();
  }

  // Si la vue a été ouverte dans une fenêtre popup (depuis le bouton
  // "🖥 Écran de salle" de Thémis Séance), on propose de fermer la fenêtre
  // au lieu de revenir aux résultats Agora.
  const isPopupWindow = typeof window !== "undefined" && !!window.opener && window.opener !== window;
  const handleQuit = () => {
    if (isPopupWindow) { try { window.close(); } catch {} return; }
    onNav("results");
  };

  // L'habillage de l'écran de l'AG (src/SalleSeance.js, 18/10/2026) : fond
  // sombre, pictogramme officiel, minuteur, QR code de l'espace du votant.
  // Repli pendant le chargement du morceau : le contenu seul, sur le fond
  // de l'écran.
  const termine=agenda.length>0&&agenda.every(s=>s.status==="closed");
  return(
    <Suspense fallback={<div className="screen-mode">{content}</div>}>
      <SalleSeanceLazy session={session} libelleInstance={instCfg.label} lien={lienAcces} lienLisible={urlAccesLisible(session?.id)}
        attente={attente} termine={termine} pleineLargeur={amendementMode} nbPresents={quorum} minuteur={timerSec}
        quitter={{libelle:isPopupWindow?"Fermer":"Quitter",onClick:handleQuit}}>
        {content}
      </SalleSeanceLazy>
    </Suspense>
  );
};




// ── COUCHE D'ACCÈS PAR DOMAINE (Étape 1 refonte navigation) ─────────────────
// MODE OBSERVE : on calcule la décision d'accès *cible* mais on NE BLOQUE RIEN.
// Les accès qui seraient refusés par le modèle cible sont seulement journalisés
// (console + auditLog), pour mesurer l'impact réel avant tout enforcement
// (Étape 4). Modèle : Budget = admin Enio strict ; outils de domaine = membres
// du domaine + CODIR(CD)/CA/DG/PR ; RH et RGPD = habilitation via fiche.
// Voir docs/audit-navigation.md §3.1.
const ACCESS_OBSERVE_MODE = true;   // true = observe (ne bloque pas) ; false = inactif
// Étape 4 — ENFORCEMENT. false = aucun blocage (seul l'observe journalise) ;
// true = bloque réellement l'accès (deeplink inclus) aux vues non autorisées.
// À passer à true APRÈS revue des logs observe (action=access_observe), pour
// ne verrouiller qu'à bon escient. Garde central dans le rendu de App().
// ACTIVÉ (2026-06-28) : modèle « tous les menus visibles + page navrés si pas
// d'accès » — la visibilité du menu n'est plus gardée, seule la route l'est.
const ACCESS_ENFORCE_MODE = true;

// Lot L7 : la raison d'un refus, dite à la personne (la vitrine « Demander
// l'accès » l'affiche). Les motifs de evaluateViewAccess restent ceux du
// journal (access_observe).
const raisonAcces = (v) => {
  const s = String((v && v.scope) || "");
  if (v && String(v.reason || "").startsWith("compte invité")) return "Votre compte invité permet la consultation seule.";
  if (s === "admin") return "Cet espace est réservé à l'équipe d'Enio.";
  if (s === "hab:RH") return "Cet espace est réservé aux personnes habilitées aux ressources humaines.";
  if (s === "hab:DPO") return "Cet espace est réservé aux délégués à la protection des données et, en lecture, au directeur général de leur mutuelle.";
  if (s === "hab:EC") return "Cet espace est réservé à l'expert-comptable et à l'équipe d'Enio.";
  if (s.startsWith("dom:")) return "Cet espace est réservé aux membres du domaine concerné.";
  return "";
};

// Normalisation d'un libellé de pôle (accents/casse/ponctuation).
const _normPole = s => String(s||"")
  .normalize("NFD").replace(/[̀-ͯ]/g,"")
  .toLowerCase().replace(/[^a-z0-9]+/g," ").trim();

// Index inverse pôle(normalisé) → id de domaine, construit depuis ORG_DOMAINES.
const DOMAINE_BY_POLE = (() => {
  const m = {};
  for (const d of ORG_DOMAINES) for (const p of (d.poles||[])) m[_normPole(p)] = d.id;
  return m;
})();
const ALL_DOMAINE_IDS = ORG_DOMAINES.map(d => d.id);

// Domaines d'un membre, dérivés de sa fiche (pôles d'intérêt) + rôles.
// Gouvernance transverse (DG, CODIR=CD, CA, Président=PR) → TOUS les domaines.
const memberDomaineIds = (session, delegates) => {
  if (!session) return [];
  const me = (Array.isArray(delegates) ? delegates.find(d => d.id === session.id) : null) || session;
  const roles = Array.isArray(me.roles) ? me.roles : [];
  if (roles.some(r => ["DG","CD","CA","PR"].includes(r))) return ALL_DOMAINE_IDS.slice();
  const ids = new Set();
  for (const p of (me.polesInteret || [])) { const id = DOMAINE_BY_POLE[_normPole(p)]; if (id) ids.add(id); }
  for (const d of (me.domaines || [])) {
    if (ALL_DOMAINE_IDS.includes(d)) { ids.add(d); continue; }
    const byLabel = ORG_DOMAINES.find(x => _normPole(x.label) === _normPole(d) || _normPole(x.folderName) === _normPole(d));
    if (byLabel) ids.add(byLabel.id);
  }
  return [...ids];
};

const _isEnioAdmin = session => session?.role === "admin" || session?.role === "superadmin";

// Table view → périmètre cible, ses préfixes et accessScopeForView : dans
// src/accesVues.js depuis le 14/10/2026 (le menu y lit aussi le cadenas des
// pages réservées aux administrateurs, buildNavModules).

// Décision d'accès cible pour une vue → { allowed, scope, reason }.
const evaluateViewAccess = (view, session, delegates) => {
  const scope = accessScopeForView(view);
  if (scope === "transverse") return { allowed: true, scope, reason: "transverse" };
  if (!session) return { allowed: false, scope, reason: "non connecté" };
  // Compte invité (lecture seule — mutuelles candidates) : uniquement les vues
  // transverses, déjà accordées ci-dessus. Tout le reste est fermé.
  if (session.role === "invite") return { allowed: false, scope, reason: "compte invité — consultation seule" };
  if (_isEnioAdmin(session)) return { allowed: true, scope, reason: "admin Enio" };
  const me = (Array.isArray(delegates) ? delegates.find(d => d.id === session.id) : null) || session;
  const roles = Array.isArray(me.roles) ? me.roles : [];
  if (scope === "admin") return { allowed: false, scope, reason: "réservé admin Enio" };
  if (scope === "hab:RH") {
    const ok = peutAccederRH(me);
    return { allowed: ok, scope, reason: ok ? "habilité RH" : "non habilité RH" };
  }
  if (scope === "hab:DPO") {
    // Le DG de la mutuelle lit Calypso (décision de Pierre du 14/10/2026,
    // ouverte avec le serveur le 21/10/2026) : calypsoDonnees décide de tout.
    const ok = roles.includes("DPO") || roles.includes("DPO_ADJOINT") || roles.includes("DG");
    return { allowed: ok, scope, reason: ok ? "habilité DPO" : "non habilité DPO" };
  }
  if (scope === "hab:EC") {
    const ok = roles.includes("EC");
    return { allowed: ok, scope, reason: ok ? "expert-comptable" : "non habilité comptabilité" };
  }
  if (scope.startsWith("dom:")) {
    const domId = scope.slice(4);
    const ok = memberDomaineIds(session, delegates).includes(domId);
    return { allowed: ok, scope, reason: ok ? ("membre domaine "+domId) : ("hors domaine "+domId) };
  }
  return { allowed: true, scope, reason: "défaut" };
};

// Dédoublonnage des observations (id|view) sur la durée de vie de l'onglet.
const _accessObserved = new Set();


const navCanAccessHygie = (session, delegates, isAdmin) => {
  if (!session) return false;
  if (isAdmin) return true;
  const me = (Array.isArray(delegates) ? delegates.find(d => d.id === session.id) : null) || session;
  const roles = me.roles || [];
  // Le DG lit Calypso (21/10/2026) ; le serveur (calypsoDonnees) décide de tout.
  return roles.includes("DPO") || roles.includes("DPO_ADJOINT") || roles.includes("DG");
};
const navCanAccessGelPpe = (session, isAdmin) => {
  if (!session) return false;
  if (isAdmin) return true;
  const roles = Array.isArray(session.roles) ? session.roles : [];
  if (roles.includes("DG") || roles.includes("CD")) return true;
  const polesPilotage = new Set(["actuariat et pilotage de la donnee","conformite juridique et protection des donnees","gestion des risques controle interne et audit interne","administration comptabilite finance"]);
  return (session.polesInteret || []).some(p => polesPilotage.has(_normPole(p)));
};
// ============================================================================
//  ADMIN
// ============================================================================
// ============================================================================
//  SUPER-ADMIN PANEL (listes membres + comptes admin + config)
// ============================================================================


// ── Panel demandes de création de compte (par les dirigeants) ───────────────
// Lit la collection accountCreationRequests (créée via le bouton dans le
// Cockpit dirigeant). Sur "Valider", crée le doc delegates/{id} avec les
// champs proposés et envoie l'invitation Aramis (envoyerInvitationAramis).
const DemandesCreationComptePanel = ({ aramisSession, saveDelegate, rendu }) => {
  const [demandes, setDemandes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [msg, setMsg] = useState("");
  const confirm = useConfirm();

  useEffect(() => {
    let unsub;
    (async () => {
      const { collection, onSnapshot } = await import("firebase/firestore");
      const { db } = await import("./firebase");
      unsub = onSnapshot(collection(db, "accountCreationRequests"), (snap) => {
        const data = snap.docs.map(d => ({ id: d.id, ...d.data() }))
          .sort((a, b) => (b.ts || 0) - (a.ts || 0));
        setDemandes(data); setLoading(false);
      }, () => setLoading(false));
    })();
    return () => { if (unsub) unsub(); };
  }, []);

  const traiter = async (demandeAffichee, decision) => {
    let d = demandeAffichee;
    const { doc:fd, setDoc:fs, addDoc:ad, collection:fc } = await import("firebase/firestore");
    const { db } = await import("./firebase");
    const userLabel = ((aramisSession?.prenom || "") + " " + (aramisSession?.nom || "")).trim() || "Admin";
    setMsg("");
    try {
      // 0. Anti-doublon, AVANT toute écriture (relecture FB10a, 30/09/2026) :
      // une demande en attente se réécrit depuis n'importe quel navigateur, et
      // une adresse déjà portée (e-mail ou alias d'une fiche, même supprimée :
      // elle se restaure) donnerait deux fiches à la même clé de connexion et
      // d'habilitation. Adresses comparées normalisées (src/doublonAdresse.js).
      if (decision === "validee") {
        const { getDocs: gd, getDoc: gdoc } = await import("firebase/firestore");
        // La demande est RELUE au clic (relecture 4, 30/09/2026) : elle se
        // crée et se réécrit sans compte, et l'écran peut en montrer une
        // version plus ancienne. Rôles, pôles et mutuelle écrits sur la fiche
        // sont ceux relus ici, montrés avant de confirmer.
        const relue = await gdoc(fd(db, "accountCreationRequests", d.id));
        if (!relue.exists()) { setMsg("Demande introuvable : rien n'est créé."); return; }
        d = { ...(relue.data() || {}), id: d.id };
        if (d.statut !== "en_attente") { setMsg("Cette demande a déjà été traitée : rien n'est créé."); return; }
        const fiches = await gd(fc(db, "delegates"));
        const motif = motifDoublon(fiches.docs.map((s) => ({ id: s.id, ...(s.data() || {}) })), d.email);
        if (motif) { setMsg(motif); return; }
        const rolesDemandes = Array.isArray(d.roles) ? d.roles.filter((r) => typeof r === "string") : [];
        const polesDemandes = Array.isArray(d.polesInteret) ? d.polesInteret.filter((p) => typeof p === "string") : [];
        const accord = await confirm({
          title: "Créer ce compte ?",
          message: [
            `Adresse : ${normAdresse(d.email)}`,
            `Nom : ${`${d.prenom || ""} ${d.nom || ""}`.trim() || "non renseigné"}`,
            `Mutuelle : ${d.mutuelleName || "aucune"}`,
            `Rôles : ${rolesDemandes.join(", ") || "aucun"}`,
            `Pôles : ${polesDemandes.join(", ") || "aucun"}`,
            `Demandée par : ${d.demandeurName || "inconnu"}`,
            "",
            "Valeurs relues à l'instant : une demande peut être modifiée depuis n'importe quel navigateur. Vérifiez-les avant de créer la fiche.",
          ].join("\n"),
          confirmLabel: "Créer le compte", danger: rolesDemandes.length > 0,
        });
        if (accord !== true) return;
      }
      // 1. Update demande
      await fs(fd(db, "accountCreationRequests", d.id), {
        ...d, statut: decision,
        tsMaj: Date.now(),
        decidePar: userLabel,
        decideParId: aramisSession?.id || null,
      });

      // 2. Si validée : crée le delegate + envoie l'invitation
      let compteCree = null;   // la fiche créée : la cloche du demandeur y mène (26/10/2026)
      if (decision === "validee") {
        // Vérifier qu'aucun délégué n'existe déjà avec cet email (anti-doublon)
        const emailLow = normAdresse(d.email);
        const dupId = `del_${Date.now()}_${Math.random().toString(36).slice(2, 7)}`;
        if (saveDelegate) {
          await saveDelegate({
            id: dupId,
            prenom: d.prenom || "",
            nom: d.nom || "",
            email: emailLow,
            mutuelle: d.mutuelleName || "",
            fonction: d.fonction || "",
            roles: Array.isArray(d.roles) ? d.roles.filter((r) => typeof r === "string") : [],
            polesInteret: Array.isArray(d.polesInteret) ? d.polesInteret.filter((p) => typeof p === "string") : [],
            actif: true, supprime: false,
            dateCreation: Date.now(),
            createdBy: { type: "request_validation", demandeurId: d.demandeurId, demandeurName: d.demandeurName },
          });
          compteCree = dupId;
        }
        // Tente d'envoyer l'invitation Aramis (best-effort)
        try {
          const { getFunctions, httpsCallable } = await import("firebase/functions");
          const fn = getFunctions(undefined, REGION_FRANCFORT);
          await httpsCallable(fn, "envoyerInvitationAramis")({
            aramisUserId: aramisSession?.id,
            email: emailLow,
            prenom: d.prenom || "",
            nom: d.nom || "",
            mutuelle: d.mutuelleName || "",
          });
        } catch (e) { console.warn("[invitation] non envoyée:", e?.message); }
      }

      // 3. Notif au demandeur
      try {
        if (d.demandeurId) {
          await ad(fc(db, "notifications"), {
            type: "account_creation_decision",
            titre: decision === "validee"
              ? `Compte créé pour ${d.prenom} ${d.nom}`
              : `Demande de compte refusée — ${d.prenom} ${d.nom}`,
            message: decision === "validee"
              ? `${userLabel} a validé votre demande. ${d.prenom} ${d.nom} a reçu son invitation pour activer son compte Aramis.`
              : `${userLabel} a refusé votre demande de création de compte pour ${d.prenom} ${d.nom}.`,
            delegateId: d.demandeurId,
            ...(compteCree ? { compteDelegateId: compteCree } : {}),
            ts: Date.now(), read: false,
          });
        }
      } catch { /* notif best-effort */ }
      setMsg(decision === "validee" ? "Compte créé, invitation envoyée." : "Demande refusée.");
      setTimeout(() => setMsg(""), 3000);
    } catch (e) {
      setMsg("Erreur : " + (e.message || ""));
    }
  };

  // L'écran vit dans un morceau paresseux (DemandesCompteVue.js, lot L11i),
  // rendu par la colonne de contenu (`rendu`, visible du préchargement) : la
  // logique ci-dessus reste ici, ses verrous lisent App.jsx.
  return rendu({ demandes, loading, msg, onTraiter: traiter });
};



// ============================================================================
//  RESULTATS
// ============================================================================
const Results = ({appState, onNav, aramisSession, delegates=[]}) => {
  const {session,participants,agenda,votesByScrutin,history}=appState;
  // « Résultats publiés : CA du 24 septembre » (centre de notifications) :
  // navTo("results", { sessionId }) amène à la séance archivée nommée.
  const [seanceVisee, setSeanceVisee] = useState(() => (consumeNavParams() || {}).sessionId || null);
  // En direct : un second lien vers la vue ouverte défile jusqu'à SA séance,
  // même déjà visée (suivi) ; la page ne porte aucune saisie.
  const [suiviSeance, setSuiviSeance] = useState(0);
  // Bandeau de commande (grammaire du 27/10/2026) : En direct / Archivées est
  // un sélecteur segmenté ; un lien vers une séance archivée ouvre « Archivées ».
  const [vueResultats, setVueResultats] = useState(() => (seanceVisee ? "archives" : "direct"));
  useParametresVue((p) => { if (p.sessionId) { setSeanceVisee(String(p.sessionId)); setSuiviSeance((k) => k + 1); setVueResultats("archives"); } });
  const nbArchives = (history||[]).length;
  useEffect(() => {
    if (!seanceVisee) return undefined;
    const t = setTimeout(() => {
      const el = document.getElementById("resultats-" + seanceVisee);
      if (el) el.scrollIntoView({ behavior: mouvementReduit() ? "auto" : "smooth", block: "start" });
    }, 300);
    return () => clearTimeout(t);
  }, [seanceVisee, nbArchives, suiviSeance]);
  // Vérifier que l'utilisateur est connecté (votant ou admin)
  const isParticipantConnected = (()=>{try{const s=localStorage.getItem("eniovote_participant");if(!s)return false;const {sessionId,participantId}=JSON.parse(s);return sessionId===session?.id&&!!participantId;}catch{return false;}})();
  // L'administrateur est reconnu par le rôle de sa SESSION Aramis. Avant l'audit
  // du 28/09/2026, seule comptait la clé `eniovote_admin` de localStorage, que
  // tout visiteur pouvait écrire ; elle n'accorde plus rien (appShared.js).
  const isAdminConnected = estSessionAdmin(aramisSession);
  // Un membre connecté à Aramis lit les résultats PUBLIÉS des séances où il
  // était participant : c'est le public de « Résultats publiés » (Admin.js).
  // Le direct reste au votant émargé et à l'administrateur (src/ciblesLiens.js).
  const moi = aramisSession ? { ...((delegates||[]).find(d=>d.id===aramisSession.id)||{}), ...aramisSession } : null;
  const lecture = lectureResultats({ history, moi, admin: isAdminConnected, votantConnecte: isParticipantConnected });
  const viseeIntrouvable = !!seanceVisee && !lecture.archives.some(s=>s.id===seanceVisee);
  if(!lecture.acces){return(
    <div style={{display:"flex",alignItems:"center",justifyContent:"center",minHeight:"calc(var(--vh100) * 0.7)",padding:24}}>
      <div className="card fi" style={{maxWidth:400,width:"100%",textAlign:"center"}}>
        <div style={{fontSize:"var(--fs-display)",marginBottom:12}}><IconeTexte nom="lock" titre="Verrouillé"/></div>
        <div style={{fontWeight:700,fontSize:"var(--fs-corps)",marginBottom:8}}>Accès réservé</div>
        <div style={{fontSize:"var(--fs-meta)",color:"var(--text-sub)",marginBottom:20}}>Vous devez être connecté en tant que participant ou administrateur pour accéder aux résultats.</div>
        <button className="btn btn-teal" onClick={()=>onNav&&onNav("participant")}>Se connecter</button>
      </div>
    </div>
  );}
  const actifs=participants.filter(isActif);
  const quorum=actifs.length;
  const instCfg=getInstConfig(session);
  const nbOuverts=agenda.filter(s=>s.status==="open").length;
  // En direct, les archives passent par le segment « Archivées » (quand il y
  // en a) ; sans le direct, la page ne montre que les archives (ciblesLiens.js).
  const {titre:titreResultats,segments,options:optionsAffichage,montrerDirect,montrerArchives}=affichageResultats({lecture,vue:vueResultats,nbOuverts});
  return(
    <div className="fi page-largeur">
      <EnTetePage module="gouv" titre={titreResultats} icone="chart-bar" bandeau
        etat={lecture.direct ? (session ? `${session.name}${instCfg.label?` · ${instCfg.label}`:""}` : null) : "Les séances auxquelles vous avez participé."}
        chiffres={lecture.direct ? [
          nbOuverts>0&&{libelle:nbOuverts>1?`${nbOuverts} votes en cours`:"Vote en cours",attente:true},
          session&&{valeur:actifs.length,libelle:"actifs"},
        ] : null}/>
      {/* La barre de la liste, en tête des résultats (R7). */}
      {segments&&(
        <Suspense fallback={null}>
          <BarreResultatsRaw valeur={vueResultats} onChange={setVueResultats} options={optionsAffichage}/>
        </Suspense>
      )}
      {viseeIntrouvable&&<div className="card" role="status" style={{marginBottom:18,fontSize:"var(--fs-meta)",color:"var(--text-sub)"}}>Les résultats de cette séance ne sont plus publiés.</div>}
      {montrerDirect&&<>
      {agenda.filter(s=>s.status==="open").map(s=>{const vs=votesByScrutin[s.id]||[];return(
        <div key={s.id} className="card fi" style={{marginBottom:18,borderColor:C.teal,borderWidth:2}}>
          <div style={{display:"flex",alignItems:"center",gap:7,marginBottom:10}}><span className="pulse" style={{color:"var(--tx-teal)"}}>●</span><span style={{fontSize:"var(--fs-micro)",color:"var(--tx-teal)",fontWeight:700}}>Vote en cours</span></div>
          <div style={{fontWeight:700,fontSize:"var(--fs-corps)",marginBottom:8}}>{s.question}</div>
          <div style={{display:"flex",gap:7,flexWrap:"wrap",marginBottom:12}}><VoteBadge secret={s.secret}/><ResultsBadge published={s.resultsPublished}/></div>
          <div style={{marginBottom:14}}><div style={{fontSize:"var(--fs-micro)",color:navTexte(C.gray),fontWeight:700,marginBottom:5}}>Participation · {vs.length} / {quorum}</div><Prog value={vs.length} max={quorum||1} color={C.teal}/></div>
          <ScrutinResults s={s} vs={vs} quorum={quorum} isAdmin={false}/>
        </div>
      );})}
      {agenda.filter(s=>s.status==="open").length===0&&<div className="card" style={{textAlign:"center",padding:40,marginBottom:18}}><div style={{fontSize:"var(--fs-display)",marginBottom:12}}><IconeTexte nom="chart-bar" titre="Graphique"/></div><div style={{fontWeight:700,fontSize:"var(--fs-nav)",color:navTexte(C.gray)}}>Aucun scrutin en cours</div></div>}
      {agenda.filter(s=>s.status==="closed").length>0&&<>
        <div style={{fontWeight:700,fontSize:"var(--fs-meta)",color:"var(--tx-teal)",marginBottom:12}}>Scrutins terminés</div>
        {agenda.filter(s=>s.status==="closed").map(s=>{const vs=votesByScrutin[s.id]||[];return(
          <div key={s.id} className="card" style={{marginBottom:14}}>
            <div style={{fontWeight:700,fontSize:"var(--fs-nav)",marginBottom:7}}>{s.question}</div>
            <div style={{display:"flex",gap:7,flexWrap:"wrap",marginBottom:10}}><VoteBadge secret={s.secret}/><ResultsBadge published={s.resultsPublished}/>{s.closedAt&&<span style={{fontSize:"var(--fs-micro)",color:navTexte(C.gray),alignSelf:"center"}}>Clos {s.closedAt}</span>}</div>
            <div style={{marginBottom:12}}><Prog value={vs.length} max={quorum||1} color={C.teal}/></div>
            <ScrutinResults s={s} vs={vs} quorum={quorum} isAdmin={false}/>
          </div>
        );})}
      </>}
      </>}
      {!lecture.direct&&lecture.archives.length===0&&!viseeIntrouvable&&<div className="card" style={{textAlign:"center",padding:40,marginBottom:18}}><div style={{fontWeight:700,fontSize:"var(--fs-nav)",color:navTexte(C.gray)}}>Aucun résultat publié pour vos séances</div></div>}
      {montrerArchives&&lecture.archives.length>0&&<>
        <div style={{fontWeight:700,fontSize:"var(--fs-meta)",color:navTexte(C.gray),marginBottom:12,marginTop:8}}>Sessions archivées</div>
        {lecture.archives.map((sess,si)=>{const sActifs=(sess.participants||[]).filter(isActif);const sInstCfg=getInstConfig(sess);return(
          <div key={sess.id||si} id={sess.id?"resultats-"+sess.id:undefined} className="card" style={{marginBottom:14,scrollMarginTop:"calc(var(--app-header-h, 56px) + 12px)"}}>
            <div style={{display:"flex",alignItems:"center",gap:8,marginBottom:3}}><div style={{fontWeight:700,fontSize:"var(--fs-meta)"}}>{sess.name}</div><InstanceBadge type={sess.instanceType} label={sInstCfg.label}/></div>
            <div style={{fontSize:"var(--fs-micro)",color:navTexte(C.gray),marginBottom:12}}>{sess.date} · {sActifs.length} actifs</div>
            {(sess.agenda||[]).map(s=>{const vs=(sess.votesByScrutin||{})[s.id]||[];return(
              <div key={s.id} style={{marginBottom:12}}>
                <div style={{fontWeight:700,fontSize:"var(--fs-micro)",marginBottom:6}}>{s.question}</div>
                <div style={{display:"flex",gap:6,marginBottom:10,flexWrap:"wrap"}}><VoteBadge secret={s.secret}/><ResultsBadge published={s.resultsPublished}/></div>
                <ScrutinResults s={{...s,resultsPublished:true}} vs={vs} quorum={sActifs.length} isAdmin={false}/>
                <hr className="sep"/>
              </div>
            );})}
          </div>
        );})}
      </>}
    </div>
  );
};

// ============================================================================
//  DOCUMENT LIBRARY — Bibliothèque SharePoint (parcours sans couture)
//  Gère authentification MS, listing, upload, download, delete, create folder.
//  Utilisable n'importe où en passant un `subPath` (projet/X, gouvernance/Y, …).
// ============================================================================
// ── Popup fiche contact réutilisable avec édition admin ──────────────────────

// DelegateContactPopup vit désormais dans src/DelegateContactPopup.js
// (chargé à la demande, voir en tête) : Admin.js en a besoin aussi et ne peut pas importer App.


export default function App() {
  useEffect(()=>{
    // Exposer les composants internes Admin et ThemisView pour les "slots" de ThemisInstances
    // (évite les imports circulaires tout en permettant la délégation visuelle)
    if (typeof window !== "undefined") {
      window.__ThemisAdminComponent = AdminLazy;
      window.__ThemisViewComponent = ThemisViewLazy;
    }
  },[]);
  useEffect(()=>{
    injectFont();injectCSS();applyFontSize(getFontSize());applyTheme(themeEnVigueur());
    // Publie html[data-coquille="tel|tablette|bureau"] et le tient à jour. C'est
    // lui qui permettra au CSS de distinguer le téléphone SANS media query de
    // largeur — la seule sonde qui ne mente pas quand Safari élargit la page.
    installerClasseCoquille();
    // Réglages de « Moi » (animations, transparence, sons) posés sur <html>,
    // et suivi du thème du système quand le réglage vaut « Système ». Sons
    // actifs : le moteur écoute les gestes dès maintenant (audio d'iOS).
    appliquerPreferences();
    prechargerSons();
    suivreThemeSysteme();
    // Meta PWA dynamiques
    const setMeta=(n,v)=>{let m=document.querySelector('meta[name="'+n+'"]');if(!m){m=document.createElement('meta');m.name=n;document.head.appendChild(m);}m.content=v;};
    setMeta("theme-color","#003F59");
    setMeta("apple-mobile-web-app-capable","yes");
    setMeta("apple-mobile-web-app-status-bar-style","black-translucent");
    setMeta("apple-mobile-web-app-title","Aramis");
    document.title="Enio - Aramis";

    // Nettoyage d'éventuelles clés MSAL laissées par une ancienne version
    // (avant le passage au proxy backend graphProxy). Non bloquant.
    try {
      sessionStorage.removeItem("aramis_msal_pending_hash");
      sessionStorage.removeItem("aramis_msal_pending_search");
      sessionStorage.removeItem("aramis_msal_pending_ts");
    } catch {}
  },[]);
  // ── Recherche globale ⌘K / Ctrl+K ────────────────────────────────────────
  const [globalSearchOpen, setGlobalSearchOpen] = useState(false);
  // Feuille « Modules » de la barre basse : la mosaïque, en plein écran.
  const [feuilleModules, setFeuilleModules] = useState(false);
  // Raccourcis ⌥1 à ⌥9 (lot L6) : les modules visibles, dans l'ordre du rail,
  // et navTo, tenus à jour à chaque rendu (l'écouteur clavier est posé une fois).
  const modulesRaccourcisRef = useRef([]);
  const navToRef = useRef(null);
  // Dernier module reellement visite : sert de repli a l'onglet contextuel
  // de la barre basse quand la vue courante n'appartient a aucun module
  // (l'accueil, typiquement). Voir le calcul de moduleOnglet plus bas.
  const moduleRecentRef = useRef(null);
  // "tel" | "tablette" | "bureau" — lu sur le pointeur et l'écran physique.
  const { estTel, estTablette, estBureau } = useCoquille();
  // Coquille (lot L6, synthèse §6.2) : iPad en paysage d'au moins 1 024 px =
  // rail complet, libellés compris ; bureau de moins de 1 024 px = rail en
  // icônes seules, pages du module par le bouton « Sommaire ».
  const tabletteLarge = useMediaQuery("(orientation: landscape) and (min-width: 1024px)");
  const bureauEtroit = useMediaQuery("(max-width: 1023px)");
  // « Moi » (feuille au doigt, panneau au bureau) : l'avatar de l'en-tête, le
  // pied du rail et l'onglet « Moi » l'ouvrent.
  const [moiOuvert, setMoiOuvert] = useState(false);
  // Barre d'onglets : téléphone et iPad en portrait (synthèse §6.2, §6.3).
  const avecOnglets = estTel || (estTablette && !tabletteLarge);
  // Messagerie (décision n° 8) : ouverte par l'en-tête, « Moi » et la palette ;
  // son compteur de non-lus remonte à l'en-tête et à l'onglet « Moi ».
  const [chatOuvert, setChatOuvert] = useState(false);
  const [messagesNonLus, setMessagesNonLus] = useState(0);
  // Socrate (11/10/2026) : même pilotage que la messagerie. Le bouton « S » de
  // l'en-tête n'apparaît qu'une fois la porte serveur ouverte (`disponible`,
  // remonté par le composant, seul à lire le statut). Les deux panneaux
  // s'ouvrent au même endroit, sous l'en-tête : ouvrir l'un replie l'autre,
  // sans rien perdre (la conversation de Socrate reste, comme le fil ouvert).
  const [socrateOuvert, setSocrateOuvert] = useState(false);
  const [socrateDisponible, setSocrateDisponible] = useState(false);
  useEffect(() => { if (chatOuvert) setSocrateOuvert(false); }, [chatOuvert]);
  useEffect(() => { if (socrateOuvert) setChatOuvert(false); }, [socrateOuvert]);
  useEffect(() => { if (!socrateDisponible) setSocrateOuvert(false); }, [socrateDisponible]);
  const [feuilleModulesMontee, setFeuilleModulesMontee] = useState(false);
  useEffect(() => { if (feuilleModules) setFeuilleModulesMontee(true); }, [feuilleModules]);
  // Animation de déverrouillage post-login : null | {prenom, demo?}. La session
  // est écrite par loginAramis AVANT le lancement ; seul le navTo est différé.
  const [unlockAnim, setUnlockAnim] = useState(null);
  const loginAvecAnimation = (d) => { loginAramis(d); setUnlockAnim({ prenom: d?.prenom || "" }); };
  const finishUnlock = () => {
    const wasDemo = unlockAnim?.demo;
    const demoObj = unlockAnim;
    setUnlockAnim(null);
    // Lien de convocation ouvert AVANT connexion (?view=themis-documents-seance
    // &sid=…) : on y atterrit maintenant plutôt que sur « Ma semaine ». Sans ce
    // rattrapage, le délégué qui clique le lien de sa convocation perd sa
    // destination pendant le login.
    if (!wasDemo) {
      let _docsPending = "";
      try { _docsPending = sessionStorage.getItem("aramis_docs_seance_pending") || ""; } catch { /* storage indispo */ }
      if (_docsPending) {
        try { sessionStorage.removeItem("aramis_docs_seance_pending"); } catch { /* storage indispo */ }
        setDocsSeanceId(_docsPending);
        navTo("themis-documents-seance", { sid: _docsPending });
        return;
      }
      // Album de la Médiathèque ouvert avant connexion (lien de l'annonce
      // « Les photos … sont disponibles sur Aramis », 12/10/2026) : on l'ouvre
      // maintenant ; le serveur revérifie le droit (peutVoirAlbum).
      let _albumPending = "";
      try { _albumPending = sessionStorage.getItem("aramis_media_album_pending") || ""; } catch { /* storage indispo */ }
      if (_albumPending) {
        try { sessionStorage.removeItem("aramis_media_album_pending"); } catch { /* storage indispo */ }
        if (/^[A-Za-z0-9]{6,40}$/.test(_albumPending)) { navTo("mediatheque-album", { album: _albumPending }); return; }
      }
    }
    // Expert-comptable externe (rôle EC exclusif) : atterrit directement sur son portail comptable.
    const _r = Array.isArray(aramisSession?.roles) ? aramisSession.roles : [];
    if (aramisSession && aramisSession.role !== "admin" && aramisSession.role !== "superadmin" && _r.length === 1 && _r[0] === "EC") { navTo("compta-ec"); return; }
    if (!wasDemo) { navTo("ma-semaine"); return; }
    // Mode démo (?unlockdemo=1) : rejoue en boucle pour pouvoir l'observer.
    setTimeout(() => setUnlockAnim(demoObj), 900);
  };
  // Aperçu sans connexion : /?unlockdemo=1 (purement visuel, joue en boucle ralentie).
  useEffect(() => {
    try {
      if (new URLSearchParams(window.location.search).get("unlockdemo") === "1") {
        setUnlockAnim({ prenom: "Pierre", demo: true });
      }
    } catch {}
  }, []);
  useEffect(() => {
    const handler = (e) => {
      if (e.defaultPrevented || e.isComposing) return;
      // Les feuilles « Moi » et « Modules » rendent #root inerte : ⌘K et ⌥1 à
      // ⌥9 les ferment d'abord, sinon la palette s'ouvrirait inutilisable et la
      // navigation passerait derrière la feuille (relecture L6).
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "k") {
        e.preventDefault();
        setMoiOuvert(false); setFeuilleModules(false);
        setGlobalSearchOpen(v => !v);
        return;
      }
      // ⌥1 à ⌥9 : ouvrir le n-ième module, au clavier donc sans animation
      // (jamais pendant une frappe dans un champ, sauf dans la palette).
      const dansPalette = !!(e.target && e.target.closest && e.target.closest(".cq-palette"));
      const i = raccourciModule(e, { dansPalette });
      const m = i >= 0 ? modulesRaccourcisRef.current[i] : null;
      if (m && navToRef.current) {
        e.preventDefault();
        setGlobalSearchOpen(false); setMoiOuvert(false); setFeuilleModules(false);
        navToRef.current(routePortail(m), null, { coquille: true, clavier: true });
      }
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, []);

  // ── PWA Install prompt ────────────────────────────────────────────────────
  // Chrome / Edge / Firefox → événement `beforeinstallprompt` natif, clic installe en 1 geste.
  // Safari macOS / iOS → n'émet pas cet event, l'installation se fait manuellement
  //   (macOS : Fichier → Ajouter au Dock / iOS : Partager → Sur l'écran d'accueil).
  //   On détecte Safari pour afficher un bandeau spécifique avec les instructions.
  const [installPrompt, setInstallPrompt] = useState(null);
  const [showInstallBanner, setShowInstallBanner] = useState(false);
  const [safariInstallHint, setSafariInstallHint] = useState(false);
  // Une surface d'interruption à la fois (lot L6) : l'installation passe APRÈS
  // la session à renouveler, la bienvenue et la clé d'accès
  // (src/fileInterruptions.js). La fenêtre « Enio a besoin de pouvoir vous
  // envoyer des notifications » est retirée le 30/09/2026 : elle promettait
  // « votes, réunions, demandes d'aide » alors qu'aucun message n'était envoyé.
  // La permission sera demandée après un premier acte, avec le lot serveur.
  const tourInstallation = useTourInterruption("installation", showInstallBanner || safariInstallHint);

  useEffect(() => {
    const handler = (e) => { e.preventDefault(); setInstallPrompt(e); setShowInstallBanner(true); };
    window.addEventListener("beforeinstallprompt", handler);

    // Détecte Safari (macOS + iOS) + pas déjà installée en standalone + pas déjà dismissed
    const ua = navigator.userAgent;
    const isSafari = /^((?!chrome|android).)*safari/i.test(ua);
    const isIOS = /iPad|iPhone|iPod/.test(ua);
    const isStandalone = window.matchMedia("(display-mode: standalone)").matches
      || window.navigator.standalone === true;
    const dismissed = localStorage.getItem("aramis_install_safari_dismissed") === "1";

    // Safari : au TROISIÈME lancement seulement (synthèse §6.6) ; le compteur
    // avance une fois par session d'onglet.
    let lancements = 0;
    try {
      lancements = Number(localStorage.getItem("aramis_lancements") || 0);
      if (!sessionStorage.getItem("aramis_lancement_compte")) {
        lancements += 1;
        localStorage.setItem("aramis_lancements", String(lancements));
        sessionStorage.setItem("aramis_lancement_compte", "1");
      }
    } catch { /* stockage indisponible */ }
    if ((isSafari || isIOS) && !isStandalone && !dismissed && lancements >= 3) {
      // Laisse 5s au cas où un browser basé Chromium déguisé en Safari émet quand même l'event
      const t = setTimeout(() => setSafariInstallHint(true), 5000);
      return () => {
        clearTimeout(t);
        window.removeEventListener("beforeinstallprompt", handler);
      };
    }
    return () => window.removeEventListener("beforeinstallprompt", handler);
  }, []);

  const handleInstall = async () => {
    if (!installPrompt) return;
    installPrompt.prompt();
    const { outcome } = await installPrompt.userChoice;
    if (outcome === "accepted") setShowInstallBanner(false);
    setInstallPrompt(null);
  };

  const dismissSafariHint = () => {
    setSafariInstallHint(false);
    try { localStorage.setItem("aramis_install_safari_dismissed", "1"); } catch { /* ignore */ }
  };

  // Deep-link : ?view=athos-reunions&doodle=1 (depuis les emails de sondage)
  const _deepLinkParams = new URLSearchParams(window.location.search);
  const _deepView = _deepLinkParams.get("view");
  const [doodleAutoOpen] = useState(()=>_deepLinkParams.get("doodle")==="1");
  // Test MSAL par navigateur : ?msal=1 active la connexion Microsoft par MSAL
  // (SSO silencieux) sur CE poste seulement, ?msal=0 la retire — cf. msal.js.
  try {
    const _m=_deepLinkParams.get("msal");
    if(_m==="1") localStorage.setItem("aramis_sso_msal","1");
    else if(_m==="0") localStorage.removeItem("aramis_sso_msal");
  } catch {}

  // Lien magique de vote : ?view=participant&sid=<séance>&pid=<participant>.
  // Envoyé par e-mail aux participants → accès direct à l'espace de vote
  // (émargement + vote) SANS connexion Aramis. On le mémorise dans
  // sessionStorage pour qu'il survive aux navigations / réécritures d'URL.
  // Le QR code de la séance (urlAccesSeance, 18/10/2026) porte la séance
  // SEULE (`sid`, sans `pid`) : même porte, rien de prérempli ; un membre
  // connecté y arrive aussi, au lieu de « Ma semaine ».
  const [magicVote] = useState(()=>{
    if(_deepView==="participant" && (_deepLinkParams.get("pid") || _deepLinkParams.get("sid"))){
      const mv={ sid:_deepLinkParams.get("sid")||"", pid:_deepLinkParams.get("pid")||"", t:_deepLinkParams.get("t")||"", _ts:Date.now() };
      try{ sessionStorage.setItem("eniovote_magic_vote", JSON.stringify(mv)); }catch{}
      return mv;
    }
    try{ return JSON.parse(sessionStorage.getItem("eniovote_magic_vote")||"null"); }catch{ return null; }
  });

  // Documents préparatoires d'une séance : ?view=themis-documents-seance&sid=<séance>.
  // Lien porté par la convocation. Contrairement au lien magique de vote, il
  // EXIGE une connexion Aramis : c'est justement le passage par Aramis qui
  // permet de servir les pièces SharePoint à des délégués qui n'ont aucun
  // compte Microsoft. On mémorise donc la cible pour y atterrir APRÈS le
  // login — sinon le délégué se retrouvait sur « Ma semaine », le lien reçu
  // par e-mail perdu en route.
  const [docsSeanceId,setDocsSeanceId]=useState(()=>{
    if(_deepView==="themis-documents-seance" && _deepLinkParams.get("sid")){
      const sid=_deepLinkParams.get("sid");
      try{ sessionStorage.setItem("aramis_docs_seance_pending", sid); }catch{}
      return sid;
    }
    try{ return sessionStorage.getItem("aramis_docs_seance_pending")||""; }catch{ return ""; }
  });
  // Album de la Médiathèque : ?view=mediatheque-album&album=<id>, lien de
  // l'annonce par courriel (12/10/2026). Sans session, la page de garde
  // l'aurait perdu : il est mémorisé ici, rouvert après la connexion
  // (finishUnlock), purgé à l'arrivée sur la vue et à la déconnexion, comme
  // les documents de séance.
  useState(()=>{
    const album=_deepView==="mediatheque-album"?_deepLinkParams.get("album"):null;
    if(album && /^[A-Za-z0-9]{6,40}$/.test(album)){
      try{ sessionStorage.setItem("aramis_media_album_pending", album); }catch{ /* storage indispo */ }
    }
    return null;
  });

  // Confirmation de présence Thémis : ?view=themis-presence&t=<token>&p=<statut>.
  // Lien des boutons du mail de convocation — AUCUN compte requis (token opaque
  // vérifié par la CF). Capturé au boot car navTo réécrit l'URL ensuite.
  const [presenceLink]=useState(()=>{
    if(_deepView==="themis-presence" && _deepLinkParams.get("t")){
      return { t:_deepLinkParams.get("t"), p:_deepLinkParams.get("p")||"" };
    }
    return null;
  });

  // Pouvoirs (procurations) : ?view=themis-pouvoir&t=<token> pour donner ou
  // révoquer, ?view=themis-pouvoir-confirmation&t=<token>&r=accepte|refuse
  // pour confirmer un pouvoir saisi par un tiers. Même principe que la
  // confirmation de présence : jeton opaque, aucun compte requis.
  const [pouvoirLink]=useState(()=>{
    if((_deepView==="themis-pouvoir"||_deepView==="themis-pouvoir-confirmation") && _deepLinkParams.get("t")){
      return { t:_deepLinkParams.get("t"), r:_deepLinkParams.get("r")||"",
        mode:_deepView==="themis-pouvoir-confirmation"?"confirmation":"donner" };
    }
    return null;
  });

  // Émargement signé à distance : ?view=themis-emargement&t=<token> (mail
  // « Merci d'avoir émargé ! Veuillez signer »). Jeton dédié, aucun compte.
  const [emargementLink]=useState(()=>{
    if(_deepView==="themis-emargement" && _deepLinkParams.get("t")) return { t:_deepLinkParams.get("t") };
    return null;
  });

  // Repas d'une séance : ?view=themis-repas&t=<token> (jeton dédié, aucun compte).
  const [repasLink]=useState(()=>{
    if(_deepView==="themis-repas" && _deepLinkParams.get("t")) return { t:_deepLinkParams.get("t") };
    return null;
  });

  const [view,setView]=useState(()=>{
    // Aperçu des surfaces mobiles, hors production : il doit s'ouvrir SANS
    // session, c'est toute sa raison d'être (cf. src/ApercuMobile.js).
    if(process.env.NODE_ENV !== "production" && _deepView === "__apercu") return "__apercu";
    // Lien magique de vote → on ouvre directement l'espace de vote.
    if(_deepView==="participant" && magicVote) return "participant";
    // Lien de confirmation de présence → page publique, session ou pas
    // (sinon le destinataire sans session Aramis retombait sur la page de
    // garde et sa réponse ne partait jamais).
    if(_deepView==="themis-presence" && presenceLink) return "themis-presence";
    if((_deepView==="themis-pouvoir"||_deepView==="themis-pouvoir-confirmation") && pouvoirLink) return _deepView;
    if(_deepView==="themis-emargement" && emargementLink) return "themis-emargement";
    if(_deepView==="themis-repas" && repasLink) return "themis-repas";
    // Si une session Aramis valide existe, restaurer la dernière vue
    try{
      const s=localStorage.getItem("aramis_session")||sessionStorage.getItem("aramis_session");
      if(s){
        const parsed=JSON.parse(s);
        const timeout=1440*60*1000;
        if(!parsed._ts||Date.now()-parsed._ts<timeout){
          // Deep-link prioritaire sur la vue sauvegardée
          if(_deepView) return _deepView;
          const saved=sessionStorage.getItem("aramis_view");
          const forbidden=["page-garde","aramis-login","totp-setup"];
          if(saved&&!forbidden.includes(saved)) return saved;
          return "accueil";
        }
      }
    }catch{}
    return "page-garde";
  });
  // SÉCURITÉ — Abuse detector sur navigations : un comportement normal fait
  // 5-20 nav/min en usage réel. >40 nav en 60s = script, bot, ou frappe au
  // clavier anormale. On log et on throttle (délai additionnel) pour freiner.
  // Deux compteurs depuis le lot L6 (décision de Pierre n° 9) : les navigations
  // de COQUILLE (rail, onglets, feuille « Modules », palette, raccourcis) ont
  // leur propre plafond, 120 par minute ; les autres gardent 40.
  const _limiteurContenuRef = useRef(null);
  if (!_limiteurContenuRef.current) _limiteurContenuRef.current = creerLimiteur(LIMITE_CONTENU);
  const _limiteurCoquilleRef = useRef(null);
  if (!_limiteurCoquilleRef.current) _limiteurCoquilleRef.current = creerLimiteur(LIMITE_COQUILLE);
  const _navThrottleUntilRef = useRef(0);
  // Vues déjà rendues dans cette session : leur code est chargé, une
  // transition de vue peut les montrer d'un coup.
  const vuesVisiteesRef = useRef(new Set());
  // Remonter en haut après un changement de vue hors transition (la
  // transition le fait elle-même, dans sa mise à jour).
  const defilerEnHautRef = useRef(false);
  // Vue dont on attend le code (barre de progression) ; jeton : seule la
  // DERNIÈRE navigation demandée bascule (lot L6, étape 3).
  const [vueEnChargement, setVueEnChargement] = useState(null);
  const jetonNavRef = useRef(0);
  // Repli sans transition de vue (Safari < 18, mouvement réduit) : le contenu
  // entre en fondu de 120 ms ; jamais au clavier ni au retour arrière.
  const fonduRef = useRef(false);

  // ── Changer de vue (lot L6, étape 2) ─────────────────────────────────────
  // Un seul point, partagé par navTo et le retour arrière (popstate) :
  //   • View Transition (M3, synthèse §6.5) si l'API existe, hors mouvement
  //     réduit, hors clavier (option `clavier` OU touche de moins d'une
  //     seconde : Entrée sur un bouton du contenu, Alt+← du retour arrière),
  //     hors retour arrière au doigt, et si la vue
  //     d'arrivée peut être rendue d'un coup ; une transition en cours est
  //     d'abord terminée (un clic n'est jamais perdu) ;
  //   • sinon changement direct ;
  //   • retour en haut de page (sauf au retour arrière).
  //   • étape 3 : si le code de la vue manque, on le charge EN GARDANT la vue
  //     courante (barre de progression) ; au-delà de 2,5 s on bascule quand
  //     même, la vue montrant alors son propre écran de chargement. Seule la
  //     dernière navigation demandée bascule (jeton).
  const changerVue = (v, opts = null) => {
    const o = opts || {};
    const jeton = ++jetonNavRef.current;
    terminerTransitionEnCours();
    if (v === view) {
      setVueEnChargement(null); if (o.historique) o.historique(); setView(v);
      // Même vue, nouveaux paramètres (lien de notification, palette, bouton) :
      // la vue n'est JAMAIS remontée, un encart en ligne rempli disparaîtrait
      // avec sa saisie. Elle reçoit ses paramètres (aramis:parametres-vue) et
      // ouvre l'objet sans rien démonter (src/parametresVue.js).
      // Retour arrière ou avant (popstate) : RIEN n'est annoncé, la vue reste
      // telle quelle (comme avant le lot). L'adresse de l'entrée d'historique
      // s'appliquait sous une fenêtre ouverte, sans son dialogue : l'éditeur
      // d'invitation passait de la réunion B à A avec le formulaire de B
      // (relecture du 30/09, src/retourArriereMemeVue.test.js).
      if (!o.retour) annoncerParametresVue(v, o.params || null);
      return;
    }
    let fait = false;
    // Relevé AU GESTE : l'attente du code (étape 3) ne doit pas le faire oublier.
    const clavier = !!o.clavier || saisieClavierRecente();
    const basculer = (pret) => {
      if (fait || jeton !== jetonNavRef.current) return;
      fait = true;
      setVueEnChargement(null);
      if (o.historique) o.historique();
      noterRecent(v);
      const anime = doitAnimerTransition({
        api: apiTransitionsDisponible(), reduit: mouvementReduit(),
        clavier, retour: !!o.retour, doigt: !pointeurFin(), pret,
      });
      if (anime) { transitionVue(() => setView(v), { tuile: o.tuile || null, cle: o.cle || null, defiler: !o.retour }); return; }
      if (!o.retour) defilerEnHautRef.current = true;
      if (!o.retour && !clavier) fonduRef.current = true;
      setView(v);
    };
    const pret = vuePrete(v);   // true, false (code absent) ou null (vue inconnue)
    // On n'attend que DANS la coquille : depuis l'écran de connexion (vue hors
    // coquille), on bascule tout de suite sur le squelette de la vue.
    const dansCoquille = !!aramisSession && !["page-garde", "aramis-login", "screen"].includes(view);
    const attente = pret === false && dansCoquille ? prechargerVue(v) : null;
    if (attente) {
      setVueEnChargement(v);
      const t = setTimeout(() => basculer(false), 2500);
      // Code arrivé : bascule animable ; échec : bascule sans transition, la vue
      // passe par son chargement paresseux (et son filet après déploiement).
      attente.then(() => { clearTimeout(t); basculer(vuePrete(v) === true); });
      return;
    }
    basculer(pret === true || vuesVisiteesRef.current.has(v));
  };
  const changerVueRef = useRef(changerVue);
  changerVueRef.current = changerVue;
  useLayoutEffect(() => {
    vuesVisiteesRef.current.add(view);
    if (defilerEnHautRef.current) {
      defilerEnHautRef.current = false;
      try { window.scrollTo(0, 0); } catch { /* jsdom */ }
    }
    if (fonduRef.current) {
      fonduRef.current = false;
      const c = document.querySelector(".cq-contenu");
      if (c && typeof c.animate === "function") {
        try { c.animate([{ opacity: 0 }, { opacity: 1 }], { duration: 120, easing: "ease" }); } catch { /* WAAPI absente */ }
      }
    }
  }, [view]);

  // Wrapper navigation : persiste la vue dans sessionStorage + détection d'abus
  // + deeplink : on peut passer un `params` (object) qui sera disponible via
  // sessionStorage "aramis_nav_params" pour que le module cible ouvre l'item
  // précis (ex: { projectId, reunionId, consultationId, sessionId, delegateId }).
  //
  // L'URL est SYNC en parallèle (?view=X&projectId=Y) pour que :
  //   • Copier-coller l'URL ouvre directement la même fiche dans un autre onglet
  //   • Le bouton « Copier le lien » sur chaque fiche fonctionne nativement
  //   • Bookmarks et partages entre membres marchent
  // opts (lot L6) : { coquille, clavier, tuile, cle } — navigation de la
  // coquille (compteur à part), déclenchée au clavier (jamais animée), tuile de
  // départ et clé du module d'arrivée (M3). Les appels à deux arguments
  // gardent exactement leur comportement.
  const navTo = (v, params = null, opts = null) => {
    // Lien d'une notification (centre, notification système : `lienNotification`) :
    // il arrive pendant n'importe quelle saisie, et changer de vue fermerait en
    // silence la fenêtre de travail ouverte (règle du 24/08) ; sur la vue
    // courante, l'objet s'ouvrirait derrière elle. Chaque fenêtre passe d'abord
    // par son dialogue de choix ; gardée (« Annuler »), on ne va nulle part et
    // rien n'est écrit. De même pour tout geste qui ANNONCE des paramètres à la
    // vue courante (palette, bouton) : l'objet s'ouvrirait sous la fenêtre.
    const annonceVueCourante = v === view && !!params && typeof params === "object";
    const garde = annonceVueCourante ? { ...(opts || {}), annonceVueCourante } : opts;
    if (differerSiFenetreOuverte(garde, () => navToRef.current && navToRef.current(v, params, opts))) return;
    const now = Date.now();
    // Throttle actif ? rejeter silencieusement
    if (_navThrottleUntilRef.current > now) return;

    // Stockage du payload pour que le module cible le consomme au mount
    try {
      if (params && typeof params === "object") {
        sessionStorage.setItem("aramis_nav_params", JSON.stringify({ ...params, _ts: now }));
      } else {
        sessionStorage.removeItem("aramis_nav_params");
      }
    } catch {}

    // Étape 1 (refonte nav) — MODE OBSERVE : journalise les accès qui seraient
    // refusés par le modèle d'accès cible, SANS bloquer la navigation.
    if (ACCESS_OBSERVE_MODE && aramisSession) {
      try {
        const verdict = evaluateViewAccess(v, aramisSession, delegates);
        if (!verdict.allowed) {
          const dedupKey = aramisSession.id + "|" + v;
          if (!_accessObserved.has(dedupKey)) {
            _accessObserved.add(dedupKey);
            console.warn("[access-observe] "+v+" — "+verdict.reason+" (scope "+verdict.scope+")");
            import("firebase/firestore").then(({ collection: col, addDoc }) =>
              import("./firebase").then(({ db }) => {
                addDoc(col(db, "auditLog"), {
                  ts: now,
                  action: "access_observe",
                  actionCategory: "access_observe",
                  view: v,
                  accessScope: verdict.scope,
                  accessReason: verdict.reason,
                  userId: aramisSession.id,
                  userPrenom: aramisSession.prenom || "",
                  userNom: aramisSession.nom || "",
                  userMutuelle: aramisSession.mutuelle || "",
                  userRole: aramisSession.role || "member",
                }).catch(() => {});
              }).catch(() => {})
            ).catch(() => {});
          }
        }
      } catch {}
    }

    // Fenêtre glissante de 60 s, un compteur par origine (décision n° 9).
    const _coquille = !!(opts && opts.coquille);
    const _debit = (_coquille ? _limiteurCoquilleRef : _limiteurContenuRef).current.noter(now);

    if (_debit.depasse) {
      // Trop de nav : active un throttle de 10s + log audit
      console.warn("[security] Rate-limit navigation : >" + (_coquille ? LIMITE_COQUILLE : LIMITE_CONTENU) + " nav en 60s" + (_coquille ? " (coquille)" : ""));
      _navThrottleUntilRef.current = now + 10 * 1000;
      // Log asynchrone (non bloquant)
      try {
        if (aramisSession?.id) {
          import("firebase/firestore").then(({ collection: col, addDoc }) =>
            import("./firebase").then(({ db }) => {
              addDoc(col(db, "auditLog"), {
                ts: now,
                action: "access_abuse_rapid_nav",
                actionCategory: "security",
                userId: aramisSession.id,
                userPrenom: aramisSession.prenom || "",
                userNom: aramisSession.nom || "",
                userMutuelle: aramisSession.mutuelle || "",
                userRole: aramisSession.role || "member",
                userAgent: (navigator.userAgent || "").slice(0, 200),
                detail: _debit.compte + " nav en 60s" + (_coquille ? " (coquille)" : ""),
              }).catch(() => {});
            }).catch(() => {})
          ).catch(() => {});
        }
      } catch {}
      return;
    }

    // Vue mémorisée et entrée d'historique : écrites AU MOMENT DE LA BASCULE
    // (changerVue). Depuis le lot L6 la bascule peut attendre le code de la
    // vue ; une vue jamais affichée ne laisse ni vue mémorisée ni entrée.
    const historique = () => {
    try{ sessionStorage.setItem("aramis_view", v); }catch{}
    // Intégration bouton « retour » du navigateur : pousse une entrée dans
    // l'historique pour chaque navigation. L'utilisateur peut ensuite revenir
    // en arrière avec ← (mobile) ou Backspace / Alt+← (desktop).
    // En plus : on écrit la vue + les params en query string pour permettre
    // le partage d'URL et l'ouverture en nouvel onglet.
    try {
      const qp = new URLSearchParams();
      qp.set("view", v);
      if (params && typeof params === "object") {
        for (const [k, val] of Object.entries(params)) {
          if (k.startsWith("_")) continue; // skip _ts et autres internes
          if (val == null) continue;
          // Valeurs scalaires uniquement (string/number/bool). Les objets
          // restent côté sessionStorage.
          if (typeof val === "object") continue;
          qp.set(k, String(val));
        }
      }
      const newUrl = window.location.pathname + "?" + qp.toString();
      // Évite de pousser un état identique (re-cliquer la même tuile)
      if (window.history.state?.aramisView !== v
          || window.location.search !== "?" + qp.toString()) {
        // L'entrée porte sa position (fil, index) : le retour arrière sait
        // ainsi d'où il part et où il va (gardeHistorique.js).
        gardeHistorique.pousser({ aramisView: v, params: params || null }, newUrl);
      }
    } catch {}
    };
    changerVue(v, { ...(opts || {}), historique, params });
  };
  navToRef.current = navTo;
  // Clic sur une notification système (service worker, public/sw.js) : la
  // fenêtre ouverte reçoit l'adresse de l'objet et y va par navTo, sans
  // recharger la page (une saisie en cours n'est jamais perdue).
  useEffect(() => {
    const sw = typeof navigator !== "undefined" && navigator.serviceWorker;
    if (!sw || !sw.addEventListener) return undefined;
    const surMessage = (e) => {
      const d = e && e.data;
      if (!d || d.type !== "aramis-ouvrir" || typeof d.url !== "string") return;
      try {
        const u = new URL(d.url, window.location.origin);
        const v = u.origin === window.location.origin && u.searchParams.get("view");
        if (!v || !navToRef.current) return;
        const params = {};
        u.searchParams.forEach((val, k) => { if (k !== "view") params[k] = val; });
        navToRef.current(v, Object.keys(params).length ? params : null, { lienNotification: true });
      } catch { /* adresse illisible : rien */ }
    };
    sw.addEventListener("message", surMessage);
    return () => sw.removeEventListener("message", surMessage);
  }, []);
  const {appState,saveState,loading,error}=useFirebaseState();
  // Lecture PUBLIQUE de la séance (lot 4 des règles) : le votant, avec ou sans
  // compte, et l'écran de salle hors session lisent seancePublique/current
  // (question, choix, résultats anonymes) ; sessions/current exige une
  // session Aramis.
  const {projection:seancePublique}=useSeancePublique(view==="screen"||view==="participant");
  const etatVotant=useMemo(()=>versEtatSalle(seancePublique),[seancePublique]);
  const [isOnline,setIsOnline]=useState(navigator.onLine);
  useEffect(()=>{
    const up=()=>setIsOnline(true);
    const down=()=>setIsOnline(false);
    window.addEventListener("online",up);
    window.addEventListener("offline",down);
    return()=>{window.removeEventListener("online",up);window.removeEventListener("offline",down);};
  },[]);

  // Bouton « retour arrière » du navigateur : synchronise la vue avec
  // l'historique. On écoute popstate (déclenché par ← / Alt+← / swipe back)
  // et on restore la vue depuis history.state.
  // Init : remplace l'entrée courante pour qu'elle contienne aramisView
  // (sinon le 1er retour revient avant l'app, avec state=null).
  // Fenêtre de travail ouverte : le saut est d'abord ramené, chaque fenêtre
  // passe par son dialogue de choix, et il n'est rejoué que si toutes sont
  // fermées (règle du 24/08, relecture du 04/10 : gardeHistorique.js).
  useEffect(() => {
    try {
      const etat = window.history.state;
      if (etat && etat.aramisView === view) gardeHistorique.adopter(etat);
      else gardeHistorique.adopter({ aramisView: view }, "#" + view);
    } catch {}
    const onPop = (e) => {
      gardeHistorique.surPopstate(e, (etat) => {
        const v = etat?.aramisView;
        if (v && typeof v === "string") {
          try { sessionStorage.setItem("aramis_view", v); } catch {}
          // Retour arrière : transition au pointeur fin seulement (au doigt, le
          // balayage d'iOS anime déjà), pas de retour en haut (lot L6). Sur la
          // MÊME vue, aucun paramètre n'est annoncé (changerVue) : rien ne bouge.
          changerVueRef.current(v, { retour: true });
        }
      });
    };
    window.addEventListener("popstate", onPop);
    return () => window.removeEventListener("popstate", onPop);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  // ── Session Aramis unifiée — timeout = config.sessionTimeoutMinutes ──
  // Déclarée AVANT les hooks Firestore : elle pilote leur paramètre `enabled`
  // (perf boot — les collections post-login ne s'abonnent qu'une fois connecté,
  // le canal long-polling reste disponible pour le splash et le login).
  const [aramisSession,setAramisSession]=useState(()=>{
    try{
      const s=(()=>{try{return localStorage.getItem("aramis_session");}catch{try{return sessionStorage.getItem("aramis_session");}catch{return null;}}})();
      if(!s) return null;
      return JSON.parse(s);
    }catch{return null;}
  });
  // Garde de session Firebase (fuite ② : le jeton vérifié doit exister, pas
  // seulement la session Aramis) — au démarrage et à chaque retour au premier
  // plan. Observable seulement tant que l'interrupteur serveur est en « mesure ».
  useEffect(()=>{
    if(!aramisSession?.id) return undefined;
    let desinstaller=null, annule=false;
    import("./authGuard").then(m=>{ if(!annule) desinstaller=m.installerGardeSession(); });
    // Bannière « Se reconnecter » : l'écran de connexion se rouvre par-dessus
    // la session, qui n'est pas fermée ; un login réussi la remplace et
    // rouvre une session Firebase (jeton vérifié).
    const onReco=async(ev)=>{
      // Session ouverte par passkey : la cérémonie se rejoue sur place (Touch ID /
      // Face ID, un geste — le clic sur la bannière en est un) et rouvre la
      // session Firebase sans quitter l'écran. Sinon, ou en cas d'échec :
      // l'écran de connexion, par-dessus la session Aramis conservée.
      // La doctrine exige une connexion par passkey (12/10/2026) : son refus
      // demande la cérémonie quelle que soit la façon dont la session s'est
      // ouverte (mot de passe puis passkey, mot de passe et code).
      try{
        const g=await import("./authGuard");
        if(g.methodeConnexion()==="passkey"||(ev&&ev.detail&&ev.detail.origine==="connexion-forte")){
          const { loginWithPasskey }=await import("./appShared");
          const data=await loginWithPasskey();
          if(data?.profile?.id&&String(data.profile.id)===String(aramisSession?.id)&&data.firebaseToken){
            const fb=await import("./firebase");
            if(await g.connecterJetonFirebase(fb.auth,data.firebaseToken,"passkey")) return;
          }
        }
      }catch(e){ console.info("[authGuard] reconnexion biométrique refusée ou annulée —",e?.message); }
      navTo("aramis-login");
    };
    window.addEventListener("aramis:reconnecter", onReco);
    return ()=>{ annule=true; if(desinstaller) desinstaller(); window.removeEventListener("aramis:reconnecter", onReco); };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  },[aramisSession?.id]);
  // ── Impersonation : "Voir Aramis comme X" (admin-only)
  // Quand actif, effectiveSession remplace aramisSession pour tous les composants
  // sauf le bandeau d'impersonation et le header (qui gardent la vraie session).
  const [impersonateAs, setImpersonateAs] = useState(null);
  // Fiche ouverte (instance Thémis, événement…) : SA barre latérale (étapes +
  // commandes) remplace la barre de module — deux rails côte à côte
  // désorientaient (retour Pierre, 24/08). Généralisé : la valeur est le NOM
  // de la vue dont la fiche est ouverte (null = aucune), pour que chaque
  // module à fiche puisse masquer la sidebar sans câblage en dur.
  const [ficheOuverteView, setFicheOuverteView] = useState(null);
  // Pas de reset sur changement de vue ici : le cleanup d'unmount de chaque
  // fiche (onFicheStateChange(false)) s'en charge — un reset parent sur
  // [view] s'exécuterait APRÈS l'effet de mount de la nouvelle fiche
  // (effets enfants avant parents) et écraserait son signal.
  const effectiveSession = impersonateAs || aramisSession;
  // Drapeaux de gating des abonnements (perf boot). Restent pré-login :
  // sessions/current + config (porte du splash), delegates + memberLists
  // (écran de login), ideaBox (boîte à idées publique, early-return `ideas`).
  const sessionActive = !!aramisSession;
  const isAdminSession = !!(aramisSession?.role==="admin"||aramisSession?.role==="superadmin"||hasValidLegacyAdmin());
  const {config,saveConfig}=useConfig();
  const {lists,saveList,deleteList}=useMemberLists();
  const {admins,saveAdmin,deleteAdmin}=useAdmins(sessionActive);
  const {delegates,delegatesTechniques,saveDelegate,deleteDelegate}=useDelegates(sessionActive);
  const {gouvernance,saveGouvernance}=useGouvernance(sessionActive);
  const {ideas,saveIdea,deleteIdea}=useIdeaBox();
  const {auditLog,logAction}=useAuditLog(isAdminSession);
  const {projects,saveProject,deleteProject,setEngagement}=useProjects(sessionActive);
  const {meetings,saveMeeting,deleteMeeting}=useMeetings(sessionActive);
  const {members,saveMember,deleteMember}=useMembers(sessionActive);
  const {reunions,saveReunion,updateInviteStatut}=useReunions(sessionActive);
  const {externes,saveExterne}=useExternesReunion(sessionActive);
  // État connexion membre (persisté en sessionStorage)
  const [showQuestionModal, setShowQuestionModal] = useState(false);
  // Conversion idée → question d'entraide : seedData préfabriquée à partir
  // de l'idée d'origine, l'auteur effectif étant le soumetteur de l'idée
  // (pas l'admin qui convertit). Cf. QuestionModal.seedData.
  const [convertIdeaSeed, setConvertIdeaSeed] = useState(null);
  const [openQuestion, setOpenQuestion] = useState(null);

  // Accès « Mes notes de frais » : RÉSERVÉ aux salariés ENIO + membres explicitement ouverts.
  // L'éligibilité est décidée côté serveur (par e-mail) ; on la résout ici pour piloter
  // l'affichage du menu et de la route (le serveur reste l'autorité qui refuse le cas échéant).
  const [ndfEligible, setNdfEligible] = useState(false);
  useEffect(() => {
    let annule = false;
    if (!effectiveSession?.id) { setNdfEligible(false); return undefined; }
    (async () => {
      try {
        // Pas de question sans session Firebase. Au démarrage, une session Aramis
        // PÉRIMÉE est restaurée un instant avant d'être close : la question partait
        // alors sans jeton (seuls appels déclarés mesurés du 11 au 18/09/2026, sept
        // personnes, toujours cette fonction). En mode strict le serveur la refuse
        // de toute façon — même garde que « espace salarié » ci-dessous.
        const garde = await import("./authGuard");
        await garde.attendreAuthPrete();
        const { auth } = await import("./firebase");
        if (annule) return;
        if (!auth.currentUser) { setNdfEligible(false); return; }
        const r = await callDepenses("apNdfMonAcces", effectiveSession, {});
        if (!annule) setNdfEligible(!!r?.eligible);
      } catch { if (!annule) setNdfEligible(false); }
    })();
    return () => { annule = true; };
  }, [effectiveSession]);

  // Mise à jour Cowork À LA CONNEXION du propriétaire du budget (demande de
  // Pierre du 05/10/2026) : ouverture ou restauration de SA session réelle,
  // une fois par chargement, en arrière-plan. Plus à l'ouverture de la page
  // Budget, qui se contente désormais d'en écouter le résultat.
  useEffect(() => {
    if (!aramisSession?.id || !estProprietaireBudget(aramisSession)) return;
    import("./syncCoworkConnexion").then((m) => m.lancerSyncCoworkConnexion(aramisSession)).catch(() => {});
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [aramisSession?.id]);

  // La cible « documents de séance » mémorisée n'est qu'un relais de login :
  // une fois la page atteinte connecté, on la purge pour qu'elle ne détourne
  // pas la connexion suivante sur ce navigateur.
  useEffect(() => {
    if (view === "themis-documents-seance" && aramisSession?.id) {
      try { sessionStorage.removeItem("aramis_docs_seance_pending"); } catch { /* storage indispo */ }
    }
    if (view === "mediatheque-album" && aramisSession?.id) {
      try { sessionStorage.removeItem("aramis_media_album_pending"); } catch { /* storage indispo */ }
    }
  }, [view, aramisSession?.id]);

  // « Mon espace salarié » dans Aramis (refonte 26/07/2026) : le compte Aramis
  // prime — un salarié qui a un accès Aramis n'a AUCUN mot de passe à créer,
  // son espace s'ouvre ici. L'entrée de menu n'apparaît que s'il a réellement
  // un espace (sinon les ~200 délégués de mutuelles verraient un lien mort).
  // L'appel s'appuie sur le token Firebase du pont Aramis (uid = delegateId) ;
  // sans token, pas d'entrée — cohérent, l'espace serait inaccessible.
  const [espaceSalarieEligible, setEspaceSalarieEligible] = useState(false);
  // Cockpit de l'espace salarié (26/10/2026) : « Mon équipe » pour un manager
  // (même réponse du serveur), et les chiffres du cockpit pour le rail.
  const [espaceSalarieManager, setEspaceSalarieManager] = useState(false);
  const [compteursEspace, setCompteursEspace] = useState(null);
  useEffect(() => {
    let annule = false;
    if (!effectiveSession?.id) { setEspaceSalarieEligible(false); setEspaceSalarieManager(false); return undefined; }
    (async () => {
      try {
        // La session Aramis est relue de localStorage de façon SYNCHRONE, alors
        // que Firebase Auth restaure la sienne de façon ASYNCHRONE. Sans cette
        // attente, l'appel partait sans token au rechargement de page et
        // l'entrée de menu disparaissait à chaque F5.
        const { getAuth, onAuthStateChanged } = await import("firebase/auth");
        const auth = getAuth();
        if (auth.authStateReady) await auth.authStateReady();
        else await new Promise(res => { const u = onAuthStateChanged(auth, () => { u(); res(); }); });
        if (annule) return;
        if (!auth.currentUser) { setEspaceSalarieEligible(false); return; }
        const { getFunctions, httpsCallable } = await import("firebase/functions");
        const r = await httpsCallable(getFunctions(undefined, "europe-west3"), "rhSelfEspaceDisponible")({});
        if (!annule) { setEspaceSalarieEligible(!!r?.data?.disponible); setEspaceSalarieManager(!!r?.data?.estManager); }
      } catch { if (!annule) { setEspaceSalarieEligible(false); setEspaceSalarieManager(false); } }
    })();
    return () => { annule = true; };
  }, [effectiveSession]);

  // Acteur du journal d'activité (Étape 10d) : qui réalise les sauvegardes
  // (consommé par _logEdit dans useFirebaseState pour tracer before/after).
  useEffect(() => {
    setAuditActor(aramisSession ? (`${aramisSession.prenom||""} ${aramisSession.nom||""}`.trim() || aramisSession.email || "Admin") : "Admin");
  }, [aramisSession]);

  // SÉCURITÉ — Tracker les entrées dans les vues sensibles (admin panel, annuaire,
  // Thémis). Déclenche logSecurityAccess avec l'action correspondante. Le throttle
  // interne évite le spam (max 1 event par action par session).
  useEffect(() => {
    if (!aramisSession?.id || !view) return;
    if (view === "admin" || view === "admin-demandes" || view === "athos-demandes" || view === "admin-adoption" || view === "admin-emails-auto" || view === "admin-envois" || view === "admin-erreurs" || view === "admin-auth-mesure" || view === "admin-sante-collectes" || view === "admin-usages" || view === "admin-nouveautes" || view === "super-synchronisation" || view === "admin-campagnes" || view === "admin-budget" || view === "admin-budget-outils" || VUES_BUDGET.includes(view) || view === "admin-depenses" || view === "admin-ndf" || view === "compta-ec" || view.startsWith("admin-super-")) {
      logSecurityAccess("admin_panel");
    } else if (view === "athos-annuaire") {
      logSecurityAccess("annuaire");
    } else if (view.startsWith("themis-")) {
      logSecurityAccess("themis");
    } else if (view === "emailing") {
      logSecurityAccess("emailing");
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [view, aramisSession?.id]);

  // USAGES D'ARAMIS (F1, 11/10/2026) : à chaque vue, l'ouverture du module
  // (parmi les dix du menu) est notée par la CF usageNoterModule, au plus une
  // fois par module, par heure et par onglet, sans retenir la navigation. Le
  // serveur relit la fiche et ne garde que des agrégats par jour, module et
  // mutuelle. Jamais pour une page publique à jeton (lien de vote compris) :
  // src/usagesModules.js. Rien à voir avec le journal de sécurité ci-dessus.
  useEffect(() => {
    noterVueModule({ view, session: aramisSession, lienPublic: view === "participant" && !!magicVote });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [view, aramisSession?.id]);

  // SÉCURITÉ — Audit d'accès : écrit une entrée dans auditLog pour tracer les
  // événements sensibles (login, accès panel admin, accès annuaire massif).
  // Throttlé : 1 event par action par session pour éviter le spam (on log le
  // FAIT qu'on accède, pas CHAQUE interaction). La CF scheduled monitorSecurityAnomalies
  // tourne chaque heure et alerte les superadmins sur anomalies.
  const logSecurityAccess = useCallback(async (action, session = null) => {
    try {
      const sess = session || aramisSession;
      if (!sess?.id) return;
      // Anti-spam : throttle 1 event par action par session (30min cooldown)
      const key = `security_throttle_${action}_${sess.id}`;
      const last = parseInt(sessionStorage.getItem(key) || "0", 10);
      if (Date.now() - last < 30 * 60 * 1000) return;
      sessionStorage.setItem(key, String(Date.now()));

      const { collection: col, addDoc } = await import("firebase/firestore");
      const { db } = await import("./firebase");
      await addDoc(col(db, "auditLog"), {
        ts: Date.now(),
        action: "access_" + action,
        actionCategory: "security",
        userId: sess.id,
        userPrenom: sess.prenom || "",
        userNom: sess.nom || "",
        userMutuelle: sess.mutuelle || "",
        userRole: sess.role || "member",
        userAgent: (navigator.userAgent || "").slice(0, 200),
        language: navigator.language || "",
      });
    } catch (e) {
      // Ne jamais bloquer le flow user sur un échec d'audit
      console.warn("[audit] logSecurityAccess error:", e?.message);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [aramisSession?.id]);

  // SÉCURITÉ : au chargement de l'app, si la session Aramis n'est pas admin
  // mais qu'un cache admin legacy existe d'un précédent utilisateur, purger.
  // Couvre le cas : user A (admin) se déconnecte ou refresh sans clear, user B
  // (normal) se connecte → aurait hérité des droits admin sans ce check.
  useEffect(() => {
    try {
      const stored = localStorage.getItem("eniovote_admin");
      if (!stored) return;
      const cached = JSON.parse(stored);
      if (!aramisSession) return; // pas connecté, laisser legacy admin standalone
      const isCurrentUserAdmin = aramisSession.role === "admin" || aramisSession.role === "superadmin";
      const cacheMatchesCurrent = cached?.id === aramisSession.id;
      if (!isCurrentUserAdmin || !cacheMatchesCurrent) {
        console.warn("[security] purge eniovote_admin stale (session mismatch ou user non-admin)");
        localStorage.removeItem("eniovote_admin");
      }
    } catch {}
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [aramisSession?.id, aramisSession?.role]);

  // Palette ⌘K (lot L6) : son code est préchargé quatre secondes après
  // l'arrivée, pour qu'elle s'ouvre dans la même image que la frappe.
  useEffect(() => {
    if (!aramisSession?.id) return undefined;
    const t = setTimeout(() => { GlobalSearchRaw.preload(); FeuilleModulesRaw.preload(); }, 4000);
    return () => clearTimeout(t);
  }, [aramisSession?.id]);

  // isAdmin disponible dans App pour le routing — toujours basé sur la vraie session.
  // Depuis l'audit du 28/09/2026, le terme localStorage vaut toujours false
  // (appShared.js) : seul le rôle de session, émis par le serveur, décide.
  const isAdminApp=!!(aramisSession?.role==="admin"||aramisSession?.role==="superadmin"||hasValidLegacyAdmin());
  // Boîte à outils budgétaire : propriétaire unique (chantier A). Garde UX ; la
  // vraie barrière est côté serveur (exigerProprietaireBudget sur les 7 CFs).
  const isProprietaireBudget=estProprietaireBudget(aramisSession);
  // Un correspondant budget (memberLists[].referentsCotisations) ne fait que
  // RECEVOIR les courriels d'appel de cotisation : il n'ouvre pas « Budget &
  // cotisations » (décision de Pierre du 21/10/2026, qui retire l'onglet
  // « CA réels » qu'il voyait jusque-là pour proposer le CA de sa mutuelle).
  // isAdmin basé sur la session effective (pour le contenu quand impersonation active)
  const isAdminEffective = impersonateAs
    ? !!(effectiveSession?.role==="admin"||effectiveSession?.role==="superadmin")
    : isAdminApp;
  // Demandes d'engagement à valider (lot L11d-bis) : lues pour un administrateur
  // de la session RÉELLE, en direct ; null = on ne sait pas (pas de pastille).
  // Le menu, lui, suit la session affichée (« Voir comme ») : buildNavModules
  // ne pose le compteur que sur une entrée visible.
  const nDemandesEngagement = useDemandesEngagementEnAttente(!!aramisSession && isAdminApp);
  // Hestia (lot H0) : drapeau de la session RÉELLE (Hestia sert l'identité du
  // jeton, « Voir comme » n'y change rien), chargé après le premier rendu.
  const drapeauHestia = useDrapeauHestia(aramisSession?.id || null);
  // Audit interne (D1) : drapeau de la session RÉELLE (le serveur sert
  // l'identité du jeton), demandé après le premier rendu ; un refus est
  // redemandé à l'entrée dans Conformité (src/audit/drapeauAudit.js).
  const drapeauAudit = useDrapeauAudit(aramisSession?.id || null, MODULE_VIEWS.hygie.includes(view));
  // RH Maître : ce que RHModule a lu de rhWhoAmI à son ouverture, noté pour le
  // rail (src/droitRH.js), aucune requête de plus. Session de « Voir comme »,
  // celle que reçoit RHModule : le rail suit la garde de ses pages.
  const isRhMaitre = useDroitRH(effectiveSession?.id || null);
  // Accès au module RH/Recrutement (scope App, pour la garde de vue). Admin Enio
  // OU délégué habilité « RH » (case fiche) ou DG — décision de Pierre du
  // 15/10/2026 : plus le CODIR, plus le pôle « Ressources humaines »
  // (src/droitsRH.js, source unique).
  // L'étanchéité par mutuelle est garantie côté Cloud Functions.
  const canAccessRHnav = (() => {
    const sess = effectiveSession || aramisSession;
    if (!sess) return false;
    const me = (delegates||[]).find(d => d.id === sess.id) || sess;
    return peutAccederRH(me, { isAdmin: isAdminEffective });
  })();
  // Rôles dirigeants de la mutuelle (DG/PR/CA/CD) — basé sur la session effective.
  // Non utilise actuellement (la page Ma mutuelle gere elle-meme sa logique
  // dirigeant/membre) mais conserve pour d'eventuelles futures gardes d'acces.
  const effectiveRoles = effectiveSession?.roles || [];
  // eslint-disable-next-line no-unused-vars
  const isDirigeantApp = ["DG","PR","CA","CD"].some(r => effectiveRoles.includes(r));
  // Accès refusé → VITRINE du module (lot L7, synthèse §8 Événements) : au
  // lieu de la page d'excuses, la plaque du module, ce qu'il contient et
  // « Demander l'accès ». La demande reste le courriel prérempli d'avant
  // (ressource demandée en objet, identité du demandeur dans le corps) : rien
  // ne s'écrit. `view` absent : la vue courante d'App.
  const AccessDenied = ({ reason, view: vueDemandee }) => {
    const _vue = vueDemandee || view;
    const _id = [aramisSession?.prenom, aramisSession?.nom].filter(Boolean).join(" ");
    const _mail = aramisSession?.mail || aramisSession?.email || "";
    const _subject = "Demande d'accès Aramis" + (_vue ? " : " + _vue : "");
    const _body =
      "Bonjour,\n\nJe souhaiterais pouvoir accéder à la ressource suivante dans Aramis : "
      + (_vue || "(à préciser)") + ".\n\n"
      + (_id || _mail ? "Demandeur : " + _id + (_mail ? " <" + _mail + ">" : "") + "\n\n" : "")
      + "Pourriez-vous m'indiquer si je peux ou dois y avoir accès ?\n\nMerci.";
    const _href = "mailto:aramis@enio.fr?subject=" + encodeURIComponent(_subject) + "&body=" + encodeURIComponent(_body);
    return (
      <div className="ev">
        <VitrineAccesLazy view={_vue} reason={reason} connecte={!!aramisSession}
          membre={!!aramisSession && aramisSession.role!=="invite"}
          mailto={_href} onNav={navTo}/>
      </div>
    );
  };
  // Helpers storage avec fallback (Safari navigation privée bloque localStorage)
  const storageSet=(k,v)=>{try{localStorage.setItem(k,v);}catch{try{sessionStorage.setItem(k,v);}catch{}}};
  const storageDel=(k)=>{try{localStorage.removeItem(k);}catch{}try{sessionStorage.removeItem(k);}catch{}};
  // Vérification du timeout — toutes les 60 secondes
  useEffect(()=>{
    if(!aramisSession) return;
    const checkTimeout = () => {
      const isAdminSession = aramisSession.role==="admin"||aramisSession.role==="superadmin";
      const timeoutMin = isAdminSession
        ? (config?.sessionTimeoutMinutes||1440)
        : (config?.memberSessionTimeoutMinutes||config?.sessionTimeoutMinutes||1440);
      const timeoutMs = timeoutMin * 60 * 1000;
      if(aramisSession._ts && Date.now()-aramisSession._ts > timeoutMs){
        setAramisSession(null);
        storageDel("aramis_session");
        navTo("aramis-login"); // Forcer le retour à la page de connexion
      }
    };
    checkTimeout();
    const interval = setInterval(checkTimeout, 60000);
    return () => clearInterval(interval);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  },[config?.sessionTimeoutMinutes, aramisSession?._ts]);
  const loginAramis=(d)=>{
    // Sécurité : ne pas stocker les données sensibles dans localStorage
    const {passwordHash, passwordSalt, totp, passkey, ...safeFields} = d;
    const data={...safeFields,_ts:Date.now()};
    setAramisSession(data);
    storageSet("aramis_session",JSON.stringify(data));
    oublierMemoiresPersonne();

    // SÉCURITÉ CRITIQUE : si la session admin legacy (eniovote_admin) est encore
    // présente et ne correspond PAS à l'utilisateur qui se connecte, la purger.
    // Sinon un délégué non-admin héritait d'une session admin résiduelle d'un
    // précédent utilisateur sur le même navigateur → accès admin frauduleux.
    // Même chose si le nouveau loggué n'est pas admin.
    try {
      const stored = localStorage.getItem("eniovote_admin");
      if (stored) {
        const cached = JSON.parse(stored);
        const isCurrentUserAdmin = d.role === "admin" || d.role === "superadmin";
        const cacheMatchesCurrent = cached?.id === d.id;
        if (!isCurrentUserAdmin || !cacheMatchesCurrent) {
          localStorage.removeItem("eniovote_admin");
        }
      }
    } catch {}

    // AUDIT SÉCURITÉ : loggue le login pour détection d'anomalies
    // (> 20 logins/h = automation, même user depuis >3 user-agents = compromission)
    logSecurityAccess("session_start", d).catch(() => {});

    // PREUVE DE CONNEXION (src/delegateStatut.js) : gamification/{id}.lastLogin,
    // au plus une fois par jour, SANS aucun point (lot L1a, 27/09/2026). C'est
    // la seule écriture de la trace : la retirer repeindrait tout l'annuaire en
    // orange (leçon du 27/08). Lot L1b : elle passera au serveur (connexions/).
    noterConnexion(d.id).catch(()=>{});
  };
  const logoutAramis=()=>{
    setAramisSession(null);
    storageDel("aramis_session");
    // Un album mémorisé avant connexion ne détourne pas la connexion suivante.
    try { sessionStorage.removeItem("aramis_media_album_pending"); } catch { /* storage indispo */ }
    // Mémoires de page d'UNE personne (la une de Ma semaine, les albums des
    // séances) : la déconnexion ne recharge pas la page, la suivante n'en
    // hérite pas (memoiresPersonne.js, relecture Médiathèque du 04/10).
    oublierMemoiresPersonne();
    // SÉCURITÉ : purger AUSSI la session admin legacy pour qu'un utilisateur
    // suivant sur le même navigateur n'hérite pas de droits admin.
    try { localStorage.removeItem("eniovote_admin"); } catch {}
    // Fermer la session Firebase Auth (identité vérifiée) pour ne pas laisser
    // un uid actif pour l'utilisateur suivant sur le même navigateur.
    (async () => {
      try {
        const [{ signOut }, fb] = await Promise.all([import("firebase/auth"), import("./firebase")]);
        await signOut(fb.auth);
      } catch (_) { /* non bloquant */ }
    })();
    navTo("page-garde");
  };

  const { questions: accueilQuestions, deleteQuestion } = useQuestions(aramisSession, isAdminApp);
  // Traces de connexion (preuve de compte actif) : lot L1a, 27/09/2026. Même
  // collection qu'avant, plus aucun point. Le nom `leaderboard` est CONSERVÉ vers
  // les écrans consommateurs (Admin, Adoption, équipe d'une mutuelle,
  // Historiques, Présentation) : le renommer, c'est risquer une prop orpheline
  // qui vaudrait [] sans erreur et repeindrait l'annuaire en orange (27/08).
  const { traces: leaderboard } = useDernieresConnexions(sessionActive);

  // ── Écran de démarrage « Genèse » : le picto se construit pièce à pièce
  // pendant le chargement (BootGenese, AuthBackdrop.js). Quand les données
  // arrivent, on garde l'écran ~0,6 s de plus : les pièces s'assemblent d'un
  // coup (snap) puis l'écran s'efface en fondu — sortie propre au lieu d'une
  // coupe sèche, quel que soit l'état de la boucle au moment de la fin.
  const [bootLinger,setBootLinger] = useState(true);
  const bootReady = !loading && config!==null;
  // Aperçu sans attendre un vrai chargement lent : /?bootdemo=1 (boucle infinie).
  const bootDemo = new URLSearchParams(window.location.search).get("bootdemo")==="1";
  useEffect(()=>{
    if(bootReady && bootLinger && !bootDemo){ const t=setTimeout(()=>setBootLinger(false),620); return ()=>clearTimeout(t); }
  },[bootReady,bootLinger,bootDemo]);
  if(loading||config===null||bootLinger||bootDemo) return <BootGenese exiting={bootReady&&!bootDemo}/>;

  if(error) return(
    <div className="ev" style={{display:"flex",alignItems:"center",justifyContent:"center",minHeight:"var(--vh100)",padding:24}}>
      <div className="card" style={{maxWidth:400,textAlign:"center"}}>
        <div style={{fontSize:"var(--fs-display)",marginBottom:12}}><IconeTexte nom="alert-triangle" couleur="var(--tx-orange)" titre="Attention"/></div>
        <div style={{fontWeight:700,fontSize:"var(--fs-corps)",marginBottom:8,color:"var(--tx-red)"}}>Erreur de connexion Firebase</div>
        <div style={{fontSize:"var(--fs-micro)",color:navTexte(C.gray),marginBottom:16}}>{error}</div>
        <div style={{fontSize:"var(--fs-micro)",color:navTexte(C.gray)}}>Verifiez votre configuration dans <code>src/firebase.js</code></div>
      </div>
    </div>
  );

  // Handler unique pour la sauvegarde d'une question (mode "Pose normale" OU
  // "Conversion d'une idée"). Extrait pour pouvoir monter le QuestionModal
  // à la fois dans le retour anticipé de la vue `ideas` et dans le shell
  // principal sans dupliquer 130 lignes de logique (résolution emails dest.,
  // notif in-app, mail CF, suppression idée).
  const handleQuestionSave = async (data) => {
    const {collection,addDoc,doc,deleteDoc}=await import("firebase/firestore");
    const {db}=await import("./firebase");
    const fromIdeaId = data.fromIdeaId || null;
    if (data.fromIdeaId !== undefined) { delete data.fromIdeaId; }
    const docRef = await addDoc(collection(db,"questions"),data);
    const questionId = docRef.id;

    // Les courriels : destinataires, auteur et texte lus par le SERVEUR dans
    // la question enregistrée (21/10/2026, functions/lib/questionsDestinataires.js),
    // jamais une liste d'adresses du navigateur. Envoi cadencé (20 par minute) :
    // la page rappelle tant qu'il reste du monde, sans bloquer l'enregistrement.
    (async () => {
      try {
        const { getFunctions, httpsCallable } = await import("firebase/functions");
        const { envoyerParVagues, phraseEnvoiListe } = await import("./envoiParVagues");
        const appel = httpsCallable(getFunctions(undefined, REGION_FRANCFORT), "notifierQuestion", { timeout: 130000 });
        const fin = await envoyerParVagues({ appeler: async () => (await appel({
          aramisUserId: aramisSession?.id, questionId, appUrl: window.location.origin })).data });
        if (fin.restants || fin.erreur || fin.echecs) toast.error(phraseEnvoiListe(fin, { libelle: "Courriels de la question" }));
      } catch (e) {
        console.warn("[notifierQuestion] échec envoi emails :", e.message);
      }
    })();

    if (fromIdeaId) {
      try { await deleteDoc(doc(db, "ideaBox", fromIdeaId)); }
      catch (e) { console.warn("[convertIdea] suppression idée échouée :", e.message); }
    }

    // Centre de notifications (30/09/2026) : texte final (version 2), auteur
    // identifié (jamais notifié de son propre acte) ; la mutuelle n'est plus
    // une ADRESSE (elle montrait la question à tous les collègues de l'auteur).
    const auteurQuestion = [data.createurNom, data.createurMutuelle].filter(Boolean);
    await creerNotification("question",{
      version:2,
      titre:`${data.type==="aide"?"Demande d'aide":"Question"} : ${data.titre}`,
      message:`Posée par ${auteurQuestion.length > 1 ? `${auteurQuestion[0]} (${auteurQuestion[1]})` : auteurQuestion[0] || "un membre"}.`,
      lien:"questions-historique", params:{ questionId },
      membreNom:data.createurNom,
      auteur:data.createurNom,
      auteurId:data.createurId||null,
      questionId,
      destinatairesIds:data.destinatairesIds,
      destinatairesPoles:data.destinatairesPoles,
    });
  };

  // Élément JSX du modal — monté à la fois dans le retour anticipé `ideas`
  // (sinon le bouton "🔁 En question" n'ouvre rien) et dans le shell principal.
  const questionModalElement = aramisSession && (showQuestionModal || convertIdeaSeed) ? (
    <QuestionModalLazy
      aramisSession={effectiveSession}
      delegates={delegates}
      seedData={convertIdeaSeed}
      onClose={()=>{ setShowQuestionModal(false); setConvertIdeaSeed(null); }}
      onSave={handleQuestionSave}/>
  ) : null;

  // ── PORTAIL EXPERT-COMPTABLE (rôle EC exclusif) : session externe qui ne voit QUE le volet
  // comptable de Dépenses. Placé AVANT tous les dispatchers ?view=… (screen / ideas / document /
  // etc.) pour qu'aucune URL forgée ne contourne l'isolation.
  const _ecRoles = Array.isArray(aramisSession?.roles) ? aramisSession.roles : [];
  const _isEcPur = !!aramisSession && aramisSession.role !== "admin" && aramisSession.role !== "superadmin" && _ecRoles.length === 1 && _ecRoles[0] === "EC";
  if (_isEcPur) {
    return (
      <div className="ev">
        <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",gap:12,padding:"var(--filet-ecart) 16px",borderBottom:"1px solid var(--border-color)",background:"var(--bg-card)"}}>
          <div style={{fontWeight:700,color:"var(--tx-navy)",fontFamily:"Montserrat,sans-serif"}}>Aramis · Comptabilité</div>
          <button className="btn btn-sm btn-ghost" onClick={logoutAramis}>Se déconnecter</button>
        </div>
        <ComptaExpertPanelLazy aramisSession={aramisSession}/>
      </div>
    );
  }

  // Aperçu des surfaces mobiles, DÉVELOPPEMENT UNIQUEMENT (élagué du bundle de
  // production par DefinePlugin). Voir src/ApercuMobile.js : il existe parce que
  // l'app exige une connexion, et que trois chantiers responsive ont échoué
  // faute de pouvoir regarder le rendu là où il compte.
  if(process.env.NODE_ENV !== "production" && view === "__apercu")
    return <ApercuMobileLazy/>;

  if(view==="screen") return <ScreenView appState={appState.session?appState:etatVotant} onNav={navTo}/>;

  // Les onze props du Header étaient recopiées à l'identique dans NEUF returns
  // anticipés. Un seul objet : la coquille mobile qui vient s'y branchera en un
  // point, au lieu de neuf.
  // Super-administrateur de la session RÉELLE (même en « Voir comme ») : la
  // page « Synchronisation » et son bouton de la barre du haut (14/10/2026).
  const estSuperAdminReel = aramisSession?.role==="superadmin" || hasValidLegacySuperAdmin();
  const headerProps = {
    view, onNav: navTo, session: appState.session,
    aramisSession, logoutAramis, ndfEligible, espaceSalarieEligible,
    impersonateAs, setImpersonateAs: isAdminApp ? setImpersonateAs : null,
    delegates,
    onOpenGlobalSearch: aramisSession ? () => setGlobalSearchOpen(true) : null,
    moiOuvert, setMoiOuvert,
    messagesNonLus, onOuvrirMessagerie: aramisSession ? () => setChatOuvert(true) : null,
    socrateOuvert, onBasculerSocrate: aramisSession && socrateDisponible ? () => setSocrateOuvert((o) => !o) : null,
    drapeauAudit, isRhMaitre,
    isHabiliteHestia: drapeauHestia.habilite, hestiaAssistanteSeule: drapeauHestia.assistanteSeule,
    synchroCowork: !!aramisSession && estSuperAdminReel,
  };

  // Lien de vote (e-mail nominatif) ou QR code de la séance : l'espace du
  // votant en plein écran, sans en-tête ni navigation, comme le téléphone du
  // vote de l'AG (recette du 18/10/2026). Un membre connecté à Aramis y
  // arrive aussi (au lieu de « Ma semaine ») et garde « Revenir à Aramis » ;
  // depuis le menu « Voter » (sans lien), l'espace vit dans la coquille.
  if(view==="participant" && magicVote)
    return <div className="ev"><ParticipantLazy appState={etatVotant} mutuelles={getMutuelles(config)} epure
      onRevenir={aramisSession?()=>navTo("ma-semaine"):null}/></div>;
  // Boîte à idées ANONYME : page publique (la soumission est vérifiée contre
  // l'annuaire, sans compte). Elle garde son retour anticipé et son en-tête
  // « Connexion » ; connecté, l'écran est un enfant de la coquille (lot L6).
  if(view==="ideas" && !aramisSession) return(
    <>
    <div className="ev">
      <Header {...headerProps}/>
      <div style={{minHeight:"calc(var(--vh100) - var(--h-coquille))"}}>
        <IdeaBoxPublicLazy delegates={delegates} saveIdea={saveIdea} onNav={navTo}
          ideas={ideas} deleteIdea={deleteIdea} isAdmin={isAdminApp} aramisSession={effectiveSession}
          onConvertToQuestion={isAdminApp ? (idea) => {
            // Résolution du soumetteur de l'idée vers un délégué (matching
            // prénom + nom + email — déjà vérifié lors de la soumission).
            const norm = s => (s||"").trim().toLowerCase();
            const submitter = delegates.find(d =>
              norm(d.prenom) === norm(idea.prenom) &&
              norm(d.nom) === norm(idea.nom) &&
              norm(d.email) === norm(idea.email));
            if (!submitter) {
              alert("Impossible de retrouver le soumetteur de cette idée dans l'annuaire. "
                + "L'idée ne peut pas être convertie automatiquement.");
              return;
            }
            // Titre : 1ère ligne / 80 premiers caractères de l'idée
            const firstLine = (idea.texte || "").split(/\r?\n/)[0].trim();
            const titreSeed = firstLine.length > 80
              ? firstLine.slice(0, 77).trimEnd() + "…"
              : firstLine;
            setConvertIdeaSeed({
              fromIdeaId: idea.id,
              titre: titreSeed,
              message: idea.texte || "",
              createurId: submitter.id,
              createurPrenom: submitter.prenom || "",
              createurNom: submitter.nom || "",
              createurMutuelle: submitter.mutuelle || idea.mutuelle || "",
            });
          } : null}/>
      </div>
    </div>
    {questionModalElement}
    </>
  );

  // ── Page publique réponse invitation réunion ──────────────────────────────
  const _urlP=new URLSearchParams(window.location.search);
  const _paramR=_urlP.get("r");
  const _paramT=_urlP.get("t");
  if(window.location.pathname==="/repondre"&&_paramR&&_paramT){
    return <PageReponseReunionLazy reunionId={_paramR} token={_paramT}/>;
  }
  // ── Page publique invité événement externe (Sprint 2) ────────────────────
  // ?view=invite&t=TOKEN — RSVP, update coordonnées, +1, billet QR
  if(_urlP.get("view")==="invite"&&_paramT){
    return <PageInviteEventLazy token={_paramT}/>;
  }
  // ── Portail co-organisateur / partenaire externe (/co?t=TOKEN) ───────────
  // Accessible SANS session : tableau de bord des réponses + ajout d'invités.
  // Sans token (/co nu ou lien mort), la page propose le renvoi du lien
  // d'accès par e-mail (self-service partenaire).
  if(_urlP.get("view")==="partner"||window.location.pathname==="/co"){
    return <PagePartnerEventLazy token={_paramT||""}/>;
  }
  // ── Saisie dataviz augmentée (/?view=dataviz-saisie&t=TOKEN) ──────────────
  // Page publique tokenisée : saisie des indicateurs économiques sans session.
  if(_urlP.get("view")==="dataviz-saisie"&&_paramT){
    return <PageDatavizSaisieLazy token={_paramT}/>;
  }
  // ── Validation 1-clic des notes de frais depuis l'e-mail (/?view=ndf-valider&t=TOKEN) ──
  // Page publique tokenisée : contrôle/validation sans session (jamais de paiement).
  if(_urlP.get("view")==="ndf-valider"&&_paramT){
    return <NdfValiderTokenLazy token={_paramT}/>;
  }
  // ── Export comptable mensuel du cabinet (/?view=export-comptable&t=TOKEN) ──
  // Secours : la page est d'ordinaire montée avant <App/> (routesPubliques.js).
  if(_urlP.get("view")==="export-comptable"&&_paramT){
    return <PageExportComptableLazy token={_paramT}/>;
  }
  // ── Médiathèque : page à jeton des invités sans compte (12/10/2026) ──────
  // Secours, comme l'export : d'ordinaire montée avant <App/> (routesPubliques.js).
  if(_urlP.get("view")==="mediatheque-invite"&&_paramT){
    return <PageMediathequeInviteLazy token={_paramT} desabonner={_urlP.get("desabonner")==="1"}/>;
  }
  // ── Mes formations : pages à jeton (16/10/2026) ───────────────────────────
  // Secours : d'ordinaire montées avant <App/> (routesPubliques.js).
  if(_urlP.get("view")==="formation-emargement"&&_paramT){
    return <PageEmargementFormationLazy token={_paramT}/>;
  }
  if(_urlP.get("view")==="formation-formateur"&&_paramT){
    return <PageFormateurLazy token={_paramT}/>;
  }
  if(_urlP.get("view")==="mes-formations-invite"&&_paramT){
    return <PageMesFormationsInviteLazy token={_paramT}/>;
  }
  if(_urlP.get("view")==="mes-formations-lien"){
    return <PageMesFormationsInviteLazy token=""/>;
  }
  // Fenêtre de lecture ouverte depuis une page à jeton (20/10/2026) : son
  // adresse ne porte rien, le jeton lui vient de la page par postMessage.
  if(_urlP.get("view")==="formation-document-jeton"){
    return <LectureFenetreLazy jeton/>;
  }
  // ── Pôle RH : accord d'une personne en mobilité (18/10/2026) ──────────────
  // Secours : d'ordinaire montée avant <App/> (routesPubliques.js).
  if(_urlP.get("view")==="rh-mobilite"&&_paramT){
    return <PageMobiliteAccordLazy token={_paramT}/>;
  }
  // ── PV signé : signer depuis le lien du courriel (03/11/2026) ──────────────
  // Secours : d'ordinaire montée avant <App/> (routesPubliques.js).
  if(_urlP.get("view")==="pv-signature"&&_paramT){
    return <PvSignaturePageLazy token={_paramT}/>;
  }
  // ── Microsite public événement (Sprint 6) ────────────────────────────────
  // Accessible via 2 chemins (le rewrite Vercel garde l'URL navigateur en
  // /event/<slug> et NE met PAS de query string, donc on doit aussi détecter
  // le pathname — sinon on tombe dans le guard "pas de session" qui redirige
  // vers l'accueil Aramis) :
  //   1) /event/<slug>             (URL canonique partageable, sans query)
  //   2) ?view=event&slug=<slug>   (legacy + ancres internes)
  let _eventSlug = _urlP.get("slug");
  const _isEventPath = window.location.pathname.startsWith("/event/");
  if (_isEventPath && !_eventSlug) {
    _eventSlug = decodeURIComponent(window.location.pathname.slice("/event/".length).replace(/\/$/, ""));
  }
  if ((_urlP.get("view")==="event" || _isEventPath) && _eventSlug) {
    return <PageEventPublicLazy slug={_eventSlug}/>;
  }
  // ── Page Carrières publique d'une mutuelle (RH) ───────────────────────────
  // Accessible SANS session : /carrieres/<slug> (rewrite Vercel) ou
  // ?view=career&slug=<slug>. Brandée + intégrable en iframe (CSP /carrieres/*).
  let _careerSlug = _urlP.get("slug");
  const _isCareerPath = window.location.pathname.startsWith("/carrieres/");
  if (_isCareerPath && !_careerSlug) {
    _careerSlug = decodeURIComponent(window.location.pathname.slice("/carrieres/".length).replace(/\/$/, ""));
  }
  if ((_urlP.get("view")==="career" || _isCareerPath) && _careerSlug) {
    return <PageCareerPublicLazy slug={_careerSlug}/>;
  }
  // ── Portail salarié AUTONOME — salariés SANS compte Aramis ────────────────
  // /espace-salarie (URL de référence) ou /espace-rh + ?view=espace-rh
  // (historique, conservés : des liens sont déjà partis). Authentification
  // Firebase Auth dédiée avec mot de passe propre au portail.
  // Les salariés QUI ONT un compte Aramis passent, eux, par la vue
  // « mon-espace-salarie » à l'intérieur d'Aramis (voir plus bas) — ils n'ont
  // aucun mot de passe supplémentaire à créer.
  if (_urlP.get("view")==="espace-rh" || _urlP.get("view")==="espace-salarie"
      || window.location.pathname.startsWith("/espace-rh")
      || window.location.pathname.startsWith("/espace-salarie")) {
    return <RHEspaceSalarieLazy/>;
  }
  // ── Enquête à chaud formation (publique, token) ─────────────────────────────
  // ?view=enquete-formation&token=… — DOIT être testée AVANT le bloc générique
  // ?token=… ci-dessous (réservé au reset de mot de passe), sinon interceptée.
  if (_urlP.get("view")==="enquete-formation" && _urlP.get("token")) {
    return <PageEnqueteFormationLazy token={_urlP.get("token")}/>;
  }
  // Route ?view=document&id=XXX — ouvre un doc SharePoint via Aramis
  // (accès géré centralement par le compte de service, pas par les droits M365
  //  individuels du destinataire). Nécessite une session Aramis active.
  const _docId=_urlP.get("id");
  if(_urlP.get("view")==="document"&&_docId){
    return <DocumentViewerLazy docId={_docId} aramisSession={aramisSession} onNav={navTo}/>;
  }
  // Route /reset?token=xxx — réinitialisation mot de passe
  const _paramToken=_urlP.get("token");
  if(_paramToken){
    return <PageResetPasswordLazy token={_paramToken}/>;
  }
  if(view==="page-garde") return(
    <>
      <PageGarde onConnexion={()=>navTo("aramis-login")}/>
      {unlockAnim && <UnlockAnimation prenom={unlockAnim.prenom} slow={!!unlockAnim.demo} onDone={finishUnlock}/>}
    </>
  );

  // Guard global : si pas de session et vue non publique → forcer la connexion.
  // Exception : lien magique de vote (participant pré-identifié par e-mail).
  // themis-presence : cible des boutons de confirmation du mail de
  // convocation — token opaque vérifié par la CF, aucun compte requis.
  const PUBLIC_VIEWS = ["page-garde","aramis-login","themis-presence","themis-pouvoir","themis-pouvoir-confirmation","themis-emargement","themis-repas"];
  if(!aramisSession && !PUBLIC_VIEWS.includes(view) && !(view==="participant" && magicVote)) {
    return <CarteSessionExpireeLazy onReconnecter={()=>navTo("aramis-login")}/>;
  }

  // ── ENFORCEMENT D'ACCÈS (Étape 4) — inerte tant que ACCESS_ENFORCE_MODE=false.
  // Quand activé : bloque l'accès (deeplink inclus) aux vues non autorisées par
  // le modèle d'accès partagé (evaluateViewAccess). Home/transverse non bloquées.
  // rh-login = point d'entrée Firebase Auth des recruteurs externes (doit rester
  // accessible aux non-habilités pour récupérer leurs custom claims).
  // mon-espace-salarie = espace PERSONNEL du salarié : jamais soumis au modèle
  // d'accès métier (le serveur reste l'autorité — il n'ouvre l'espace qu'aux
  // porteurs d'un rhComptes actif).
  // Accès refusé (ACCESS_ENFORCE_MODE, inerte tant qu'il vaut false) : calculé
  // ici, rendu DANS la colonne de la coquille (lot L6, étape 1) au lieu d'un
  // retour anticipé qui remontait un second en-tête. Mêmes exceptions.
  const verdictAcces = (ACCESS_ENFORCE_MODE && aramisSession && !PUBLIC_VIEWS.includes(view)
      && view!=="ma-semaine" && view!=="accueil" && view!=="rh-login"
      && !MODULE_VIEWS.salarie.includes(view)
      && !(view==="participant" && magicVote))
    ? evaluateViewAccess(view, effectiveSession || aramisSession, delegates) : null;
  const accesRefuse = !!(verdictAcces && !verdictAcces.allowed);


  if(view==="aramis-login") return(
    <>
      <AramisLogin
        delegates={delegates} saveDelegate={saveDelegate}
        admins={admins} lists={lists} config={config}
        onLogin={loginAvecAnimation}
        onClose={()=>navTo("page-garde")}/>
      {unlockAnim && <UnlockAnimation prenom={unlockAnim.prenom} onDone={finishUnlock}/>}
    </>
  );

  // Mes formations (20/10/2026) : un document de session lu dans sa propre
  // fenêtre, ouverte par « Ouvrir dans une fenêtre » (membre depuis « Moi »,
  // organisateur depuis la session). Hors coquille, comme une page publique :
  // ni rail, ni en-tête, ni barre d'onglets. La session RÉELLE (le serveur
  // répond pour elle, comme « Mes formations » en « Voir comme ») ; le droit est
  // relu par le serveur à chaque page.
  if(view==="formation-document" && aramisSession) return <LectureFenetreLazy aramisSession={aramisSession}/>;



  // "Ma mutuelle" est desormais la MEME vue que "Engagement de ma mutuelle"
  // (fusion : donut engagement + listes intelligentes projets + sections dirigeant).

  // Module courant (barre latérale générique) — même contexte d'accès que le
  // menu principal (Header) et le menu de répartition.
  // La liste est calculee UNE fois : la barre laterale y cherche le module de la
  // vue, la barre d'onglets y prend son repli. Deux appels a buildNavModules
  // auraient pu diverger au moindre ecart de contexte d'acces.
  // « Voir comme » : le menu (rail, onglets, feuille « Modules », ⌥1 à ⌥9) est
  // celui de la personne affichée, comme l'était la barre de modules de
  // l'en-tête avant le lot L6 et comme le fil de l'en-tête (relecture L6).
  const modulesMenu = (() => {
    if (!aramisSession) return [];
    try {
      return buildNavModules({
        isAdmin: isAdminEffective,
        canAccessGelPpe: navCanAccessGelPpe(effectiveSession, isAdminEffective),
        canAccessHygie: navCanAccessHygie(effectiveSession, delegates, isAdminEffective),
        canAccessRHnav,
        isSessionLive: !!appState.session,
        isProprietaireBudget: estProprietaireBudget(effectiveSession),
        audit: drapeauAudit, isRhMaitre,
        isHabiliteHestia: drapeauHestia.habilite, hestiaAssistanteSeule: drapeauHestia.assistanteSeule,
        compteurs: { "athos-demandes": nDemandesEngagement },
      });
    } catch (_) { return []; }
  })();
  // « Administration » et « Super administration » : dans le rail après
  // Pilotage et dans la feuille « Modules » (demande de Pierre du 29/09/2026),
  // plus dans « Moi ». Mêmes conditions que l'ancienne rubrique de « Moi » :
  // le rôle de la session RÉELLE, super-admin strict, jamais celui de la
  // personne affichée en « Voir comme ». Ce sont les gardes des pages : les
  // vues d'administration lisent isAdminApp, SuperAdminPanel le rôle de
  // aramisSession. Un admin qui « voit comme » Pierre n'a donc pas la Super
  // administration, et un admin qui « voit comme » un membre garde la sienne.
  const showSuperAdmin = aramisSession?.role==="superadmin" || hasValidLegacySuperAdmin();
  const modulesAdmin = aramisSession ? buildNavAdmin({ isAdmin: isAdminApp, isSuperAdmin: showSuperAdmin }) : [];
  // « Mon espace salarié » (26/10/2026) : espace PERSONNEL de la forme d'un
  // module, juste sous « Ma semaine » dans le rail (pas un onzième module :
  // buildNavEspaceSalarie, navConstants.js). Droits de la personne connectée.
  const modulesPersonnels = aramisSession ? buildNavEspaceSalarie({ disponible: espaceSalarieEligible,
    estManager: espaceSalarieManager, ndf: ndfEligible, compteurs: compteursEspace }) : [];
  const modulesRail = [...modulesPersonnels, ...modulesMenu, ...modulesAdmin];
  const moduleCourant = (!aramisSession || ficheOuverteView === view)
    ? null : (moduleForView(modulesRail, view) || null);
  // Module de la VUE, pour html[data-module] (lot L3) : calculé à part de
  // moduleCourant, qui vaut null quand une fiche ouverte masque le rail — la
  // page reste pourtant dans son module.
  const cleModuleVue = (moduleForView(modulesMenu, view) || {}).key || null;
  // Onglet contextuel de la barre basse. Sur l'accueil, moduleForView ne renvoie
  // RIEN — MODULE_VIEWS n'a pas d'entree « ma-semaine », et lui en donner une
  // offrirait au passage un rail de 270 px a l'accueil du BUREAU. On retombe
  // donc sur le dernier module visite, puis sur le premier du menu : l'onglet
  // montre toujours une vraie destination, jamais le « Aucun » vide signale par
  // Pierre le 17/09. Un ref et non un state : rien a re-rendre, la valeur suit.
  if (moduleCourant) moduleRecentRef.current = moduleCourant;
  const moduleOnglet = moduleCourant || moduleRecentRef.current
    || modulesMenu.find(m => m.show !== false) || null;
  modulesRaccourcisRef.current = modulesMenu.filter(m => m && m.show !== false);
  // Rail (lot L6) : au bureau et sur l'iPad en paysage. Une fiche ouverte (qui
  // masquait déjà la barre latérale) le réduit aux icônes au bureau et le
  // retire sur l'iPad. Le tiroir « Sommaire » sert partout où le rail ne
  // porte pas le sommaire (doigt, bureau étroit, fiche ouverte).
  const ficheOuverte = !!aramisSession && ficheOuverteView === view;
  const railAffiche = !!aramisSession && (estBureau || (estTablette && tabletteLarge)) && !(ficheOuverte && !estBureau);
  const railEtroit = estBureau && (bureauEtroit || ficheOuverte);
  const sommaireEnTiroir = !railAffiche || railEtroit;
  // Rembourrage de la colonne (largeur commune des pages, 05/10/2026) : le
  // MÊME pour toute page de la coquille, qu'elle ait un module ou non. Il
  // n'était posé qu'avec un module : Ma semaine, Mon profil, la visite guidée,
  // Mon espace salarié et Mes notes de frais sortaient 24 px plus larges que
  // PLF & PLFSS et décalés de 12 px vers le rail. Restent sans rembourrage : la
  // fiche ouverte (fiche d'événement, fiche de séance : rail réduit, voulu),
  // l'accueil du téléphone (AccueilMobile, gouttière de 16 px du socle mobile)
  // et les pages publiques à jeton (PUBLIC_VIEWS : présence, pouvoir,
  // émargement, repas), rendues dans la coquille quand un membre est connecté :
  // leur colonne (.pj-colonne) garde sa largeur et sa marge du téléphone.
  const colonneRembourree = !!moduleCourant
    || (!!aramisSession && !ficheOuverte && !(estTel && view==="ma-semaine") && !PUBLIC_VIEWS.includes(view));
  const REMBOURRAGE_COLONNE = "12px calc(12px + var(--sar,0px)) 0 calc(12px + var(--sal,0px))";
  // Bandeau de commande (27/10/2026) : la roue dentée des réglages d'une page
  // suit le rôle de la session RÉELLE, comme les gardes des pages et
  // l'Administration du rail (« Voir comme » n'y change rien).
  return(
    <TourProvider aramisSession={aramisSession} onNav={navTo}>
    <SessionReelleContexte.Provider value={droitsReels(!!aramisSession && isAdminApp, !!aramisSession && showSuperAdmin)}>
    <div className="ev">
      <Header {...headerProps}/>
      <AttributModuleHtml cle={cleModuleVue}/>
      <AttributOnglets actif={avecOnglets && !!aramisSession}/>
      <ProgressionNavigation actif={!!vueEnChargement}
        couleur={vueEnChargement ? ((moduleForView(modulesRail, vueEnChargement) || {}).color || NAV_COLORS.pilotage) : null}/>
      {/* Hauteur de coquille : --vh100 (= 100vh, et 100dvh là où c'est supporté : la
          hauteur RÉELLEMENT visible sur iPhone — la variable porte le repli, qu'un objet
          de style JS ne sait pas exprimer) moins --h-coquille. L'ancien
          calc(100vh - 56px) cumulait deux erreurs sur iPhone — 100vh y vaut la hauteur
          barres masquées, et le header y fait 48px, pas 56 : toute page de module, même
          « Accès réservé », traînait ~150px de vide à faire défiler.
          AU BUREAU --h-coquille vaut le 56px EN DUR d'avant le chantier : la coquille
          rend exactement comme avant (voir la déclaration de --h-coquille — le header du
          bureau mesure ~62px, prendre la mesure réelle aurait remonté le pied de page de
          6px). La hauteur mesurée n'est prise QU'au doigt. */}
      {/* La zone sûre LATÉRALE, que le socle ne posait que sur le header : en
          PAYSAGE, l'encoche de l'iPhone mange le bord gauche et rognait le
          contenu de toutes les pages de module. Hors appareil à encoche,
          --sal/--sar valent 0 et le calc() rend exactement 12px : le rendu du
          bureau ne bouge pas. */}
      {/* Coquille (lot L6) : le RAIL (bureau, iPad en paysage) porte « Ma
          semaine », les modules et le sommaire du module actif. Au doigt, au
          bureau étroit et fiche ouverte, les pages du module passent par le
          bouton « Sommaire » (ModuleSidebar en tiroir). Source unique
          buildNavModules (CLAUDE.md, « Menu desktop = menu mobile »). */}
      <div className="cq-corps" data-rail={railAffiche ? "1" : undefined} data-sommaire={sommaireEnTiroir ? "tiroir" : "rail"}
        style={{minHeight:"calc(var(--vh100) - var(--h-coquille))",paddingBottom:"max(var(--sab,0px), var(--h-onglets,0px))"}}>
      {/* Repère du haut de page : l'en-tête y lit (IntersectionObserver) quand
          afficher le titre de la page au doigt. */}
      <div data-cq-sentinelle="" aria-hidden="true" style={{position:"absolute",top:0,left:0,width:1,height:140,pointerEvents:"none"}}/>
      {railAffiche && (
        <RailNavigation modules={modulesMenu} admin={modulesAdmin} personnel={modulesPersonnels} view={view} moduleActif={moduleForView(modulesRail, view)}
          onNav={navTo} onMoi={() => setMoiOuvert(true)} session={effectiveSession || aramisSession}
          etroit={railEtroit}/>
      )}
      <div className="cq-contenu" style={moduleCourant ? {display:"flex",alignItems:"flex-start",alignContent:"flex-start",gap:"var(--cq-contenu-gap, 16px)",flexWrap:"wrap",padding:REMBOURRAGE_COLONNE}
          : colonneRembourree ? {padding:REMBOURRAGE_COLONNE} : {paddingLeft:railAffiche ? 0 : "var(--sal,0px)",paddingRight:"var(--sar,0px)"}}>
        {moduleCourant && sommaireEnTiroir && <ModuleSidebar module={moduleCourant} view={pageServie(view)} onNav={navTo} tiroir/>}
        {/* La colonne de la vue ne porte AUCUNE clé : un lien vers la vue courante
            ne la remonte jamais (src/parametresVue.js, lienVueCourante.test.js). */}
        <div style={moduleCourant ? {flex:"1 1 560px",minWidth:0} : undefined}>
        {/* UNE SEULE COQUILLE (lot L6, étape 1). Les huit écrans qui remontaient
            leur propre en-tête par un retour anticipé (ideas, Accès refusé,
            mon-profil, emailing, classement, questions-historique, accueil,
            athos-ma-mutuelle) sont des enfants de cette colonne : l'en-tête,
            le rail, la barre d'onglets, la messagerie, la visite guidée et le
            pied de page ne sont plus démontés en y entrant (et l'abonnement
            aux notifications n'est plus recréé à chaque passage). C'est la
            condition des transitions partagées (étape 2). */}
        {accesRefuse ? <AccessDenied view={view} reason={raisonAcces(verdictAcces)}/> : (<>
        {view==="ideas" && (
        <IdeaBoxPublicLazy delegates={delegates} saveIdea={saveIdea} onNav={navTo}
          ideas={ideas} deleteIdea={deleteIdea} isAdmin={isAdminApp} aramisSession={effectiveSession}
          onConvertToQuestion={isAdminApp ? (idea) => {
            // Résolution du soumetteur de l'idée vers un délégué (matching
            // prénom + nom + email — déjà vérifié lors de la soumission).
            const norm = s => (s||"").trim().toLowerCase();
            const submitter = delegates.find(d =>
              norm(d.prenom) === norm(idea.prenom) &&
              norm(d.nom) === norm(idea.nom) &&
              norm(d.email) === norm(idea.email));
            if (!submitter) {
              alert("Impossible de retrouver le soumetteur de cette idée dans l'annuaire. "
                + "L'idée ne peut pas être convertie automatiquement.");
              return;
            }
            // Titre : 1ère ligne / 80 premiers caractères de l'idée
            const firstLine = (idea.texte || "").split(/\r?\n/)[0].trim();
            const titreSeed = firstLine.length > 80
              ? firstLine.slice(0, 77).trimEnd() + "…"
              : firstLine;
            setConvertIdeaSeed({
              fromIdeaId: idea.id,
              titre: titreSeed,
              message: idea.texte || "",
              createurId: submitter.id,
              createurPrenom: submitter.prenom || "",
              createurNom: submitter.nom || "",
              createurMutuelle: submitter.mutuelle || idea.mutuelle || "",
            });
          } : null}/>
        )}
        {/* Mes formations (16/10/2026) : les fiches « formation suivie » de la
            personne seule, ouvertes depuis « Moi ». */}
        {view==="mes-formations" && (
          <MesFormationsMembreLazy aramisSession={effectiveSession} apercu={!!impersonateAs}/>
        )}
        {view==="mon-profil" && (
        <MonProfilViewLazy
          aramisSession={effectiveSession}
          apercu={!!impersonateAs}
          delegates={delegates}
          saveDelegate={saveDelegate}
          loginAramis={loginAramis}
          reunions={reunions}
          projects={projects}
          questions={accueilQuestions}
          updateInviteStatut={updateInviteStatut}
          onNav={navTo}
          config={config}
          onClose={()=>setView("athos-ma-mutuelle")}/>
        )}
        {view==="emailing" && (isAdminApp ? (
        <EmailingViewLazy
          aramisSession={aramisSession}
          delegates={delegates}
          saveDelegate={saveDelegate}
          lists={lists}
          config={config}
          gouvernance={gouvernance}/>
        ) : <AccessDenied view={view} reason="L'emailing direct est réservé à l'équipe d'administration d'Aramis."/>)}
        {/* Vue « classement » : points et classements RETIRÉS (lot L1a, 27/09/2026 :
            aucune interface ne compare des personnes ou des mutuelles). La string
            reste une vue VALIDE À JAMAIS (favoris, liens, vue mémorisée) : elle mène au
            profil, en attendant le carnet de mandat. Déclarée dans LEGACY_REDIRECTS. */}
        {view==="classement" && (
      <LegacyViewRedirectLazy to="mon-profil" onNav={navTo} label="Redirection vers votre profil…"/>
        )}
        {/* Tableau de bord Activité retiré le 30/09/2026 (lot L11d, demande de
            Pierre) : la vue reste valide à jamais (favoris, vue mémorisée). */}
        {/* Cartographie et plan d'audit proposés à une mutuelle : retirés le
            13/10/2026 (décision de Pierre) ; vues valides à jamais. */}
        {view==="risques-propositions" && (
      <LegacyViewRedirectLazy to="risques" onNav={navTo} label="Redirection vers la cartographie des risques…"/>
        )}
        {view==="audit-propositions" && (
      <LegacyViewRedirectLazy to="audit" onNav={navTo} label="Redirection vers les missions d'audit…"/>
        )}
        {/* « Échange avec Cowork » de la Conformité : sorti du rail le
            13/10/2026 pour Super administration › Synchronisation ; vue valide
            à jamais. */}
        {view==="conformite-cowork" && (
      <LegacyViewRedirectLazy to="super-synchronisation" onNav={navTo} label="Redirection vers la synchronisation avec Cowork…"/>
        )}
        {view==="athos-dashboard" && (
      <LegacyViewRedirectLazy to="athos" onNav={navTo} label="Redirection vers l'accueil Activité…"/>
        )}
        {/* « Ressources documentaires » : page supprimée de longue date, encore
            cible de liens envoyés par les campagnes de relance ; valide à jamais
            (LEGACY_REDIRECTS), jamais de page blanche (11/10/2026). */}
        {view==="ressources" && (
      <LegacyViewRedirectLazy to="athos" onNav={navTo} label="Redirection vers l'accueil Activité…"/>
        )}
        {view==="questions-historique" && (
        <QuestionsHistoriqueViewLazy
          questions={accueilQuestions}
          aramisSession={effectiveSession}
          delegates={delegates}
          isAdmin={isAdminApp}
          onNav={navTo}
          onNewQuestion={()=>setShowQuestionModal(true)}
          onReply={async(qId,msg)=>{
            const {doc,updateDoc,arrayUnion}=await import("firebase/firestore");
            const {db}=await import("./firebase");
            await updateDoc(doc(db,"questions",qId),{messages:arrayUnion(msg)});
          }}
          onClose2={async(qId)=>{
            const {doc,updateDoc}=await import("firebase/firestore");
            const {db}=await import("./firebase");
            await updateDoc(doc(db,"questions",qId),{statut:"clos"});
          }}
          onReopen={async(qId)=>{
            // Permission gérée par QuestionsHistoriqueView/QuestionThread :
            // bouton visible uniquement si créateur ou admin. On laisse passer
            // tous les appels ici car la garde UI suffit pour V1 (cohérent
            // avec le pattern actuel de l'app).
            const q = accueilQuestions.find(x => x.id === qId);
            if (!q) return;
            if (q.createurId !== aramisSession?.id && !isAdminApp) return;
            const {doc,updateDoc}=await import("firebase/firestore");
            const {db}=await import("./firebase");
            await updateDoc(doc(db,"questions",qId),{statut:"ouvert"});
          }}
          onDelete={isAdminApp ? deleteQuestion : null}/>
        )}
        {/* Lot L11p (30/09/2026) : vues mortes, valides à jamais (favoris, liens,
            vue mémorisée), déclarées dans LEGACY_REDIRECTS. « accueil » n'est
            rendue qu'à un connecté : hors connexion, la garde de session répond. */}
        {view==="accueil" && (
      <LegacyViewRedirectLazy to="ma-semaine" onNav={navTo} label="Redirection vers Ma semaine…"/>
        )}
        {view==="questions" && (
      <LegacyViewRedirectLazy to="questions-historique" onNav={navTo} label="Redirection vers Questions & entraide…"/>
        )}
        {/* Affaires publiques (02/10/2026 ; module à part, juste après Activité,
            depuis le 12/10/2026 : `module` est son module de modulesMenu). Le radar reçoit la session
            RÉELLE (le serveur identifie l'appelant par son jeton) ; ses gestes
            d'administration exigent isAdminApp ET radarLire.estAdmin, jamais en
            « Voir comme » (apercu). */}
        {view==="affaires-publiques" && aramisSession && (
          <AffairesPubliquesViewLazy onNav={navTo} module={modulesMenu.find(x=>x.key==="ap")} conformite={modulesMenu.find(x=>x.key==="hygie")}
            aramisSession={aramisSession} equipe={isAdminApp && !impersonateAs} apercu={!!impersonateAs}/>
        )}
        {/* PLF & PLFSS 2027 (04/10/2026) : session RÉELLE (le serveur identifie
            l'appelant par son jeton) ; « Collecter maintenant » exige isAdminApp,
            jamais en « Voir comme » (apercu). */}
        {view==="plf-plfss-2027" && aramisSession && (
          <PlfPlfssLazy aramisSession={aramisSession} estAdminSession={isAdminApp} apercu={!!impersonateAs} onNav={navTo}/>
        )}
        {/* Rapports AMO-AMC (05/10/2026) : au menu des administrateurs le temps
            que Pierre relise le contenu ; la page, qui ne lit aucune donnée
            serveur (pièces publiques), reste valide pour tout connecté qui en a
            le lien, comme PLF & PLFSS. onNav : « Et maintenant ? » mène à PLF &
            PLFSS 2027 et aux Textes de loi. */}
        {view==="rapports-amo-amc" && aramisSession && (
          <RapportsAmoAmcLazy onNav={navTo} />
        )}
        {/* Textes de loi (lot B1.2) : session RÉELLE (le serveur identifie
            l'appelant par son jeton) ; « Suivre » et « Arrêter le suivi »
            exigent isAdminApp, jamais en « Voir comme » (apercu). */}
        {view==="textes-de-loi" && aramisSession && (
          <TextesLoiLazy aramisSession={aramisSession} estAdminSession={isAdminApp} apercu={!!impersonateAs} onNav={navTo}/>
        )}
        {/* Refonte des Affaires publiques (12/10/2026) : le texte en clair,
            Parlementaires, Votes et scrutins, ouverts à tout connecté ; notre
            position (texte en clair, article) pour l'équipe d'Enio de la
            session RÉELLE seule (isAdminApp, jamais en « Voir comme »). Les
            positions publiques des élus (lot L4, 18/10/2026) : droit D2 relu
            par le serveur ; l'annuaire sert le miroir d'affichage. */}
        {view==="texte-en-clair" && aramisSession && (
          <TexteEnClairLazy aramisSession={aramisSession} estAdminSession={isAdminApp} apercu={!!impersonateAs} onNav={navTo}/>
        )}
        {view==="article-de-loi" && aramisSession && (
          <FicheArticleLazy aramisSession={aramisSession} estAdminSession={isAdminApp} apercu={!!impersonateAs} onNav={navTo}/>
        )}
        {view==="parlementaires" && aramisSession && (
          <ParlementairesLazy aramisSession={aramisSession} delegates={delegates} apercu={!!impersonateAs} onNav={navTo}/>
        )}
        {view==="parlementaire" && aramisSession && (
          <FicheParlementaireLazy aramisSession={aramisSession} delegates={delegates} apercu={!!impersonateAs} onNav={navTo}/>
        )}
        {view==="votes-et-scrutins" && aramisSession && (
          <VotesScrutinsLazy aramisSession={aramisSession} onNav={navTo}/>
        )}
        {view==="scrutin" && aramisSession && (
          <FicheScrutinLazy aramisSession={aramisSession} onNav={navTo}/>
        )}
        {view==="amendement" && aramisSession && (
          <PageAmendementLazy aramisSession={aramisSession} estAdminSession={isAdminApp} apercu={!!impersonateAs} onNav={navTo}/>
        )}
        {/* Notes institutionnelles (B6) : administrateurs de la session RÉELLE,
            jamais en « Voir comme » (le serveur agit sur l'identité du jeton :
            il refuse tout autre appelant). Aucune lecture par les membres. */}
        {(view==="notes-institutionnelles" || view==="note-institutionnelle" || view==="notes-institutionnelles-modeles") && aramisSession && (isAdminApp && !impersonateAs
          ? <NotesModuleLazy view={view} onNav={navTo} sessionId={aramisSession.id}
              noteId={new URLSearchParams(window.location.search).get("note")}/>
          : <AccessDenied view={view} reason={impersonateAs ? "Les notes institutionnelles s'ouvrent dans votre propre session : quittez « Voir comme »." : "Les notes institutionnelles sont réservées aux administrateurs d'Enio."}/>)}
        {/* Notes d'Enio reçues (lot L4, 12/10/2026) : tout membre connecté, dans
            sa propre session (le serveur, notesRecues, lit l'identité du jeton :
            en « Voir comme », il servirait les notes de la session réelle) ; un
            compte invité ne lit rien. */}
        {view==="notes-recues" && aramisSession && (aramisSession.role==="invite" || impersonateAs
          ? <AccessDenied view={view} reason={impersonateAs ? "Les notes reçues se lisent dans votre propre session : quittez « Voir comme »." : "Les notes d'Enio se lisent avec un compte membre d'Aramis : un compte invité ne les lit pas."}/>
          : <NotesModuleLazy view={view} onNav={navTo} sessionId={aramisSession.id}
              diffusionId={new URLSearchParams(window.location.search).get("diffusion")}/>)}
        {/* Doctrine d'Enio (socle) : l'atelier, équipe d'Enio, session RÉELLE,
            jamais en « Voir comme » (le serveur agit sur l'identité du jeton :
            il refuse tout autre appelant). Les membres lisent les synthèses
            publiées dans « Positions d'Enio » (doctrine-positions). La file des
            propositions des membres (doctrine-propositions, lot L3) aussi, et
            les débats (doctrine-debats, doctrine-debat ?debat=<id>, lot L4). */}
        {(view==="doctrine" || view==="doctrine-theme" || view==="doctrine-debats" || view==="doctrine-debat" || view==="doctrine-import" || view==="doctrine-propositions") && aramisSession && (isAdminApp && !impersonateAs
          ? <DoctrineModuleLazy view={view} onNav={navTo} sessionId={aramisSession.id}
              themeId={new URLSearchParams(window.location.search).get("theme")}
              debatId={new URLSearchParams(window.location.search).get("debat")}/>
          : <AccessDenied view={view} reason={impersonateAs ? "La doctrine d'Enio s'ouvre dans votre propre session : quittez « Voir comme »." : "L'atelier de la doctrine d'Enio est réservé à l'équipe d'Enio ; ses positions publiées se lisent dans « Positions d'Enio »."}/>)}
        {/* Positions d'Enio (doctrine ouverte, lot L2) : tout membre connecté ;
            un compte invité ne les lit pas (le serveur, doctrineMembres, le
            refuse aussi, sur le jeton Aramis de la session RÉELLE). En « Voir
            comme », la page est celle de tous les membres et l'atelier ne s'y
            propose pas. « Mes propositions » (doctrine-mes-propositions, lot L3) :
            même morceau ; en « Voir comme », ni dépôt ni lecture (le serveur
            agirait sur la session réelle). */}
        {(view==="doctrine-positions" || view==="doctrine-mes-propositions") && aramisSession && (aramisSession.role==="invite" || (effectiveSession && effectiveSession.role==="invite")
          ? <AccessDenied view={view} reason="Les positions d'Enio se lisent avec un compte membre d'Aramis : un compte invité ne les lit pas."/>
          : <DoctrinePositionsLazy onNav={navTo} sessionId={aramisSession.id} estAdminSession={isAdminApp && !impersonateAs} view={view} voirComme={!!impersonateAs}
              themeId={new URLSearchParams(window.location.search).get("theme")}
              propositionId={new URLSearchParams(window.location.search).get("proposition")}/>)}
        {view==="radar-presidentielle-2027" && aramisSession && (
          <RadarPresidentielleLazy aramisSession={aramisSession} estAdminSession={isAdminApp} apercu={!!impersonateAs} onNav={navTo}/>
        )}
        {/* « Ma mutuelle » est la MÊME vue que « Engagement de ma mutuelle »
            (donut d'engagement, listes de projets, sections dirigeant). */}
        {view==="athos-ma-mutuelle" && (
          <EngagementMutuelleViewLazy
            aramisSession={effectiveSession}
            apercu={!!impersonateAs}
            delegates={delegates}
            lists={lists}
            projects={projects}
            reunions={reunions}
            questions={accueilQuestions}
            leaderboard={leaderboard}
            onNav={navTo}
            getStatutMaturite={getStatutMaturite}
            STATUTS_MATURITE={STATUTS_MATURITE}
            RisqueBadges={RisqueBadges}
            renderBold={renderBold}/>
        )}
        {view==="tour-hub" && (
          <TourHubPageLazy aramisSession={effectiveSession} onBack={()=>navTo("accueil")}/>
        )}
        {view==="athos"&&(()=>{
          const isAdminLocal = !!(effectiveSession?.role==="superadmin"||effectiveSession?.role==="admin");
          return (
            <AthosHomeV2Lazy
              onNav={navTo}
              module={modulesMenu.find(x=>x.key==="athos")}
              projects={projects}
              reunions={reunions}
              delegates={delegates}
              lists={lists}
              aramisSession={effectiveSession}
              isAdmin={isAdminLocal}
              demandesAValider={isAdminLocal&&isAdminApp?nDemandesEngagement:null}
              getStatutMaturite={getStatutMaturite}
              voirComme={!!impersonateAs}
            />
          );
        })()}
        {/* Ancienne home Activité (rollback depuis 05/2026), puis « Missions
            statutaires » et « Réviser la classification » (dépubliées le
            27/08/2026) : routes valides à jamais, redirigées (lot L11p). */}
        {view==="athos-home-classique"&&(
          <LegacyViewRedirectLazy to="accueil-athos" onNav={navTo} label="Redirection vers l'accueil Activité…"/>
        )}
        {(view==="athos-migration-review"||view==="athos-missions-statutaires")&&(
          <LegacyViewRedirectLazy to="athos-projets" onNav={navTo} label="Redirection vers Projets, offres & solutions…"/>
        )}
        {/* Alias de la page Projets (30/09/2026) : même écran, sans clé ; la
            catégorie devient un paramètre, le projet visé est transmis. */}
        {(view==="athos-solutions"||view==="athos-missions"||view==="athos-projets-classique")&&(
          <LegacyViewRedirectLazy to="athos-projets" onNav={navTo} paramKeys={["projectId","categorie"]}
            params={view==="athos-solutions" ? {categorie:"solution"} : view==="athos-missions" ? {categorie:"mission"} : null}
            label="Redirection vers Projets, offres & solutions…"/>
        )}
        {/* Ancien accueil Agora → accueil Gouvernance (lot 3, 04/09/2026). */}
        {view==="agora"&&(
          <LegacyViewRedirectLazy to="accueil-gouv" onNav={navTo}/>
        )}
        {/* Vitrines de module (refonte accueil) — pilotes Nos Mutuelles + Conformité.
            Règle de Pierre (11/10/2026) : la page d'un module et le rail
            montrent les MÊMES entrées. Toute vitrine lit `modulesMenu` (droits,
            drapeaux de l'audit interne et d'Hestia, compteurs compris), jamais
            une reconstruction de buildNavModules : celle de Conformité, sans
            le drapeau, cachait « Audit interne » que le rail affichait
            (verrou : src/vitrinesAlignees.test.js). */}
        {view==="accueil-mutuelles"&&(()=>{
          const _m=modulesMenu.find(x=>x.key==="mutuelles");
          const _nbMut=(lists||[]).filter(l=>!/enio/i.test(l.name||"")).length;
          return <ModuleVitrineLazy module={_m} onNav={navTo}
            heroStats={_nbMut?[{value:_nbMut,label:"mutuelles membres"}]:[]}
            openRoute="athos-annuaire" openLabel="Ouvrir l'annuaire"/>;
        })()}
        {view==="accueil-conformite"&&(()=>{
          const _m=modulesMenu.find(x=>x.key==="hygie");
          return <ModuleVitrineLazy module={_m} onNav={navTo}
            openRoute="veille-juridique" openLabel="Ouvrir la veille juridique"/>;
        })()}
        {view==="accueil-athos"&&(()=>{
          /* Le VRAI droit (session affichée, comme le menu) : « Demandes
             d'engagement » n'apparaît qu'aux administrateurs (lot L11d-bis). */
          const _m=modulesMenu.find(x=>x.key==="athos");
          const _hs=[];
          if((projects||[]).length) _hs.push({value:(projects||[]).length,label:"projets"});
          if((reunions||[]).length) _hs.push({value:(reunions||[]).length,label:"réunions"});
          return <ModuleVitrineLazy module={_m} onNav={navTo} heroStats={_hs}
            counts={{"athos-projets":(projects||[]).length,"athos-reunions":(reunions||[]).length,
              ...(isAdminEffective&&nDemandesEngagement>0?{"athos-demandes":nDemandesEngagement}:{})}}
            openRoute="athos" openLabel="Ouvrir l'accueil"/>;
        })()}
        {/* Accueil Gouvernance unique : les quatre instances d'abord. */}
        {view==="accueil-gouv"&&(
          <GouvernanceAccueilLazy onNav={navTo} module={modulesMenu.find(x=>x.key==="gouv")} appState={appState} ideas={ideas} questions={accueilQuestions}
            aramisSession={effectiveSession} delegates={delegates}/>
        )}
        {view==="accueil-pilotage"&&(isAdminEffective ? (()=>{
          /* Le MÊME menu que le rail (`modulesMenu`), droits compris : une
             reconstruction sans `isProprietaireBudget` masquait « Budget » de
             cette vitrine alors que le rail l'affichait (02/10/2026). */
          const _m=modulesMenu.find(x=>x.key==="pilotage");
          const _hs=[];
          const _prep=((appState&&appState.pendingSessions)||[]).length;
          if(_prep) _hs.push({value:_prep,label:"séance(s) en préparation"});
          /* Lot L7 : le signal s'éteint à la clôture, au plus tard 12 h après l'ouverture. */ {const _sig=signalSeance(appState&&appState.session); if(_sig.etat==="ouverte") _hs.push({value:1,label:"séance en cours"}); else if(_sig.etat==="non-cloturee") _hs.push({value:1,label:"séance à clôturer"});}
          return <ModuleVitrineLazy module={_m} onNav={navTo} heroStats={_hs}
            openRoute="pilotage-seances" openLabel="Ouvrir les séances"/>;
        })() : <AccessDenied reason="Le pilotage interne est réservé à l'équipe d'administration d'Aramis."/>)}
        {/* accueil-rh unifié : alias de l'accueil RH brandé (RHHome). La vitrine
            générique RH a été retirée pour ne plus avoir deux pages d'accueil
            confondantes — le rendu passe par RHModule (view "rh") ci-dessous. */}
        {view==="accueil-marches"&&(()=>{
          const _m=modulesMenu.find(x=>x.key==="exploration");
          return <ModuleVitrineLazy module={_m} onNav={navTo}
            openRoute="veille" openLabel="Ouvrir la veille AO"/>;
        })()}
        {view==="exploration"&&(
          <ExplorationHomeLazy
            onNav={navTo}
            delegates={delegates}
            modules={modulesMenu}
          />
        )}
        {/* Module Hygie — DPO/RGPD. Wrapping DPOGate (auth + chiffrement)
            avant rendu de HygieHome. Sprint 1 : seule la home est rendue ;
            les sous-routes hygie-* seront ajoutées dans les sprints suivants. */}
        {view==="hygie"&&(
          <DPOGateLazy
            aramisSession={effectiveSession}
            delegates={delegates}
            lists={lists}
            choixEspace>
            <HygieHomeLazy onNav={navTo} module={modulesMenu.find(x=>x.key==="hygie")}/>
          </DPOGateLazy>
        )}
        {view==="hygie-registre"&&(
          <DPOGateLazy
            aramisSession={effectiveSession}
            delegates={delegates}
            lists={lists}>
            <HygieRegistreLazy onNav={navTo} aramisSession={effectiveSession}/>
          </DPOGateLazy>
        )}
        {view==="hygie-library"&&(
          <DPOGateLazy
            aramisSession={effectiveSession}
            delegates={delegates}
            lists={lists}>
            <HygieLibraryLazy onNav={navTo} aramisSession={effectiveSession}/>
          </DPOGateLazy>
        )}
        {view==="hygie-aipd"&&(
          <DPOGateLazy
            aramisSession={effectiveSession}
            delegates={delegates}
            lists={lists}>
            <HygieAIPDLazy onNav={navTo} aramisSession={effectiveSession}/>
          </DPOGateLazy>
        )}
        {view==="hygie-dsar"&&(
          <DPOGateLazy
            aramisSession={effectiveSession}
            delegates={delegates}
            lists={lists}>
            <HygieDSARLazy onNav={navTo} aramisSession={effectiveSession}
              delegates={delegates} projects={projects} reunions={reunions} lists={lists}/>
          </DPOGateLazy>
        )}
        {view==="hygie-violations"&&(
          <DPOGateLazy
            aramisSession={effectiveSession}
            delegates={delegates}
            lists={lists}>
            <HygieViolationsLazy onNav={navTo} aramisSession={effectiveSession}/>
          </DPOGateLazy>
        )}
        {view==="hygie-subprocessors"&&(
          <DPOGateLazy
            aramisSession={effectiveSession}
            delegates={delegates}
            lists={lists}>
            <HygieSubprocessorsLazy onNav={navTo} aramisSession={effectiveSession}/>
          </DPOGateLazy>
        )}
        {view==="hygie-formation"&&(
          <DPOGateLazy
            aramisSession={effectiveSession}
            delegates={delegates}
            lists={lists}>
            <HygieFormationLazy onNav={navTo} aramisSession={effectiveSession}
              delegates={delegates} lists={lists} gouvernance={gouvernance}/>
          </DPOGateLazy>
        )}
        {view==="hygie-dashboard"&&(
          <DPOGateLazy
            aramisSession={effectiveSession}
            delegates={delegates}
            lists={lists}>
            <HygieDashboardLazy onNav={navTo} aramisSession={effectiveSession}/>
          </DPOGateLazy>
        )}
        {view==="hygie-library-admin"&&(
          <DPOGateLazy
            aramisSession={effectiveSession}
            delegates={delegates}
            lists={lists}>
            <HygieLibraryAdminLazy onNav={navTo} aramisSession={effectiveSession}/>
          </DPOGateLazy>
        )}
        {/* La veille RGPD de Calypso est retirée le 21/10/2026 (décision de
            Pierre) : elle vit dans Affaires publiques › Veille juridique et
            réglementaire, famille « CNIL, DORA & cybersécurité »
            présélectionnée. Vue valide à jamais (favoris, liens). */}
        {view==="hygie-veille" && (
      <LegacyViewRedirectLazy to="veille-juridique" params={{ famille: "cnil_cyber" }} onNav={navTo} label="Redirection vers la veille juridique et réglementaire…"/>
        )}
        {view==="hygie-forum"&&(
          <DPOGateLazy
            aramisSession={effectiveSession}
            delegates={delegates}
            lists={lists}>
            <HygieForumLazy onNav={navTo} aramisSession={effectiveSession}/>
          </DPOGateLazy>
        )}
        {view==="hygie-annuaire"&&(
          <DPOGateLazy
            aramisSession={effectiveSession}
            delegates={delegates}
            lists={lists}>
            <HygieAnnuaireLazy onNav={navTo} aramisSession={effectiveSession}/>
          </DPOGateLazy>
        )}
        {view==="hygie-reprise"&&(
          <DPOGateLazy
            aramisSession={effectiveSession}
            delegates={delegates}
            lists={lists}>
            <HygieRepriseLazy onNav={navTo} aramisSession={effectiveSession}/>
          </DPOGateLazy>
        )}
        {view==="ma-semaine" && aramisSession && (
          /* « Tout Aramis en un coup d'œil » (RepartitionMenu) a quitté Ma
             semaine le 29/09/2026 (demande de Pierre) : il répétait le rail,
             la feuille « Modules » et la palette ⌘K, qui servent déjà tous
             les modules et l'administration (synthèse §8 : pas de page-portail
             qui répète le rail). */
          <MaSemaineViewLazy
            aramisSession={effectiveSession} appState={appState} gouvernance={gouvernance}
            delegates={delegates}
            lists={lists}
            projects={projects}
            meetings={meetings}
            reunions={reunions}
            questions={accueilQuestions}
            auditLog={auditLog}
            config={config}
            saveDelegate={saveDelegate}
            hestia={drapeauHestia}
            voirComme={!!impersonateAs}
            onNav={navTo}/>
        )}
        {/* "athos-engagement-mutuelle" est un alias historique de "athos-ma-mutuelle"
            depuis la fusion des deux pages. Accessible a tous les membres ; les
            sections dirigeantes se declenchent automatiquement selon le role. */}
        {view==="athos-engagement-mutuelle" && aramisSession && (
          <EngagementMutuelleViewLazy
            aramisSession={effectiveSession}
            apercu={!!impersonateAs}
            delegates={delegates}
            lists={lists}
            projects={projects}
            reunions={reunions}
            questions={accueilQuestions}
            leaderboard={leaderboard}
            onNav={navTo}
            getStatutMaturite={getStatutMaturite}
            STATUTS_MATURITE={STATUTS_MATURITE}
            RisqueBadges={RisqueBadges}
            renderBold={renderBold}/>
        )}
        {(view==="athos-projets"||view==="athos-reunions"||view==="athos-annuaire"||view==="athos-implantations"||view==="athos-domaines-poles"||view==="historiques")&&(
          (()=>{
            // En mode impersonation : les droits admin sont ceux de la personne impersonée
            const isAdmin = impersonateAs
              ? !!(effectiveSession?.role==="superadmin"||effectiveSession?.role==="admin")
              : !!(aramisSession?.role==="superadmin"||aramisSession?.role==="admin"||hasValidLegacyAdmin());
            const hasAccess = aramisSession || isAdmin;
            return hasAccess?(
              <AthosModule
                view={view} onNav={navTo}
                projects={projects} saveProject={saveProject} deleteProject={deleteProject} setEngagement={setEngagement}
                meetings={meetings} saveMeeting={saveMeeting} deleteMeeting={deleteMeeting}
                lists={lists} delegates={delegates} saveDelegate={saveDelegate} isAdmin={isAdmin}
                externes={externes} saveExterne={saveExterne}
                saveReunion={saveReunion} updateInviteStatut={updateInviteStatut}
                reunions={reunions} aramisSession={effectiveSession} sessionReelle={aramisSession}
                questions={accueilQuestions}
                config={config}
                gouvernance={gouvernance} saveGouvernance={saveGouvernance}
                doodleAutoOpen={doodleAutoOpen}
                leaderboard={leaderboard}
                apercu={!!impersonateAs}
              />
            ):(
              <div style={{display:"flex",alignItems:"center",justifyContent:"center",minHeight:"calc(var(--vh100) * 0.6)"}}>
                <div className="card" style={{maxWidth:360,textAlign:"center",padding:32}}>
                  <div style={{fontSize:"var(--fs-page)",marginBottom:12}}><IconeTexte nom="lock" titre="Verrouillé"/></div>
                  <div style={{fontWeight:700,fontSize:"var(--fs-corps)",marginBottom:8,color:"var(--text-main)"}}>
                    Connexion requise
                  </div>
                  <div style={{fontSize:"var(--fs-micro)",color:"var(--text-sub)",marginBottom:20}}>
                    Connectez-vous à Aramis pour accéder au module Activité.
                  </div>
                  <button className="btn btn-orange" onClick={()=>navTo("aramis-login")}>
                    Se connecter
                  </button>
                </div>
              </div>
            );
          })()
        )}
        {/* ── NOUVELLES DES MUTUELLES — feed agrégé de CP et actualités ── */}
        {view==="nouvelles-mutuelles" && aramisSession && (
          <MutuelleNewsFeedLazy aramisSession={effectiveSession} sessionReelle={aramisSession} lists={lists}/>
        )}
        {view==="nouvelles-mutuelles" && !aramisSession && (
          <div style={{display:"flex",alignItems:"center",justifyContent:"center",minHeight:"calc(var(--vh100) * 0.6)"}}>
            <div className="card" style={{maxWidth:360,textAlign:"center",padding:32}}>
              <div style={{fontSize:"var(--fs-page)",marginBottom:12}}><IconeTexte nom="lock" titre="Verrouillé"/></div>
              <div style={{fontWeight:700,fontSize:"var(--fs-corps)",marginBottom:8,color:"var(--text-main)"}}>
                Connexion requise
              </div>
              <button className="btn btn-orange" onClick={()=>navTo("aramis-login")}>Se connecter</button>
            </div>
          </div>
        )}
        {/* ── « Ressources » retirée le 2026-05-22 ──
            Page bibliothèque SharePoint autonome obsolète, redondante
            avec les bibliothèques des modules dédiés (RH, projets,
            gouvernance) qui exposent leur propre DocumentLibrary. */}

        {/* ── MODULE RH/RECRUTEMENT (banque CV, offres, matching IA, chatbot) ──
            Accès : administrateurs d'Enio, ou rôle « RH » ou DG sur la fiche
            (src/droitsRH.js). Décision de Pierre du 15/10/2026 : ni le CODIR,
            ni le pôle d'intérêt « Ressources humaines ». Le « verrou
            temporaire V1 » admin-only décrit ici jusqu'au 15/10/2026 était
            levé depuis longtemps — le commentaire mentait sur la garde.
            La liste des vues est dupliquée volontairement ici (scope local
            de App vs Header où vit la const RH_VIEWS).  */}
        {(() => {
          const RH_VIEWS_LOCAL = ["accueil-rh","rh","rh-banque-cv","rh-cv-detail","rh-offres-emploi","rh-offre-detail","rh-candidatures","rh-matching","rh-statistiques","rh-admin-config","rh-conservation","rh-personnalisation","rh-effectifs","rh-entretiens","rh-comptes","rh-import-email","rh-recruteurs","rh-login","rh-pipeline","rh-pipeline-templates","rh-scorecard-templates","rh-email-templates","rh-talent-pool","rh-co-recrutement","rh-formations-catalogue","rh-formations-sessions","rh-plan-formation","rh-formations-suivi","rh-formations-suivies","rh-registre-formation","rh-organigramme","rh-fiches-poste","rh-competences","rh-taches","rh-simulateur-paie","rh-bilan-social","rh-lisez-moi"];
          const isRHView = RH_VIEWS_LOCAL.includes(view);
          if (!isRHView || !aramisSession) return null;
          // Exception : rh-login est le point d'entrée Firebase Auth des futurs
          // recruteurs mutuelles → accessible aux non-admins (sinon ils n'ont
          // aucun moyen de se connecter pour récupérer leurs custom claims).
          if (!canAccessRHnav && view !== "rh-login") {
            // Pas d'accès RH : on cache le module
            return (
              <div style={{display:"flex",alignItems:"center",justifyContent:"center",minHeight:"calc(var(--vh100) * 0.6)"}}>
                <div className="card" style={{maxWidth:420,textAlign:"center",padding:32}}>
                  <div style={{fontSize:"var(--fs-plaque)",marginBottom:14}}><IconeTexte nom="barrier-block" titre="Travaux"/></div>
                  <div style={{fontWeight:700,fontSize:"var(--fs-corps)",marginBottom:8,color:"var(--text-main)",fontFamily:"Montserrat,sans-serif"}}>
                    Accès réservé
                  </div>
                  <div style={{fontSize:"var(--fs-micro)",color:"var(--text-sub)",marginBottom:18,lineHeight:1.5}}>
                    Le module Ressources humaines est accessible aux directeurs
                    généraux et aux délégués habilités « RH » (case à cocher sur
                    la fiche de profil), ainsi qu'aux administrateurs d'Enio.
                    Rapprochez-vous de votre référent pour obtenir l'accès.
                  </div>
                  <button className="btn btn-orange" onClick={()=>navTo("ma-semaine")}>
                    Retour à mon cockpit
                  </button>
                </div>
              </div>
            );
          }
          return (
            <RHModuleLazy
              view={view === "accueil-rh" ? "rh" : view}
              onNav={navTo}
              moduleNav={modulesMenu.find(x=>x.key==="rh")}
              aramisSession={effectiveSession}
              isAdmin={isAdminEffective}
              canAccess={canAccessRHnav}
              config={config}
              lists={lists}
              delegates={delegates}
            />
          );
        })()}

        {/* ── PRÉPARER UNE PRÉSENTATION (outil PPTX) ── */}
        {view==="preparer-presentation" && aramisSession && (
          <PreparationPresentationLazy
            aramisSession={effectiveSession}
            isAdmin={isAdminEffective}
            projects={projects}
            delegates={delegates}
            lists={lists}
            config={config}
            gouvernance={gouvernance}
            reunions={reunions}
            meetings={meetings}
            leaderboard={leaderboard}
            ideas={ideas}
            appState={appState}
            onNav={navTo}
          />
        )}

        {/* ── HESTIA (lot H0) : accueil, objectif, À traiter (Gouvernance) et
            Pilotage → Hestia. La vue est gardée côté serveur (couche d'accès,
            src/hestia/acces.js) ; Pilotage reste réservé aux administrateurs. */}
        {/* Compte invité : rien d'Hestia, même côté client (la session RÉELLE,
            comme le serveur : « Voir comme » n'y change rien). */}
        {(view==="hestia" || view==="hestia-objectif" || view==="hestia-a-traiter") && aramisSession && (aramisSession.role!=="invite"
          ? <HestiaModuleLazy view={view} onNav={navTo} voirComme={!!impersonateAs}
              o={new URLSearchParams(window.location.search).get("o")}/>
          : <AccessDenied view={view} reason="Hestia est réservée aux personnes habilitées à contribuer au projet stratégique de l'Union."/>)}
        {view==="pilotage-hestia" && aramisSession && (isAdminApp
          ? <HestiaModuleLazy view={view} onNav={navTo} voirComme={!!impersonateAs}
              onglet={new URLSearchParams(window.location.search).get("onglet")}
              groupe={new URLSearchParams(window.location.search).get("groupe")}/>
          : <AccessDenied view={view} reason="Le pilotage d'Hestia est réservé aux administrateurs d'Aramis."/>)}

        {/* ── MÉDIATHÈQUE (lot L17.2a) : accueil et album, module Événements.
            Ouverte aux membres ; chaque album est gardé par le serveur (M1),
            chaque geste de l'équipe aussi (Pierre et les administrateurs).
            Compte invité : rien, comme le serveur (estMembre). */}
        {(view==="mediatheque" || view==="mediatheque-album") && aramisSession && (aramisSession.role!=="invite"
          ? <MediathequeModuleLazy view={view} onNav={navTo} voirComme={!!impersonateAs}
              albumId={new URLSearchParams(window.location.search).get("album")}
              depuis={new URLSearchParams(window.location.search).get("depuis")}/>
          : <AccessDenied view={view} reason="La Médiathèque est réservée aux membres d'Aramis."/>)}

        {/* ── AUDITS (D1, 11/10/2026), module Conformité, sans lien avec
            Calypso : missions, fiche d'une mission, actions en cours, liste
            d'accès, modèles de grilles. Étanches entre les mutuelles : chaque
            appel est gardé par le serveur (fonction auditMissions, jeton
            Aramis). La session RÉELLE : « Voir comme » n'y change rien.
            Compte invité : rien, comme le serveur. */}
        {(view==="audit" || view==="audit-mission" || view==="audit-actions" || view==="audit-acces" || view==="audit-modeles" || view==="audit-plans" || view==="audit-plan" || view==="audit-registre" || view==="audit-constat") && aramisSession && (aramisSession.role!=="invite"
          ? <AuditModuleLazy key={new URLSearchParams(window.location.search).get("demo")==="1" ? "demo" : "reel"}
              view={view} onNav={navTo} sessionId={aramisSession.id}
              missionId={new URLSearchParams(window.location.search).get("mission")}
              planId={new URLSearchParams(window.location.search).get("plan")}
              registreId={new URLSearchParams(window.location.search).get("constat")}
              demo={new URLSearchParams(window.location.search).get("demo")==="1"} lists={lists}/>
          : <AccessDenied view={view} reason="Les audits sont réservés aux membres d'Aramis."/>)}

        {/* ── CONTRÔLE INTERNE (D2, 11/10/2026), module Conformité, sans lien
            avec Calypso : index, cartographie et tableau des risques, fiche
            d'un risque, procédures versionnées, fiche d'une procédure,
            référentiel commun. Étanches entre les mutuelles, même liste
            d'accès que l'audit interne : chaque appel est gardé par le serveur
            (fonction controleInterne, jeton Aramis). La session RÉELLE :
            « Voir comme » n'y change rien. Compte invité : rien. */}
        {(view==="controle-interne" || view==="risques" || view==="risques-tableau" || view==="risque" || view==="procedures" || view==="procedure" || view==="controle-interne-referentiel" || view==="controle-interne-acces" || view==="parametrage-mutuelle" || view==="risques-bibliotheque" || view==="conformite-propositions" || view==="conformite-demonstration") && aramisSession && (aramisSession.role!=="invite"
          ? <ControleInterneModuleLazy key={view==="conformite-demonstration" || new URLSearchParams(window.location.search).get("demo")==="1" ? "demo" : "reel"}
              view={view} onNav={navTo} sessionId={aramisSession.id}
              risqueId={new URLSearchParams(window.location.search).get("risque")}
              procedureId={new URLSearchParams(window.location.search).get("procedure")}
              demo={view==="conformite-demonstration" || new URLSearchParams(window.location.search).get("demo")==="1"} lists={lists}/>
          : <AccessDenied view={view} reason="Le contrôle interne est réservé aux membres d'Aramis."/>)}

        {/* ── TRAVAIL EN GROUPES ── */}
        {view==="travail-groupes" && aramisSession && (
          <TravailGroupesLazy
            aramisSession={effectiveSession}
            delegates={delegates}
            isAdmin={isAdminEffective}
            onNav={navTo}
            config={config}
          />
        )}

        {/* ── CONSULTATIONS ASYNCHRONES ── */}
        {view==="consultations" && aramisSession && (
          <ConsultationAsyncLazy
            aramisSession={effectiveSession}
            delegates={delegates}
            isAdmin={isAdminEffective}
            lists={lists}
            gouvernance={gouvernance}
            poleRenames={config?.poleRenames}
            onNav={navTo}
          />
        )}

        {/* Consultation « Enio 2030 » retirée le 01/10/2026 (G11 : « Plus
            personne, on supprime »). La vue reste VALIDE À JAMAIS (liens des
            courriels en circulation, favoris) et mène à l'accueil Gouvernance,
            déclarée dans LEGACY_REDIRECTS. Le front ne lit ni n'écrit plus
            aucune collection strategie*. */}
        {view==="strategie-2030" && (
      <LegacyViewRedirectLazy to="accueil-gouv" onNav={navTo} label="Redirection vers l'accueil Gouvernance…"/>
        )}

        {/* ── NOOTA Import — panel admin Athos ── (la garde reste celle du
            panneau ; son refus est l'écran commun de la coquille, lot L11n) */}
        {view==="athos-noota" && aramisSession && (
          <NootaImportPanelLazy aramisSession={effectiveSession}
            refus={<AccessDenied reason="L'import Noota est réservé aux administrateurs d'Aramis."/>}/>
        )}

        {/* ── DATAVIZ — Module collaboratif ── */}
        {view==="dataviz" && aramisSession && (
          <DatavizModuleLazy
            aramisSession={effectiveSession}
            sessionReelle={aramisSession}
            lists={lists}
            saveList={saveList}
            delegates={delegates}
            isAdmin={isAdminEffective}
            onNav={navTo}
          />
        )}

        {/* ── VEILLE BOAMP (marchés publics) ── */}
        {view==="veille" && aramisSession && (
          <VeilleModuleLazy
            aramisSession={effectiveSession}
            sessionReelle={aramisSession}
            delegates={delegates}
            saveDelegate={saveDelegate}
            lists={lists}
            isAdmin={isAdminEffective}
            onNav={navTo}
          />
        )}
        {/* ── VEILLE JURIDIQUE (régulateurs, textes officiels, parlementaire,
            fédérations, doctrine, CNIL/DORA) — accessible à tous ── */}
        {view==="veille-juridique" && aramisSession && (
          <VeilleJuridiqueModuleLazy
            aramisSession={effectiveSession}
            lists={lists}
            onNav={navTo}
            sessionReelle={aramisSession}
          />
        )}
        {/* ── REGISTRE DES ORGANISMES D'ASSURANCE (ACPR REFASSU) — tous, import admin ── */}
        {/* Les mutuelles en clair : session RÉELLE (le serveur identifie
            l'appelant par son jeton) ; brouillons et administration exigent
            isAdminApp, jamais en « Voir comme » (apercu). */}
        {view==="mutuelles-en-clair" && aramisSession && (
          <MutuellesEnClairLazy aramisSession={aramisSession} estAdminSession={isAdminApp} apercu={!!impersonateAs}/>
        )}
        {/* Fondations et fonds de dotation des mutuelles : session RÉELLE (le
            serveur identifie l'appelant par son jeton) ; « À valider » et la
            préparation des synthèses exigent isAdminApp, jamais en « Voir
            comme » (apercu). */}
        {view==="fondations-mutuelles" && aramisSession && (
          <FondationsMutuellesLazy aramisSession={aramisSession} estAdminSession={isAdminApp} apercu={!!impersonateAs} onNav={navTo}/>
        )}
        {view==="referentiel-organismes" && aramisSession && (
          <ReferentielOrganismesLazy aramisSession={effectiveSession} sessionReelle={aramisSession} lists={lists} onNav={navTo} />
        )}
        {/* ── ARGOS — intelligence économique ──────────────────────────────
            UN SEUL point de montage pour les 7 vues du module : le routage se
            fait dans ArgosModule via la prop `view`. Ce n'est pas une commodité
            d'écriture — c'est ce qui préserve le cache de recherche et la liste
            de résultats quand on ouvre une fiche puis qu'on revient. Sept
            montages séparés démonteraient le module à chaque navigation.
            Accès : aucune entrée dans VIEW_ACCESS, donc scope "transverse"
            (ouvert à tous les délégués). C'est une décision produit assumée du
            30/07/2026 — ne pas « corriger » en ajoutant une restriction : les
            consultations sont tracées côté serveur dans argosAccessLog. */}
        {ARGOS_VIEWS.includes(view) && view!=="argos-graphe" && aramisSession && (
          <ArgosModuleLazy view={view} aramisSession={effectiveSession}
            isAdmin={isAdminEffective} onNav={navTo} delegates={delegates} />
        )}
        {/* « argos-graphe » : page annoncée, jamais livrée ; route valide à
            jamais, redirigée vers la recherche avec les identifiants passés
            (lot L11p). Hors du montage du module, qui consommerait ces
            paramètres avant la redirection. */}
        {view==="argos-graphe" && aramisSession && (
          <LegacyViewRedirectLazy to="argos" paramKeys={["siren","q"]} onNav={navTo} label="Redirection vers la recherche Argos…"/>
        )}
        {/* ── GEL DES AVOIRS ET PPE — PWA zero-knowledge ──
             Ouvert : admin, DG, CODIR (rôle CD, notamment Conformité),
             membres d'un pôle du domaine Pilotage Éco. & Réglementaire. */}
        {view==="gel-avoirs-ppe" && aramisSession && (() => {
          const session = effectiveSession || aramisSession;
          // Recalcule sur effectiveSession (impersonation-aware) : la constante
          // canAccessGelPpe plus haut se base sur aramisSession seule.
          const roles = Array.isArray(session.roles) ? session.roles : [];
          const polesPilotage = new Set([
            "actuariat et pilotage de la donnee",
            "conformite juridique et protection des donnees",
            "gestion des risques controle interne et audit interne",
            "administration comptabilite finance",
          ]);
          const normPole = s => String(s||"")
            .normalize("NFD").replace(/[\u0300-\u036f]/g,"")
            .toLowerCase().replace(/[^a-z0-9]+/g," ").trim();
          const polesOK = (session.polesInteret||[]).some(p => polesPilotage.has(normPole(p)));
          const autorise = isAdminApp
            || roles.includes("DG")
            || roles.includes("CD")
            || polesOK;
          if (!autorise) {
            return (
              <div style={{display:"flex",alignItems:"center",justifyContent:"center",minHeight:"calc(var(--vh100) * 0.6)"}}>
                <div className="card" style={{maxWidth:460,textAlign:"center",padding:32}}>
                  <div style={{fontSize:"var(--fs-page)",marginBottom:12}}><IconeTexte nom="lock" titre="Verrouillé"/></div>
                  <div style={{fontWeight:700,fontSize:"var(--fs-nav)",color:"var(--text-main)",marginBottom:8}}>
                    Accès restreint
                  </div>
                  <div style={{fontSize:"var(--fs-micro)",color:"var(--text-sub)",lineHeight:1.5,marginBottom:18}}>
                    Cet outil de conformité LCB-FT est réservé aux <strong>directeurs généraux</strong>,
                    aux <strong>membres du CODIR</strong> (notamment Conformité) et aux membres d'un pôle
                    du domaine <strong>Pilotage Économique &amp; Réglementaire</strong>
                    (Actuariat, Conformité/Juridique/RGPD, Gestion des risques &amp; contrôle interne,
                    Administration &amp; finance).
                    <br/>Contactez votre admin pour obtenir un accès.
                  </div>
                  <button className="btn btn-orange" onClick={()=>navTo("exploration")}>
                    Retour à Exploration
                  </button>
                </div>
              </div>
            );
          }
          return <GelPpeModuleLazy aramisSession={session} />;
        })()}

        {/* ── GOUVERNANCE (visible tous, édition admin) ── */}
        {(view==="gouvernance"||view.startsWith("gouvernance-"))&&(
          <div className="page-largeur">
            {/* Refonte 26/08 : la page est éclatée en destinations (barre
                latérale du module — sous-menu « Composition & mandats ») avec
                URL profondes ; plus de rangée d'onglets ni de double titre.
                Largeur commune des pages (05/10/2026) : page-largeur, et non
                plus 1240 px fixes. */}
            <GouvernancePanelLazy
              page={view}
              sidInitial={new URLSearchParams(window.location.search).get("sid") || null}
              appState={appState} isAdmin={isAdminEffective}
              onNav={navTo}
              gouvernance={gouvernance} saveGouvernance={isAdminApp?saveGouvernance:()=>{}}
              delegates={delegates} saveDelegate={isAdminApp?saveDelegate:()=>{}}
              config={config}
              readOnly={!isAdminEffective}
              aramisSession={effectiveSession}
              voirComme={!!impersonateAs}
              reunions={reunions}
              lists={lists}
              DocumentLibrary={DocumentLibrary}
              DelegateContactPopup={DelegateContactPopup}
            />
          </div>
        )}

        {/* ── Anciennes vues Thémis (04/09/2026) : le hub ThemisHome est retiré
            (ses cartes vivent dans Événements et Pilotage) et la préparation
            des séances a rejoint Pilotage. Strings valides à jamais. ── */}
        {view==="themis"&&(
          <LegacyViewRedirectLazy to={isAdminEffective ? "pilotage-seances" : "accueil-gouv"} onNav={navTo}/>
        )}
        {view==="themis-instances"&&(
          <LegacyViewRedirectLazy to="pilotage-seances" paramKeys={["sessionId","sessionState"]} onNav={navTo}/>
        )}
        {view==="themis-preparer"&&(
          <LegacyViewRedirectLazy to="pilotage-seances" paramKeys={["sessionId"]} onNav={navTo}/>
        )}

        {/* ── PILOTAGE — Séances des instances : préparation (hub Thémis) ── */}
        {view==="pilotage-seances"&&(() => {
          // Verrou route : préparation/gestion des instances réservée à l'équipe
          // d'administration (aligné sur le menu admin-only). Ferme le deeplink.
          // Membre non-admin (03/09/2026, retour Hannelore Hamman : « pas accès au
          // dossier Instances ») : l'entrée « Instances » du menu Gouvernance est
          // visible de tous (modèle « tout visible + page navrés ») mais la
          // préparation est réservée à l'équipe. Plutôt qu'une page navrés
          // générique, on ORIENTE le membre vers ses documents : l'onglet de
          // son instance (Composition & mandats) et le lien de sa convocation.
          if (!isAdminEffective) {
            const _perims = perimetresAccessibles(effectiveSession, delegates, gouvernance);
            const _cibles = [
              ...INSTANCES_NAV.map((i) => [i.scope, routeInstance(i.key), i.label]),
              ["COMMUN", "gouvernance-documents", "Documents communs"],
            ].filter(([k]) => _perims.includes(k));
            return (
              <div style={{display:"flex",alignItems:"center",justifyContent:"center",minHeight:"calc(var(--vh100) * 0.6)",padding:24}}>
                <div className="card" style={{maxWidth:560,padding:"36px 34px"}}>
                  <div style={{fontSize:"var(--fs-display)",marginBottom:14,textAlign:"center"}}><IconeTexte nom="scale" titre="Juridique"/></div>
                  <div style={{fontSize:"var(--fs-nav)",color:"var(--text-main)",lineHeight:1.7,fontFamily:"Inter,system-ui,sans-serif"}}>
                    La préparation des séances (ordre du jour, convocations, procès-verbaux) est
                    réservée à l'équipe d'administration d'Enio.
                    {_cibles.length>0
                      ? " Vos documents d'instance se trouvent ici :"
                      : " Aucun mandat en cours n'est enregistré sur votre fiche : si vous siégez dans une instance, écrivez-nous pour le faire régulariser."}
                  </div>
                  {/* Ce qui mène à une autre page est une ligne entière à
                      chevron, un vrai lien (R4 du bandeau de commande, lot 9). */}
                  {_cibles.length>0&&(
                    <ul className="seances-cibles">
                      {_cibles.map(([k,route,label])=>(
                        <li key={k}>
                          <a className="seances-cible" href={adresseVue(route)} onClick={(e)=>suivreLienVue(e,{vue:route,onNav:navTo})}>
                            <IconeTexte nom="calendar"/>
                            <span className="seances-cible-t">Séances et documents · {label}</span>
                            <IconeTexte nom="chevron-right"/>
                          </a>
                        </li>
                      ))}
                    </ul>
                  )}
                  <div style={{fontSize:"var(--fs-micro)",color:"var(--text-sub)",lineHeight:1.6,marginTop:18}}>
                    Les pièces d'une séance précise (CA, AG…) s'ouvrent depuis le lien
                    « Documents préparatoires » de votre convocation.
                  </div>
                  <div style={{marginTop:22,textAlign:"center"}}>
                    <button className="btn btn-ghost" onClick={()=>navTo("ma-semaine")}>Retour à Ma semaine</button>
                  </div>
                </div>
              </div>
            );
          }
          // Exposer le contexte Admin complet pour les slots de ThemisInstances
          // (évite les bandeaux "hors ligne" fantômes et garantit l'accès aux données)
          if (typeof window !== "undefined") {
            window.__ThemisAdminContext = {
              isOnline,
              // Codes de vote côté serveur : API liée à une séance, pour les
              // effets statutaires appliqués depuis Thémis (cooptation, démission)
              codesApiPour: (sid) => creerCodesApi(sid, aramisSession?.id),
              config, saveConfig,
              lists, saveList, deleteList,
              admins, saveAdmin, deleteAdmin,
              delegates, saveDelegate, deleteDelegate,
              gouvernance, saveGouvernance,
              ideas, deleteIdea,
              auditLog, logAction,
              members: members || [], saveMember, deleteMember,
              aramisSession: effectiveSession,
              // De quoi créer la réunion Outlook DEPUIS l'onglet Convocation
              // (demande Pierre du 16/09/2026) : la fenêtre d'invitation est
              // la même que dans le module Réunions, rien n'est dupliqué.
              reunions, saveReunion, externes, saveExterne,
            };
          }
          return (
            <ThemisInstancesHubLazy
              appState={appState} onSave={saveState} onNav={navTo}
              isAdmin={isAdminEffective}
              initialSessionId={new URLSearchParams(window.location.search).get("sessionId") || null}
              initialEtape={new URLSearchParams(window.location.search).get("etape") || null}
              onFicheStateChange={(open) => setFicheOuverteView(open ? "pilotage-seances" : null)}/>
          );
        })()}

        {/* ── THÉMIS — Documents préparatoires d'une séance (délégués) ──
            Cible du lien porté par la convocation. Ouvert à tout délégué
            connecté : c'est la Cloud Function qui vérifie qu'il est bien
            convoqué à la séance demandée (listSessionDocs). */}
        {view==="themis-presence" && (
          <ThemisPresencePageLazy token={presenceLink?.t||""} statutInitial={presenceLink?.p||""}/>
        )}

        {/* Pouvoirs : le mandant donne, révoque ou confirme depuis son lien
            nominatif. Tout est vérifié et écrit par la CF (themisPouvoirs.js). */}
        {view==="themis-emargement" && (
          <ThemisEmargementPageLazy token={emargementLink?.t||""}/>
        )}
        {view==="themis-repas" && (
          <Suspense fallback={null}><ThemisRepasPageRaw token={repasLink?.t||""}/></Suspense>
        )}

        {(view==="themis-pouvoir"||view==="themis-pouvoir-confirmation") && (
          <ThemisPouvoirPageLazy token={pouvoirLink?.t||""}
            mode={view==="themis-pouvoir-confirmation"?"confirmation":"donner"}
            reponseInitiale={pouvoirLink?.r||""}/>
        )}

        {view==="themis-documents-seance" && (
          effectiveSession ? (
            <ThemisDocumentsSeanceLazy
              sessionId={docsSeanceId}
              aramisSession={effectiveSession}
              onNav={navTo}/>
          ) : <AccessDenied reason="Connectez-vous pour accéder aux documents de la séance."/>
        )}

        {/* ── THÉMIS — Événements : hub d'accueil (refonte façon Calypso) ──
            Accessible aux admins ET aux délégués co-organisateurs (vue réduite
            à leurs événements). */}
        {view==="themis-evenements" && (
          effectiveSession ? (
            <ThemisEvenementsHomeLazy onNav={navTo} isAdmin={isAdminEffective} module={modulesMenu.find(x=>x.key==="events")}/>
          ) : <AccessDenied reason="Connectez-vous pour accéder à vos événements."/>
        )}

        {/* ── THÉMIS — Liste/gestion des événements ──
            Non-admin : liste filtrée à ses événements (owner/co-org). */}
        {view==="themis-events-list" && (
          effectiveSession ? (
            <ThemisEvenementsLazy aramisSession={effectiveSession} delegates={delegates} lists={lists} onNav={navTo} initialTab="evenements" isAdmin={isAdminEffective} myUid={effectiveSession?.id}/>
          ) : <AccessDenied reason="Connectez-vous pour accéder à vos événements."/>
        )}

        {/* ── THÉMIS — Carnet de contacts externes (admin uniquement) ── */}
        {view==="themis-contacts" && (
          isAdminEffective ? (
            <ThemisEvenementsLazy aramisSession={effectiveSession} delegates={delegates} lists={lists} onNav={navTo} initialTab="contacts" isAdmin={true} myUid={effectiveSession?.id}/>
          ) : <AccessDenied reason="Le carnet de contacts externes est réservé à l'équipe d'administration."/>
        )}

        {/* ── THÉMIS — Centre de validation consolidé ── */}
        {view==="themis-evenements-validation" && (
          isAdminEffective ? (
            <ThemisEventValidationLazy aramisSession={effectiveSession} onNav={navTo}/>
          ) : <AccessDenied reason="La validation est réservée à l'équipe d'administration."/>
        )}

        {/* ── THÉMIS — Anciennes files de validation autonomes ──
            Strings conservées à jamais (deep-links des digests e-mail) →
            redirigent vers le centre de validation regroupé, qui porte les
            mêmes files en onglets. */}
        {view==="themis-pending-updates" && (
          isAdminEffective ? (
            <LegacyEventRedirectLazy targetView="themis-evenements-validation" onNav={navTo}/>
          ) : <AccessDenied reason="La validation des modifications est réservée à l'équipe d'administration."/>
        )}

        {view==="event-rsvp-inbox" && (
          isAdminEffective ? (
            <LegacyEventRedirectLazy targetView="themis-evenements-validation" onNav={navTo}/>
          ) : <AccessDenied reason="La validation des réponses RSVP est réservée à l'équipe d'administration."/>
        )}

        {view==="event-invitation-requests" && (
          isAdminEffective ? (
            <LegacyEventRedirectLazy targetView="themis-evenements-validation" onNav={navTo}/>
          ) : <AccessDenied reason="La validation des demandes d'invitation est réservée à l'équipe d'administration."/>
        )}

        {/* ── THÉMIS — Fiche événement à étapes (refonte façon Instances) ──
            La porte d'entrée d'un événement : Cadre → Contenu → Invités →
            Invitations → Plan de table → Jour J → Validation → Suivi → Bilan.
            Sa barre d'étapes remplace la barre de module (onFicheStateChange). */}
        {view==="event-fiche" && (
          effectiveSession ? (
            <EventFicheLazy aramisSession={effectiveSession} delegates={delegates} gouvernance={gouvernance} poleRenames={config?.poleRenames} onNav={navTo}
              isAdmin={isAdminEffective} myUid={effectiveSession?.id} voirComme={!!impersonateAs}
              onFicheStateChange={(open) => setFicheOuverteView(open ? "event-fiche" : null)}/>
          ) : <AccessDenied reason="Connectez-vous pour accéder à vos événements."/>
        )}

        {/* ── THÉMIS — Suivi & alertes global (lot 4) ── */}
        {view==="event-suivi" && (
          isAdminEffective ? (
            <EventSuiviLazy aramisSession={effectiveSession} onNav={navTo}/>
          ) : <AccessDenied reason="Le suivi des événements est réservé à l'équipe d'administration."/>
        )}

        {/* ── THÉMIS — Vue jour J tablette hôtesse (Sprint 3) ──
            Vue AUTONOME préservée (tablette de l'accueil) — la fiche
            événement l'embarque aussi comme étape Jour J. La garde des pages
            la réserve aux administrateurs (préfixe « event- », accesVues.js,
            décision de Pierre du 14/10/2026) ; l'ouvrir à une hôtesse non
            administratrice reste à trancher. */}
        {view==="event-day" && (
          effectiveSession ? (
            <EventDayPanelLazy aramisSession={effectiveSession} onNav={navTo}/>
          ) : <AccessDenied reason="Connectez-vous pour accéder à l'accueil de l'événement."/>
        )}

        {/* ── THÉMIS — Bibliothèque manuscrite (Sprint 8) ── */}
        {view==="themis-handwritten" && (
          isAdminEffective ? (
            <HandwrittenLibraryLazy aramisSession={effectiveSession} onNav={navTo}/>
          ) : <AccessDenied reason="La bibliothèque manuscrite est réservée à l'équipe d'administration."/>
        )}

        {/* ── THÉMIS — Plan de table (Sprint 10) ──
            String de vue conservée à jamais (deep-links) → redirige vers
            l'étape Plan de table de la fiche événement. */}
        {view==="event-table-plan" && (
          effectiveSession ? (
            <LegacyEventRedirectLazy step="table" onNav={navTo} fallbackView="themis-events-list"/>
          ) : <AccessDenied reason="Connectez-vous pour accéder au plan de table."/>
        )}

        {/* String conservée (deep-links des digests : event-guest-list&id=…)
            → redirige vers l'étape Invités de la fiche événement. */}
        {view==="event-guest-list" && (
          effectiveSession ? (
            <LegacyEventRedirectLazy step="invites" onNav={navTo} fallbackView="themis-events-list"/>
          ) : <AccessDenied reason="Connectez-vous pour accéder à la liste d'invités."/>
        )}

        {/* ── THÉMIS — Séance (Session + Participants) ── */}
        {view==="themis-seance" && (
          isAdminEffective ? (
          <AdminLazy key="themis-session"
            appState={appState} onSave={saveState} onNav={navTo}
            isOnline={isOnline}
            config={config} saveConfig={saveConfig}
            lists={lists} saveList={saveList} deleteList={deleteList}
            admins={admins} saveAdmin={saveAdmin} deleteAdmin={deleteAdmin}
            delegates={delegates} saveDelegate={saveDelegate} deleteDelegate={deleteDelegate}
            delegatesTechniques={delegatesTechniques}
            gouvernance={gouvernance} saveGouvernance={saveGouvernance}
            ideas={ideas} deleteIdea={deleteIdea} auditLog={auditLog} logAction={logAction}
            members={members||[]} saveMember={saveMember} deleteMember={deleteMember}
            aramisSession={aramisSession}
            agoraAdminMode={true}
            themisTab="session"
          />
          ) : <AccessDenied reason="L'animation d'une séance est réservée à l'équipe d'administration."/>
        )}

        {/* ── THÉMIS — En cours (admin uniquement) ── */}
        {view==="themis-live" && (
          isAdminEffective ? (
          <AdminLazy key="themis-live"
            appState={appState} onSave={saveState} onNav={navTo}
            isOnline={isOnline}
            config={config} saveConfig={saveConfig}
            lists={lists} saveList={saveList} deleteList={deleteList}
            admins={admins} saveAdmin={saveAdmin} deleteAdmin={deleteAdmin}
            delegates={delegates} saveDelegate={saveDelegate} deleteDelegate={deleteDelegate}
            gouvernance={gouvernance} saveGouvernance={saveGouvernance}
            ideas={ideas} deleteIdea={deleteIdea} auditLog={auditLog} logAction={logAction}
            members={members||[]} saveMember={saveMember} deleteMember={deleteMember}
            aramisSession={aramisSession}
            agoraAdminMode={true}
            themisTab="live"
          />
          ) : <AccessDenied reason="Cette vue de scrutin en cours est réservée à l'équipe d'administration."/>
        )}

        {/* ── Console de la séance en direct, par ses deux entrées de Pilotage
            (demande de Pierre du 18/10/2026) : « Émargement & codes de vote »
            ouvre l'onglet Participants ; « Scrutins & votes en séance » ouvre
            « En cours » si un scrutin est ouvert, sinon « Scrutins ». L'onglet
            n'est choisi qu'à l'ouverture (ongletInitial) : clore un scrutin ne
            fait pas changer d'onglet. ── */}
        {view==="seance-emargement" && (
          isAdminEffective ? (
          <AdminLazy key="seance-emargement"
            appState={appState} onSave={saveState} onNav={navTo}
            isOnline={isOnline}
            config={config} saveConfig={saveConfig}
            lists={lists} saveList={saveList} deleteList={deleteList}
            admins={admins} saveAdmin={saveAdmin} deleteAdmin={deleteAdmin}
            delegates={delegates} saveDelegate={saveDelegate} deleteDelegate={deleteDelegate}
            delegatesTechniques={delegatesTechniques}
            gouvernance={gouvernance} saveGouvernance={saveGouvernance}
            ideas={ideas} deleteIdea={deleteIdea} auditLog={auditLog} logAction={logAction}
            members={members||[]} saveMember={saveMember} deleteMember={deleteMember}
            aramisSession={aramisSession}
            agoraAdminMode={true}
            ongletInitial="participants"
          />
          ) : <AccessDenied view={view} reason="La console de séance est réservée à l'équipe d'administration."/>
        )}
        {view==="seance-scrutins" && (
          isAdminEffective ? (
          <AdminLazy key="seance-scrutins"
            appState={appState} onSave={saveState} onNav={navTo}
            isOnline={isOnline}
            config={config} saveConfig={saveConfig}
            lists={lists} saveList={saveList} deleteList={deleteList}
            admins={admins} saveAdmin={saveAdmin} deleteAdmin={deleteAdmin}
            delegates={delegates} saveDelegate={saveDelegate} deleteDelegate={deleteDelegate}
            delegatesTechniques={delegatesTechniques}
            gouvernance={gouvernance} saveGouvernance={saveGouvernance}
            ideas={ideas} deleteIdea={deleteIdea} auditLog={auditLog} logAction={logAction}
            members={members||[]} saveMember={saveMember} deleteMember={deleteMember}
            aramisSession={aramisSession}
            agoraAdminMode={true}
            ongletInitial={((appState&&appState.agenda)||[]).some(s=>s&&s.status==="open")?"live":"agenda"}
          />
          ) : <AccessDenied view={view} reason="La console de séance est réservée à l'équipe d'administration."/>
        )}

        {/* ── THÉMIS — Historique (admin uniquement) ── */}
        {view==="themis-historique" && (
          isAdminEffective ? (
          <AdminLazy key="themis-history"
            appState={appState} onSave={saveState} onNav={navTo}
            isOnline={isOnline}
            config={config} saveConfig={saveConfig}
            lists={lists} saveList={saveList} deleteList={deleteList}
            admins={admins} saveAdmin={saveAdmin} deleteAdmin={deleteAdmin}
            delegates={delegates} saveDelegate={saveDelegate} deleteDelegate={deleteDelegate}
            gouvernance={gouvernance} saveGouvernance={saveGouvernance}
            ideas={ideas} deleteIdea={deleteIdea} auditLog={auditLog} logAction={logAction}
            members={members||[]} saveMember={saveMember} deleteMember={deleteMember}
            aramisSession={aramisSession}
            agoraAdminMode={true}
            themisTab="history"
          />
          ) : <AccessDenied reason="L'historique complet des séances est réservé à l'équipe d'administration."/>
        )}

        {/* ── agora-admin conservé pour compatibilité (vue historique, valide à
            jamais). Audit du 28/09/2026 : elle montait la console pour TOUT
            visiteur, qui y trouvait la page de configuration initiale et le
            formulaire super-admin. Même garde que la vue « admin ». ── */}
        {view==="agora-admin"&&(isAdminApp ? (
          <AdminLazy
            appState={appState} onSave={saveState} onNav={navTo}
            isOnline={isOnline}
            config={config} saveConfig={saveConfig}
            lists={lists} saveList={saveList} deleteList={deleteList}
            admins={admins} saveAdmin={saveAdmin} deleteAdmin={deleteAdmin}
            delegates={delegates} saveDelegate={saveDelegate} deleteDelegate={deleteDelegate}
            gouvernance={gouvernance} saveGouvernance={saveGouvernance}
            ideas={ideas} deleteIdea={deleteIdea} auditLog={auditLog} logAction={logAction}
            members={members||[]} saveMember={saveMember} deleteMember={deleteMember}
            aramisSession={aramisSession}
            agoraAdminMode={true}
          />
        ) : <AccessDenied view={view} reason="La console d'administration est réservée aux administrateurs."/>)}
        {view==="participant"&&<ParticipantLazy appState={etatVotant} mutuelles={getMutuelles(config)}/>}
        {view==="results"   &&<Results     appState={appState} onNav={navTo} aramisSession={aramisSession} delegates={delegates}/>}
        {/* Dashboard d'adoption — accessible à tous les admins (pas seulement super) */}
        {view==="admin-budget" && (isAdminApp ? (
          // Module du menu Pilotage : plus de bouton « ← Gestion membres »
          // (vestige de l'époque où le budget vivait sous Administration).
          <AdminBudgetPanelLazy aramisSession={effectiveSession} lists={lists} delegates={delegates} saveList={saveList}/>
        ) : (
          <div style={{display:"flex",alignItems:"center",justifyContent:"center",minHeight:"calc(var(--vh100) * 0.6)"}}>
            <div className="card" style={{maxWidth:360,textAlign:"center",padding:32}}>
              <div style={{fontSize:"var(--fs-page)",marginBottom:12}}><IconeTexte nom="lock" titre="Verrouillé"/></div>
              <div style={{fontWeight:700,fontSize:"var(--fs-corps)",marginBottom:8,color:"var(--text-main)"}}>
                Accès réservé aux administrateurs
              </div>
              <button className="btn btn-orange" onClick={()=>navTo("ma-semaine")}>Retour</button>
            </div>
          </div>
        ))}
        {/* Budget : 5 écrans, 5 adresses. La garde d'accès est écrite UNE fois —
            la recopier par route exposerait le module au premier écran ajouté
            sans elle. L'ancienne vue « admin-budget-outils » reste valide à
            jamais (favoris, liens e-mail, vue mémorisée) et redirige. */}
        {view==="admin-budget-outils" && (
          <LegacyViewRedirectLazy to="budget-accueil" paramKeys={["exercice","scenario"]} onNav={navTo}/>
        )}
        {VUES_BUDGET.includes(view) && (isProprietaireBudget ? (
          <BudgetToolboxPanelLazy aramisSession={effectiveSession} lists={lists} vue={view} onNav={navTo}/>
        ) : (
          <div style={{display:"flex",alignItems:"center",justifyContent:"center",minHeight:"calc(var(--vh100) * 0.6)"}}>
            <div className="card" style={{maxWidth:360,textAlign:"center",padding:32}}>
              <div style={{fontSize:"var(--fs-page)",marginBottom:12}}><IconeTexte nom="lock" titre="Verrouillé"/></div>
              <div style={{fontWeight:700,fontSize:"var(--fs-corps)",marginBottom:8,color:"var(--text-main)"}}>Accès réservé · Budget</div>
              <div style={{fontSize:"var(--fs-meta)",color:"var(--text-sub)",marginBottom:14}}>Ce module est réservé à son propriétaire.</div>
              <button className="btn btn-orange" onClick={()=>navTo("ma-semaine")}>Retour</button>
            </div>
          </div>
        ))}
        {/* Mon espace salarié DANS Aramis — le compte Aramis prime : la session
            Aramis (et son token Firebase, uid = delegateId) suffit, aucun mot
            de passe supplémentaire. Les salariés sans compte Aramis passent par
            le portail autonome /espace-salarie. Le cockpit et ses espaces
            (26/10/2026) : une vue par espace, mon-espace-salarie-<espace>, la
            même page (RHEspaceSalarie) ; les notes de frais ont leur page. */}
        {view.startsWith("mon-espace-salarie") && (!effectiveSession ? (
          <div style={{display:"flex",alignItems:"center",justifyContent:"center",minHeight:"calc(var(--vh100) * 0.6)"}}>
            <div className="card" style={{maxWidth:360,textAlign:"center",padding:32}}>
              <div style={{fontWeight:700,fontSize:"var(--fs-corps)",marginBottom:8,color:"var(--text-main)"}}>Connectez-vous pour accéder à votre espace salarié.</div>
            </div>
          </div>
        ) : (
          <div className="page-largeur">
            <RHEspaceSalarieLazy embedded onNav={navTo} vue={view} module={modulesPersonnels[0]} lists={lists}
              appelNdf={ndfEligible ? (n,p)=>callDepenses(n, effectiveSession, p) : null}
              appelFiches={()=>import("./mesFormations/appel").then(m=>m.appelSession("mesFormations", "lister", {}, effectiveSession)).then(r=>(r&&r.fiches)||[])}
              appelFormations={(action,data)=>import("./mesFormations/appel").then(m=>m.appelSession("mesFormations", action, data, effectiveSession))}
              onCompteurs={setCompteursEspace}/>
          </div>
        ))}

        {view==="mes-notes-frais" && (!effectiveSession ? (
          <div style={{display:"flex",alignItems:"center",justifyContent:"center",minHeight:"calc(var(--vh100) * 0.6)"}}>
            <div className="card" style={{maxWidth:360,textAlign:"center",padding:32}}>
              <div style={{fontWeight:700,fontSize:"var(--fs-corps)",marginBottom:8,color:"var(--text-main)"}}>Connectez-vous pour accéder à vos notes de frais.</div>
            </div>
          </div>
        ) : ndfEligible ? (
          <div className="page-largeur">
            {/* L'astuce de l'écran d'accueil rejoint « Mon compte », à la ligne
                de l'app iPhone (bandeau de commande, 27/10/2026, lot 21). */}
            <NotesFraisSelfLazy call={(n,p)=>callDepenses(n, effectiveSession, p)} astuceEcranAccueil/>
          </div>
        ) : (
          <div style={{display:"flex",alignItems:"center",justifyContent:"center",minHeight:"calc(var(--vh100) * 0.6)"}}>
            <div className="card" style={{maxWidth:420,textAlign:"center",padding:32}}>
              <div style={{fontSize:"var(--fs-page)",marginBottom:12}}><IconeTexte nom="luggage" titre="Déplacement"/></div>
              <div style={{fontWeight:700,fontSize:"var(--fs-corps)",marginBottom:8,color:"var(--text-main)"}}>Outil réservé à la structure employeur ENIO</div>
              <div style={{fontSize:"var(--fs-meta)",color:"var(--text-sub)",marginBottom:16}}>Les notes de frais sont réservées aux salariés d'ENIO et aux membres explicitement habilités. Rapprochez-vous de la direction si vous pensez devoir y accéder.</div>
              <button className="btn btn-orange" onClick={()=>navTo("ma-semaine")}>Retour</button>
            </div>
          </div>
        ))}
        {view==="admin-depenses" && (isAdminApp ? (
          <DepensesPanelLazy aramisSession={effectiveSession} delegates={delegates}/>
        ) : (
          <div style={{display:"flex",alignItems:"center",justifyContent:"center",minHeight:"calc(var(--vh100) * 0.6)"}}>
            <div className="card" style={{maxWidth:360,textAlign:"center",padding:32}}>
              <div style={{fontSize:"var(--fs-page)",marginBottom:12}}><IconeTexte nom="lock" titre="Verrouillé"/></div>
              <div style={{fontWeight:700,fontSize:"var(--fs-corps)",marginBottom:8,color:"var(--text-main)"}}>Accès réservé aux administrateurs</div>
              <button className="btn btn-orange" onClick={()=>navTo("ma-semaine")}>Retour</button>
            </div>
          </div>
        ))}
        {/* Notes de frais — entrée Pilotage de PREMIER NIVEAU (demande Pierre 29/07) :
            même panneau que Dépenses mais en mode soloNdf (l'onglet a été retiré
            de Dépenses & paiements pour ne pas exister à deux endroits). */}
        {view==="admin-ndf" && (isAdminApp ? (
          <DepensesPanelLazy aramisSession={effectiveSession} delegates={delegates} soloNdf/>
        ) : (
          <div style={{display:"flex",alignItems:"center",justifyContent:"center",minHeight:"calc(var(--vh100) * 0.6)"}}>
            <div className="card" style={{maxWidth:360,textAlign:"center",padding:32}}>
              <div style={{fontSize:"var(--fs-page)",marginBottom:12}}><IconeTexte nom="lock" titre="Verrouillé"/></div>
              <div style={{fontWeight:700,fontSize:"var(--fs-corps)",marginBottom:8,color:"var(--text-main)"}}>Accès réservé aux administrateurs</div>
              <button className="btn btn-orange" onClick={()=>navTo("ma-semaine")}>Retour</button>
            </div>
          </div>
        ))}
        {view==="compta-ec" && <ComptaExpertPanelLazy aramisSession={effectiveSession} estAdmin={isAdminApp} sessionAdmin={isAdminApp ? aramisSession : null}/>}
        {/* Aucune route « compta » : le module de comptabilité générale existe au
            dépôt (src/ComptaModule.js, functions/compta.js) mais n'est monté
            NULLE PART — décision Pierre 01/08/2026, nous ne nous en servons pas
            à ce stade. Ne pas le rebrancher, et ne rien lui imputer depuis du
            code nouveau, sans accord explicite. */}
        {view==="admin-acces" && (isAdminApp ? (
          <AccessGovernancePanelLazy aramisSession={effectiveSession} delegates={delegates}
            saveDelegate={saveDelegate} admins={admins} auditLog={auditLog} onNav={navTo}/>
        ) : <AccessDenied view={view} reason="Cette page est réservée aux administrateurs d'Aramis."/>)}
        {/* Pages de l'Administration (lot L11i) : l'en-tête de la page d'abord, le
            retour vers « Gestion des membres » SOUS l'en-tête (onRetour). */}
        {view==="admin-emails-auto" && (isAdminApp
          ? <AdminEmailsAutoPanelLazy aramisSession={effectiveSession} onRetour={()=>navTo("admin")}/>
          : <AccessDenied view={view} reason="Cette page est réservée aux administrateurs d'Aramis."/>)}
        {/* Demandes d'engagement (Activité, lot L11d-bis) : administrateurs de la
            session RÉELLE ; sinon l'écran d'accès réservé. */}
        {view==="athos-demandes" && (isAdminApp
          ? <DemandesEngagementViewLazy aramisSession={effectiveSession}/>
          : <AccessDenied reason="Les demandes d'engagement sont réservées aux administrateurs d'Aramis."/>)}
        {view==="admin-demandes" && (isAdminApp
          ? <DemandesCreationComptePanel aramisSession={effectiveSession} saveDelegate={saveDelegate}
              rendu={(etat)=><PageDemandesCompteLazy {...etat} nombreEngagement={nDemandesEngagement} onNav={navTo}/>}/>
          : <AccessDenied view={view} reason="Cette page est réservée aux administrateurs d'Aramis."/>)}
        {view==="admin-auth-mesure" && (isAdminApp
          ? <AdminAuthMesurePanelLazy delegates={delegates} onRetour={()=>navTo("admin")}/>
          : <AccessDenied view={view} reason="Cette page est réservée aux administrateurs d'Aramis."/>)}
        {/* Santé des collectes : la VRAIE session (le serveur exige un admin). */}
        {view==="admin-sante-collectes" && (isAdminApp
          ? <AdminSanteCollectesPanelLazy aramisSession={aramisSession} onRetour={()=>navTo("admin")}/>
          : <AccessDenied view={view} reason="Cette page est réservée aux administrateurs d'Aramis."/>)}
        {/* Usages d'Aramis (F1) : la VRAIE session (le serveur exige un admin). */}
        {view==="admin-usages" && (isAdminApp
          ? <AdminUsagesPanelLazy aramisSession={aramisSession} onRetour={()=>navTo("admin")}/>
          : <AccessDenied view={view} reason="Cette page est réservée aux administrateurs d'Aramis."/>)}
        {/* Administration › Les nouveautés d'Aramis (18/10/2026) : la VRAIE
            session (le serveur exige un administrateur et sa passkey). */}
        {view==="admin-nouveautes" && (isAdminApp
          ? <NouveautesPageLazy aramisSession={aramisSession} onNav={navTo}/>
          : <AccessDenied view={view} reason="Cette page est réservée aux administrateurs d'Aramis."/>)}
        {/* Administration › Synchronisation (demande de Pierre du 13/10/2026 ;
            ouverte aux administrateurs le 19/10/2026, même adresse) : session
            RÉELLE ; chaque échange garde sa garde au serveur (réglages
            sensibles au super-administrateur, Budget à son propriétaire). */}
        {view==="super-synchronisation" && (isAdminApp
          ? <SynchronisationPageLazy aramisSession={aramisSession} config={config} onNav={navTo}/>
          : <AccessDenied view={view} reason="Cette page est réservée aux administrateurs d'Aramis."/>)}
        {/* Vote en direct de l'AG : la VRAIE session (pas « Voir comme ») — le
            serveur exige un admin (voteAgAdmin, validateCaller requireAdmin). */}
        {view==="admin-vote-ag" && (isAdminApp
          ? <VoteAgConsoleLazy aramisSession={aramisSession} onNav={navTo}/>
          : <AccessDenied view={view} reason="La console de vote est réservée aux administrateurs."/>)}
        {view==="admin-erreurs" && (isAdminApp
          ? <AdminErreursPanelLazy onRetour={()=>navTo("admin")}/>
          : <AccessDenied view={view} reason="Cette page est réservée aux administrateurs d'Aramis."/>)}
        {view==="admin-envois" && (isAdminApp ? (
          <div>
            <div style={{padding:"20px 16px 0",maxWidth:"var(--content-max)",margin:"0 auto"}}>
              <button className="btn btn-ghost btn-sm" onClick={()=>navTo("admin")}>← Gestion membres</button>
            </div>
            <EmailTrackingPanelLazy aramisSession={effectiveSession}/>
          </div>
        ) : (
          <div style={{display:"flex",alignItems:"center",justifyContent:"center",minHeight:"calc(var(--vh100) * 0.6)"}}>
            <div className="card" style={{maxWidth:360,textAlign:"center",padding:32}}>
              <div style={{fontSize:"var(--fs-page)",marginBottom:12}}><IconeTexte nom="lock" titre="Verrouillé"/></div>
              <div style={{fontWeight:700,fontSize:"var(--fs-corps)",marginBottom:8,color:"var(--text-main)"}}>
                Accès réservé aux administrateurs
              </div>
              <button className="btn btn-orange" onClick={()=>navTo("ma-semaine")}>Retour</button>
            </div>
          </div>
        ))}
        {view==="admin-campagnes" && (isAdminApp
          ? <AdminCampagnesPanelLazy aramisSession={effectiveSession} lists={lists} onRetour={()=>navTo("admin")}/>
          : <AccessDenied view={view} reason="Cette page est réservée aux administrateurs d'Aramis."/>)}
        {view==="admin-adoption" && (isAdminApp
          ? <AdoptionPanelLazy delegates={delegates} leaderboard={leaderboard} aramisSession={aramisSession} onRetour={()=>navTo("admin")}/>
          : <AccessDenied view={view} reason="Cette page est réservée aux administrateurs d'Aramis."/>)}
        {(view==="admin"||view.startsWith("admin-super-")) && (isAdminApp ? (
          <AdminLazy
            appState={appState} onSave={saveState} onNav={navTo}
            isOnline={isOnline}
            config={config} saveConfig={saveConfig}
            lists={lists} saveList={saveList} deleteList={deleteList}
            admins={admins} saveAdmin={saveAdmin} deleteAdmin={deleteAdmin}
            delegates={delegates} saveDelegate={saveDelegate} deleteDelegate={deleteDelegate}
            gouvernance={gouvernance} saveGouvernance={saveGouvernance}
            ideas={ideas} deleteIdea={deleteIdea} auditLog={auditLog} logAction={logAction}
            members={members||[]} saveMember={saveMember} deleteMember={deleteMember}
            aramisSession={aramisSession}
            leaderboard={leaderboard}
            superTab={view.startsWith("admin-super-")?view.replace("admin-super-",""):null}
          />
        ) : (
          <div style={{display:"flex",alignItems:"center",justifyContent:"center",minHeight:"calc(var(--vh100) * 0.6)"}}>
            <div className="card" style={{maxWidth:360,textAlign:"center",padding:32}}>
              <div style={{fontSize:"var(--fs-page)",marginBottom:12}}><IconeTexte nom="lock" titre="Verrouillé"/></div>
              <div style={{fontWeight:700,fontSize:"var(--fs-corps)",marginBottom:8,color:"var(--text-main)"}}>
                Accès réservé aux administrateurs
              </div>
              <button className="btn btn-orange" onClick={()=>navTo("ma-semaine")}>Retour</button>
            </div>
          </div>
        ))}
        </>)}{/* fin de la garde d'accès (accesRefuse) */}
        </div>{/* fin colonne de contenu (ModuleShell) */}
      </div>
      </div>{/* fin du corps de la coquille (rail + contenu) */}
      {/* Version réellement chargée par CE navigateur + mise à jour forcée.
          Trois correctifs livrés les 7 et 8 septembre ont été signalés comme
          non résolus alors qu'ils étaient en ligne : rien ne permettait de
          voir qu'une version antérieure tournait encore. */}
      {aramisSession && <VersionAramis/>}

      {/* Messagerie chat flottante (utilisateur ↔ utilisateur + groupes).
          Lit `?conversationId=...` au mount pour ouvrir une conv via deep link
          (typiquement depuis un mail de notification "Nouveau message"). */}
      {aramisSession && (
        <FloatingChat
          aramisSession={aramisSession}
          delegates={delegates}
          openConvIdRequest={(() => {
            try {
              const p = new URLSearchParams(window.location.search);
              return p.get("conversationId") || null;
            } catch { return null; }
          })()}
          ouvert={chatOuvert} onOuvertChange={setChatOuvert}
          onNonLus={setMessagesNonLus} sansBulle/>
      )}

      {/* Socrate — plus de bulle flottante (11/10/2026) : le bouton « S » vit
          dans l'en-tête, après la recherche, et le panneau s'ouvre dessous.
          Rendu conditionnel côté composant : statut serveur + rôle équipe. */}
      {aramisSession && (aramisSession.role === "admin" || aramisSession.role === "superadmin") && (
        <SocrateLazy aramisSession={aramisSession} sansBulle
          ouvert={socrateOuvert} onOuvertChange={setSocrateOuvert} onDisponible={setSocrateDisponible}/>
      )}

      {/* Recherche globale ⌘K */}
      {aramisSession && globalSearchOpen && (
        <GlobalSearchLazy
          open={globalSearchOpen}
          onClose={()=>setGlobalSearchOpen(false)}
          aramisSession={aramisSession}
          delegates={delegates}
          projects={projects}
          reunions={reunions}
          questions={accueilQuestions}
          onNav={navTo}
          modules={modulesMenu} personnel={modulesPersonnels} lists={lists} view={view}
          onMessagerie={() => setChatOuvert(true)}
          onQuestion={() => setShowQuestionModal(true)}/>
      )}

      {/* Modal "Poser une question" + mode "Conversion d'idée"
          → JSX extrait en haut du composant (questionModalElement) pour
          être monté aussi dans le retour anticipé de la vue `ideas`. */}
      {questionModalElement}
      {aramisSession && openQuestion && (
        <QuestionThreadLazy
          question={openQuestion}
          aramisSession={effectiveSession}
          delegates={delegates}
          onClose={()=>setOpenQuestion(null)}
          onReply={async(qId,msg)=>{
            const {doc,updateDoc,arrayUnion}=await import("firebase/firestore");
            const {db}=await import("./firebase");
            await updateDoc(doc(db,"questions",qId),{messages:arrayUnion(msg)});
            setOpenQuestion(q=>q?{...q,messages:[...(q.messages||[]),msg]}:q);
          }}
          onClose2={async(qId)=>{
            if(!window.confirm("Clôturer cette discussion ?")) return;
            const {doc,updateDoc}=await import("firebase/firestore");
            const {db}=await import("./firebase");
            await updateDoc(doc(db,"questions",qId),{statut:"clos"});
            setOpenQuestion(null);
          }}
          onReopen={(openQuestion?.createurId === aramisSession?.id || isAdminApp) ? async(qId)=>{
            const {doc,updateDoc}=await import("firebase/firestore");
            const {db}=await import("./firebase");
            await updateDoc(doc(db,"questions",qId),{statut:"ouvert"});
            setOpenQuestion(q => q ? { ...q, statut: "ouvert" } : q);
          } : null}
        />
      )}

      {/* Bandeau installation PWA (Chrome/Edge/Firefox — event natif) */}
      {showInstallBanner && tourInstallation && (
        <div className="pwa-banner" style={{position:"fixed",bottom:0,left:0,right:0,zIndex:9998,
          background:"linear-gradient(135deg, #003F59, #1a5276)",
          /* Zone sure basse : sans elle le bandeau passe sous la barre
             d'accueil iOS et le bouton « Installer » n'est plus tappable. */
          padding:"14px 20px calc(14px + var(--sab,0px))",
          display:"flex",alignItems:"center",justifyContent:"center",gap:14,
          boxShadow:"0 -4px 20px rgba(0,0,0,.3)",animation:"mobileMenuSlide .3s ease"}}>
          <div style={{fontSize:"var(--fs-compteur)",flexShrink:0}}><IconeTexte nom="device-mobile" titre="Mobile"/></div>
          <div style={{flex:"1 1 200px",minWidth:0}}>
            <div style={{fontWeight:700,fontSize:"var(--fs-meta)",color:"#fff",fontFamily:"Montserrat,sans-serif"}}>Installer Aramis</div>
            <div style={{fontSize:"var(--fs-micro)",color:"rgba(255,255,255,.6)"}}>Accès rapide depuis votre écran d'accueil</div>
          </div>
          {/* --cible-min : 0 au clavier-souris (le bouton garde sa hauteur
              naturelle d'origine, padding 8px 18px), 44pt au doigt. */}
          <button onClick={handleInstall}
            style={{background:C.orange,border:"none",borderRadius:"var(--radius-md)",padding:"8px 18px",
              minHeight:"var(--cible-min)",
              color:"#fff",fontWeight:700,fontSize:"var(--fs-micro)",cursor:"pointer",fontFamily:"Montserrat,sans-serif",flexShrink:0}}>
            Installer
          </button>
          <button onClick={() => setShowInstallBanner(false)} aria-label="Masquer ce bandeau"
            style={{background:"none",border:"none",color:"rgba(255,255,255,.5)",fontSize:"var(--fs-accroche)",
              cursor:"pointer",flexShrink:0,
              minWidth:"var(--cible-min)",minHeight:"var(--cible-min)"}}>✕</button>
        </div>
      )}

      {/* Bandeau installation PWA (Safari macOS/iOS — pas d'event, instructions manuelles) */}
      {safariInstallHint && !showInstallBanner && tourInstallation && (
        <div className="pwa-banner" style={{position:"fixed",bottom:0,left:0,right:0,zIndex:9998,
          background:"linear-gradient(135deg, #003F59, #1a5276)",
          /* flexWrap + zone sure basse : sans enroulement, deux boutons
             incompressibles ecrasaient un texte d'instructions de 3-4 lignes
             sur 375px, et le bandeau masquait durablement le bas de la page,
             barre d'accueil iOS comprise. */
          padding:"12px 20px calc(12px + var(--sab,0px))",
          display:"flex",alignItems:"center",justifyContent:"center",gap:14,
          boxShadow:"0 -4px 20px rgba(0,0,0,.3)",animation:"mobileMenuSlide .3s ease"}}>
          <div style={{fontSize:"var(--fs-section)",flexShrink:0}}><IconeTexte nom="device-mobile" titre="Mobile"/></div>
          <div style={{flex:"1 1 220px",minWidth:0}}>
            <div style={{fontWeight:700,fontSize:"var(--fs-meta)",color:"#fff",fontFamily:"Montserrat,sans-serif"}}>
              Installer Aramis sur votre {/iPad|iPhone|iPod/.test(navigator.userAgent) ? "iPhone/iPad" : "Mac"}
            </div>
            <div style={{fontSize:"var(--fs-micro)",color:"rgba(255,255,255,.75)",lineHeight:1.4,marginTop:2}}>
              {/iPad|iPhone|iPod/.test(navigator.userAgent) ? (
                <>Tapez sur l'icône <strong>Partager</strong> (carré avec flèche ↑) puis <strong>Sur l'écran d'accueil</strong></>
              ) : (
                <>Menu <strong>Fichier</strong> → <strong>Ajouter au Dock…</strong> (macOS 14 Sonoma ou plus récent)</>
              )}
            </div>
          </div>
          {/* --cible-min : 0 au clavier-souris (padding 6px 12px et corps 11px
              d'origine), 44pt au doigt. */}
          <button onClick={dismissSafariHint}
            style={{background:"none",border:"1px solid rgba(255,255,255,.3)",borderRadius:"var(--radius-sm)",padding:"6px 12px",
              minHeight:"var(--cible-min)",
              color:"#fff",fontWeight:600,fontSize:"var(--fs-micro)",cursor:"pointer",fontFamily:"Montserrat,sans-serif",flexShrink:0}}>
            Compris
          </button>
          <button onClick={dismissSafariHint} aria-label="Masquer ce bandeau"
            style={{background:"none",border:"none",color:"rgba(255,255,255,.5)",fontSize:"var(--fs-accroche)",
              cursor:"pointer",flexShrink:0,
              minWidth:"var(--cible-min)",minHeight:"var(--cible-min)"}}>✕</button>
        </div>
      )}
      <div style={{textAlign:"center",padding:"12px",fontSize:"var(--fs-micro)",color:"var(--text-sub)",fontFamily:"Montserrat,Arial,sans-serif"}}>
        Aramis · Agora · Union de Groupe Mutualiste Enio · 53 Cours Mirabeau, Aix-en-Provence
      </div>
      {/* La navigation basse, sur téléphone uniquement. Elle n'apparaît que
          connecté : sur la page de garde et l'écran de connexion, il n'y a
          nulle part où aller. Sa hauteur est réservée par --h-onglets, qui vaut
          0 partout sauf html[data-coquille="tel"]. */}
      {avecOnglets && aramisSession && (
        <BarreOnglets
          view={view} onNav={navTo} module={moduleOnglet} modules={modulesMenu}
          moduleActif={!!moduleCourant}
          ongletActif={feuilleModules ? "modules" : globalSearchOpen ? "rechercher" : moiOuvert ? "moi" : null}
          onModules={() => setFeuilleModules(true)}
          onSousMenu={(e) => navTo(routePortail(moduleOnglet), null, optsCoquille(e))}
          onRechercher={() => setGlobalSearchOpen(true)}
          onMoi={() => setMoiOuvert(true)}
          nonLus={messagesNonLus}
        />
      )}
      {/* Animation de déverrouillage : portée par la coquille unique (les écrans
          « accueil » et « athos-ma-mutuelle » la montaient eux-mêmes). */}
      {unlockAnim && <UnlockAnimation prenom={unlockAnim.prenom} onDone={finishUnlock}/>}
      {/* Feuille « Modules » (lot L6) : les modules en tuiles pleines de 52 px,
          navigation pure (liste blanche de modalOverlayLock.test.js). */}
      {aramisSession && (feuilleModules || feuilleModulesMontee) && (
        <Suspense fallback={null}>
          <FeuilleModulesRaw ouvert={feuilleModules} onFermer={() => setFeuilleModules(false)}
            modules={modulesMenu} admin={modulesAdmin} onNav={navTo}/>
        </Suspense>
      )}
    </div>
    </SessionReelleContexte.Provider>
    </TourProvider>
  );
}
/* cache bust Wed.  8 Apr. 2026 22:51:21 */
